import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  Alert, Autocomplete, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, InputAdornment, Link, MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import PercentIcon from '@mui/icons-material/Percent'
import GridOnIcon from '@mui/icons-material/GridOn'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CallSplitIcon from '@mui/icons-material/CallSplit'
import SearchIcon from '@mui/icons-material/Search'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined'
import CheckIcon from '@mui/icons-material/Check'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { useSnackbar } from 'notistack'
import PageTitle from '../../../components/PageTitle'
import { ChoiceCards, SelectCards, Pill, SectionCard, KpiRow, GapNote, helperProps, infoAdorn, NoteInfoIcon, compactPlaceholderSx } from '../components/common'
import DataTable from '../components/Tables'
import ServiceSelectorFunnel from '../components/ServiceSelectorFunnel'
import { downloadXlsx, downloadWorkbook, readWorkbook, allocationBdRows, serviceabilityRows } from '../components/fileExports'
import {
  COMMERCIAL_TYPES, SERVICES, WIZARD_STEPS, RFI_ZONES, BUCKETS, PATHS,
} from '../constants'
import {
  useCreateProposalMutation, useGenerateMutation, useSubmitMutation, usePushMutation,
  useSystemMarginMutation, useFscMutation, useLazyGetPincodeQuery,
  useGetVersionQuery, useGetProposalDocQuery, useListProposalsQuery, useLazyGetVersionQuery, useGetCouriersQuery,
  useGetProposalQuery, useSaveDraftStateMutation, useGetRateCardLibraryQuery, useGetServiceabilityServiceTypesQuery,
  useListStandardCardsQuery, useServeStandardCardMutation,
} from '../../../redux/d2cApiSlice'
import { useGetAllCourierPartnersQuery } from '../../../redux/commonApiCalls'
import { buildCreatePayload, buildGeneratePayload, dealToResume, toApiZone, SERVICE_MAP, CARD_TYPE_MAP, BUCKET_LABEL_TO_KEY, rateCardModeFor, matchStandardCard, serviceMeta, serviceCodeToKey, STANDARD_FORMATS, tierSlabRanges, rateRowLabelsFromTiers, compactRateRows, expandCompactRates } from '../apiMap'
import { openS3Doc, exportPreviewPdf } from '../s3Doc'
import ProzoDataGrid from '../../../common/ProzoDataGrid'

// PIN→City·State map (3-digit prefix), mirroring the v19 mockup so the city resolves
// instantly; the pincode master API (#24) enriches pinCache when it returns.
const PINMAP = {
  110: 'Delhi NCR · Delhi', 122: 'Gurgaon · Haryana', 201: 'Noida · Uttar Pradesh', 208: 'Kanpur · Uttar Pradesh',
  226: 'Lucknow · Uttar Pradesh', 141: 'Ludhiana · Punjab', 160: 'Chandigarh · Chandigarh', 190: 'Srinagar · Jammu & Kashmir',
  302: 'Jaipur · Rajasthan', 380: 'Ahmedabad · Gujarat', 395: 'Surat · Gujarat', 400: 'Mumbai · Maharashtra',
  411: 'Pune · Maharashtra', 452: 'Indore · Madhya Pradesh', 500: 'Hyderabad · Telangana', 560: 'Bangalore · Karnataka',
  600: 'Chennai · Tamil Nadu', 682: 'Cochin · Kerala', 700: 'Kolkata · West Bengal', 744: 'Port Blair · Andaman',
  781: 'Guwahati · Assam', 800: 'Patna · Bihar', 421: 'Thane · Maharashtra', 440: 'Nagpur · Maharashtra',
  462: 'Bhopal · Madhya Pradesh', 530: 'Visakhapatnam · Andhra Pradesh', 751: 'Bhubaneswar · Odisha',
}
const METROS = ['delhi ncr', 'mumbai', 'bangalore', 'bengaluru', 'chennai', 'kolkata']
const SPECIAL_STATES = ['jammu & kashmir', 'himachal pradesh', 'kerala', 'assam', 'arunachal pradesh', 'manipur', 'meghalaya', 'mizoram', 'nagaland', 'sikkim', 'tripura', 'ladakh', 'andaman', 'lakshadweep']

// "City · State" from the prefix map ('' if unknown).
function cityStateFor(pin) {
  const p = String(pin || '').trim()
  if (p.length < 3) return ''
  return PINMAP[p.slice(0, 3)] || ''
}
// Just the city name (for chips / cities-in-scope).
function cityFor(pin) {
  const cs = cityStateFor(pin)
  return cs ? cs.split('·')[0].trim() : ''
}
// The RFI zone row that is pickup-gated. The FE labels it "Metro" and the backend "Metros",
// and a copied/uploaded split can carry either — match on the prefix rather than on equality.
const isMetroZone = (z) => String(z || '').trim().toLowerCase().startsWith('metro')

// Classify a resolved city/state into the D2C zone tag (Metro | Special | Standard).
function zoneFromCityState(cityRaw, stateRaw) {
  const city = String(cityRaw || '').trim().toLowerCase()
  const state = String(stateRaw || '').trim().toLowerCase()
  if (!city && !state) return ''
  if (METROS.includes(city)) return 'Metro'
  if (SPECIAL_STATES.some((st) => state.includes(st))) return 'Special'
  return 'Standard'
}
function zoneForPin(pin) {
  const cs = cityStateFor(pin)
  if (!cs || cs.indexOf('·') < 0) return ''
  return zoneFromCityState(cs.split('·')[0], cs.split('·')[1] || '')
}

// QA fast-fill is visible ONLY to this login — one click fills every wizard input with
// valid demo data so the whole flow can be walked without hand-typing each field.
const PREFILL_EMAILS = ['ajeet.johri@prozo.com',"devansh.richharia@prozo.com"]

// All of this wizard's toasts anchor TOP-center instead of the app's global bottom-right —
// the footer action bar (Back / Continue) lives in that corner, and bottom-right toasts
// were landing directly on top of the Continue button.
function useWizardSnackbar() {
  const { enqueueSnackbar: raw, closeSnackbar } = useSnackbar()
  const enqueueSnackbar = useCallback((msg, opts = {}) => raw(msg, { anchorOrigin: { vertical: 'top', horizontal: 'center' }, ...opts }), [raw])
  return { enqueueSnackbar, closeSnackbar }
}

export default function NewProposal() {
  const navigate = useNavigate()
  const { state: navState } = useLocation()
  const { enqueueSnackbar } = useWizardSnackbar()
  const [active, setActive] = useState(0)
  const [showErr, setShowErr] = useState(false) // reveal inline field errors after a failed "Continue"

  const userInfo = useSelector((state) => state.user?.userInfo)
  const canPrefill = PREFILL_EMAILS.includes(String(userInfo?.username || userInfo?.email || '').trim().toLowerCase())

  // Shared export host (Option-C header): each active step publishes its "export this step"
  // action here via useHeaderExport, and the stepper header renders one export pill from it.
  const exportFnRef = useRef(null)
  const [exportMeta, setExportMeta] = useState(null)
  const exportHost = useMemo(() => ({ fnRef: exportFnRef, setMeta: setExportMeta }), [])

  // "Copy as template" seed (real structure from an existing proposal, no prospect data).
  const seed = navState?.seed

  // Step 1
  const [cardType, setCardType] = useState(seed?.cardType || 'blended')
  const [cardVariant, setCardVariant] = useState(seed?.cardVariant || null) // 'blended' | 'cpwise' — sub-choice for sddndd/hb only
  const [services, setServices] = useState(seed?.services?.length ? seed.services : [])
  const [brand, setBrand] = useState('')
  const [hubspot, setHubspot] = useState('')
  const [category, setCategory] = useState('')
  const [maxWt, setMaxWt] = useState('')
  const [totVol, setTotVol] = useState('')
  const [revPot, setRevPot] = useState('')
  const [avgWt, setAvgWt] = useState('')
  const [codShare, setCodShare] = useState('')
  const [rtoShare, setRtoShare] = useState('')
  const [avgInvoiceValue, setAvgInvoiceValue] = useState('') // ₹ — drives %-of-value COD charge + rails
  // Freight-commercials scoping fields (d2c-scoping-v8.html) not previously captured anywhere
  // in the wizard — collected here alongside the rest of Brand & deal, same as maxWt/avgWt/
  // category before them: not yet wired into buildCreatePayload/buildGeneratePayload (no
  // confirmed backend field for any of these five yet).
  const [registeredCompany, setRegisteredCompany] = useState('') // legal entity name — distinct from the brand/trade name
  const [enterpriseSize, setEnterpriseSize] = useState('') // MSME classification: Micro | Small | Medium | Large
  const [brandTotalVol, setBrandTotalVol] = useState('') // brand's total monthly shipments across ALL logistics partners — distinct from totVol (shipments to Prozo)
  const [minWt, setMinWt] = useState('') // kg — lightest item shipped
  const [currentRvp, setCurrentRvp] = useState('') // % of forward shipments picked back from the customer today — distinct from Step 3's RVP pricing-formula inputs
  const [pickups, setPickups] = useState([
    { id: 1, pin: '', vol: '' },
  ])

  // Step 2
  const [zoneSplit, setZoneSplit] = useState(RFI_ZONES.map((z) => ({ zone: z, pct: '' })))
  // Weight-slab structure ({size,count}[]) — built once, deal-wide, in Step 2 ("Split
  // differs by service type" OFF); carries "as is" into Step 3's rate-card grid. [0] =
  // starting slabs (from 0 kg), [1..] = bands, each from where the last ended. Arbitrary
  // sizes are fine here — the wire only ever snaps boundaries to the 6 backend slabs
  // (bandToSlab) when building the actual RFI/rate-card payload, not in the UI.
  const [wtTiers, setWtTiers] = useState([{ size: 0.5, count: 1 }, { size: 1, count: 8 }])
  // wtSplit is one row PER SLAB (tierSlabRanges(wtTiers)), each with a % share — kept in
  // sync with wtTiers by setSharedTiers (Step 2) on every structure change. No "band"
  // concept in the RFI split — every individual slab gets its own % share.
  const [wtSplit, setWtSplit] = useState(tierSlabRanges([{ size: 0.5, count: 1 }, { size: 1, count: 8 }]).map((s) => ({ from: String(s.from), to: String(s.to), pct: '' })))

  // Step 2 — per-pickup full % matrix (alternative to the quick split)
  const [rfiMode, setRfiMode] = useState('pct') // 'pct' (quick split) | 'matrix' (per pickup)
  const [repAll, setRepAll] = useState(true) // replicate one matrix to all pickups
  const [activePk, setActivePk] = useState(0)
  const [matrixData, setMatrixData] = useState({}) // { pkIndex: [{ from, to, counts:[5 zones] }] }

  // Step 3 — hydrate per-service config from the template seed (real values), if present.
  const [svcConfig, setSvcConfig] = useState(() => {
    if (!seed?.svcCfgPatch) return {}
    const o = {}
    Object.entries(seed.svcCfgPatch).forEach(([k, patch]) => { o[k] = { ...defaultSvcCfg(), ...patch } })
    return o
  })

  // Resume draft — hydrate the whole wizard from the saved deal. The Step-1 basics ride
  // in via the list row (navState.resume); the version (Steps 2/3) comes from getVersion.
  const resume = navState?.resume
  // The resume row is a raw ProposalListItem — its id field is `dealId` (no `proposalId`).
  const resumeId = resume?.dealId || resume?.id || null
  const { data: resumeVersion, isError: resumeVersionError } = useGetVersionQuery(resume?.currentVersionId, { skip: !resume?.currentVersionId })
  // PROD-BUG-02: fetch the full deal detail for the autosaved wizard snapshot (deal.draftState).
  const { data: resumeDetail, isError: resumeDetailError } = useGetProposalQuery(resumeId, { skip: !resumeId })
  const [saveDraftState] = useSaveDraftStateMutation()
  const [hydrated, setHydrated] = useState(false)

  // Pincode master lookup (#24) — auto-fills pickup City·State + zone from the PIN.
  const [pinCache, setPinCache] = useState({})
  const [lookupPin] = useLazyGetPincodeQuery()
  const resolvePin = async (pin) => {
    if (!pin || pin.length !== 6 || pinCache[pin]) return
    // 1) D2C pincode master (authoritative — includes zoneTag).
    try {
      const info = await lookupPin(pin).unwrap()
      if (info && info.city) {
        // isServiceable feeds Hard Stop 4 ("Serviceability honesty") — false triggers the
        // explicit stop / go-ahead choice on the Step-3 city chips; null/undefined = unknown.
        // isMetro + inMaster feed the Metro zone gate on Step 2 (sales feedback A1/A2). The
        // backend's FB-07 rule keys on the d2c_pincode_master flag ONLY, so we must keep the
        // two apart: a `fallback` hit resolves City·State from the wider pincode dictionary
        // and carries isMetro: null — inconclusive, never "not a metro".
        setPinCache((c) => ({ ...c, [pin]: { city: info.city, state: info.state, zoneTag: info.zoneTag || zoneFromCityState(info.city, info.state), isServiceable: info.isServiceable ?? null, isMetro: info.isMetro ?? null, inMaster: info.source === 'd2c_master' } }))
        return
      }
    } catch { /* not in the D2C master — try the public fallback below */ }
    // 2) Public India-Post API — full coverage for valid Indian pincodes (external, best-effort).
    try {
      const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`)
      const data = await res.json()
      const po = Array.isArray(data) && data[0]?.Status === 'Success' ? data[0].PostOffice?.[0] : null
      if (po) {
        const city = po.District || po.Block || po.Name
        const state = po.State
        // External source — zoneTag here is a local heuristic, NOT the master's authority,
        // so it must never drive the Metro gate. inMaster stays false, isMetro stays null.
        setPinCache((c) => ({ ...c, [pin]: { city, state, zoneTag: zoneFromCityState(city, state), isMetro: null, inMaster: false } }))
        return
      }
    } catch { /* fall back to the built-in prefix map in the render */ }
  }

  // API state
  const [createProposal, createState] = useCreateProposalMutation()
  const [generate, generateState] = useGenerateMutation()
  const [submitToL1, submitState] = useSubmitMutation()
  const [pushToPricing, pushState] = usePushMutation()
  const [systemMargin, smState] = useSystemMarginMutation()
  const [fsc, fscState] = useFscMutation()
  // Standard (GTM) rate-card library — read once, used by Step 3's "Standard" mode to
  // auto-match the pre-built card by service × rate-card type × prospect tier.
  const { data: rateCardLibrary = [] } = useGetRateCardLibraryQuery()
  // STANDARD commercial type (Step-1 short-circuit): the master standard cards drive
  // format availability. Only fetched once the SL actually picks the Standard card.
  const { data: standardMasterCards = [], isLoading: standardCardsLoading } = useListStandardCardsQuery(undefined, { skip: cardType !== 'standard' })
  // Deal-level rate-card mode (BLENDED | COURIER_WISE). For sddndd/hb the mode is the sub-choice
  // itself (blended → BLENDED, courier-wise → COURIER_WISE) — H&B's product identity rides in the
  // create-time cardType (still 'HB'), so rateCardMode stays within its BLENDED|COURIER_WISE spec.
  // Every other card derives the mode straight from cardType.
  const effectiveCardKey = (cardType === 'sddndd' || cardType === 'hb') ? (cardVariant === 'cpwise' ? 'cpwise' : 'blended') : cardType
  const dealRateMode = rateCardModeFor(effectiveCardKey)
  // H&B has no dedicated commercial card any more: picking a Large parcel in the service funnel
  // derives the HB commercial (its mini-agreement doc) at create time, while the packaging
  // (Blended / Courier-wise) stays the rate-card mode. Legacy resumed hb/sddndd deals still work.
  const createCardKey = (cardType === 'blended' || cardType === 'cpwise') && services.some((c) => /(_|\b)LARGE/i.test(String(c))) ? 'hb' : cardType
  const [proposalId, setProposalId] = useState(null)
  const [versionId, setVersionId] = useState(null)
  const [genResult, setGenResult] = useState(null)
  const [genWarnings, setGenWarnings] = useState([])
  const busy = createState.isLoading || generateState.isLoading

  // Resume: hydrate every step so the SL continues the SAME deal. PROD-BUG-02: the primary
  // restore source is the autosaved wizard snapshot (deal.draftState) — it captures the FULL
  // wizard (all Step-1 fields, exact service selection, RFI splits, Step-3 rate cards), so a
  // draft that was never generated resumes completely. Falls back to reconstructing from the
  // version (dealToResume) for older/generated drafts with no snapshot. Land on the step that
  // matches the deal: GENERATED → Review (Step 4); otherwise the step the SL left off on.
  useEffect(() => {
    if (!resume || hydrated) return
    // wait for the detail fetch (draftState) and, if present, the version
    if (resumeId && resumeDetail === undefined && !resumeDetailError) return
    if (resume.currentVersionId && resumeVersion === undefined && !resumeVersionError) return
    const deal = resumeDetail?.deal
    const v = (resumeVersionError ? null : resumeVersion) || resumeDetail?.version || null

    let restoredFromSnapshot = false
    if (deal?.draftState) {
      try {
        const s = JSON.parse(deal.draftState)
        setCardType(s.cardType || 'blended')
        setCardVariant(s.cardVariant || null)
        setServices(Array.isArray(s.services) ? s.services : [])   // exact selection — fixes de-selected services reappearing
        setBrand(s.brand || '')
        setHubspot(s.hubspot || '')
        setCategory(s.category || '')
        setMaxWt(s.maxWt || '')
        setTotVol(s.totVol || '')
        setRevPot(s.revPot || '')
        setAvgWt(s.avgWt || '')
        setCodShare(s.codShare || '')
        setRtoShare(s.rtoShare || '')
        setAvgInvoiceValue(s.avgInvoiceValue || '')
        setRegisteredCompany(s.registeredCompany || '')
        setEnterpriseSize(s.enterpriseSize || '')
        setBrandTotalVol(s.brandTotalVol || '')
        setMinWt(s.minWt || '')
        setCurrentRvp(s.currentRvp || '')
        if (Array.isArray(s.pickups) && s.pickups.length) setPickups(s.pickups)
        if (Array.isArray(s.zoneSplit) && s.zoneSplit.length) setZoneSplit(s.zoneSplit)
        if (Array.isArray(s.wtSplit) && s.wtSplit.length) setWtSplit(s.wtSplit)
        if (Array.isArray(s.wtTiers) && s.wtTiers.length) setWtTiers(s.wtTiers)
        if (s.rfiMode) setRfiMode(s.rfiMode)
        if (typeof s.repAll === 'boolean') setRepAll(s.repAll)
        if (s.matrixData && typeof s.matrixData === 'object') setMatrixData(s.matrixData)
        if (s.svcConfig && typeof s.svcConfig === 'object') setSvcConfig(s.svcConfig)
        setPerSvcSplit(!!s.perSvcSplit)
        if (s.svcSplits && typeof s.svcSplits === 'object') setSvcSplits(s.svcSplits)
        restoredFromSnapshot = true
      } catch { /* corrupt snapshot — fall through to the version reconstruction */ }
    }

    if (!restoredFromSnapshot) {
      const r = dealToResume(resume, v)
      setCardType(r.cardType)
      // dealToResume can't tell blended from courier-wise apart for the collapsed BLENDED
      // enum value, but H&B's PRODUCT_SEGMENT mapping is unique — infer the sub-choice from
      // the version's own rateCardMode so an old H&B draft doesn't force a re-pick on resume.
      if (r.cardType === 'hb') setCardVariant(v?.rateCardMode === 'COURIER_WISE' ? 'cpwise' : 'blended')
      if (r.services.length) setServices(r.services)
      setBrand(r.brand)
      setHubspot(r.hubspot)
      setRevPot(r.revPot)
      if (r.totVol) setTotVol(r.totVol)
      if (r.pickups.length) setPickups(r.pickups)
      if (Object.keys(r.svcCfgPatch).length) {
        const o = {}
        Object.entries(r.svcCfgPatch).forEach(([k, patch]) => { o[k] = { ...defaultSvcCfg(), ...patch } })
        setSvcConfig(o)
      }
      if (r.zoneSplit.length) setZoneSplit(r.zoneSplit)
      if (r.wtSplit.length) setWtSplit(r.wtSplit)
      // Rate-card recipe v2 — the stored recipe carries the Step-2 structure (fresh
      // flags included), so a resumed deal keeps its real slab ladder instead of the default.
      if (Array.isArray(r.wtTiers) && r.wtTiers.length) setWtTiers(r.wtTiers)
      // FB-01 — restore per-service splits when the generated version's rfiEntries vary by service.
      if (r.perSvcSplit) { setPerSvcSplit(true); setSvcSplits(r.svcSplits || {}) }
    }

    setProposalId(resumeId || deal?.id)
    if (v?.id) setVersionId(v.id)
    // Generated deals resume at Review with the real result restored — no re-walk from page 1.
    if (v && resume.status === 'GENERATED') {
      setGenResult({ dealId: resumeId || deal?.id, versionId: v.id, status: resume.status, route: v.route ?? null, bucket: v.dealBucket || null, dealMarginPct: v.dealMarginPct ?? null })
      setActive(3)
    }
    setHydrated(true)
    if (restoredFromSnapshot) {
      enqueueSnackbar(`Resumed “${brand || deal?.brandName || 'proposal'}” — your full draft was restored.`, { variant: 'success' })
    } else if (resumeVersionError || resumeDetailError) {
      enqueueSnackbar(`Resumed “${deal?.brandName || 'proposal'}” from the basics — the saved details couldn’t be loaded. Re-check Steps 2–3 before generating.`, { variant: 'warning' })
    } else {
      enqueueSnackbar(`Resumed “${deal?.brandName || 'proposal'}” — continuing the same deal${resume.status === 'GENERATED' ? ' at Review & Next Steps' : ''}.`, { variant: 'success' })
    }
  }, [resume, resumeId, resumeDetail, resumeDetailError, resumeVersion, resumeVersionError, hydrated, brand, enqueueSnackbar])

  // ── Step 4 lifecycle actions ────────────────────────────────────────
  const onRegenerated = (res) => { setVersionId(res.versionId); setGenResult(res) }
  const doSubmitToL1 = async () => {
    if (!proposalId) { enqueueSnackbar('Proposal isn’t created yet — go back and complete Step 1.', { variant: 'warning' }); return }
    // Aug-2026 loss-deal flow (QA #6): submit now accepts route 3 too — a sub-10% deal
    // goes to L1 like any other; only APPROVAL is FH-reserved there. Push-to-Pricing
    // stays available as a secondary path (order-level data), no longer forced.
    const r = genResult?.route
    if (r !== 1 && r !== 2 && r !== 3) { enqueueSnackbar('No valid route on this proposal yet — re-generate. (If it stays empty, the courier commercials aren’t loaded on this environment.)', { variant: 'warning' }); return }
    try {
      await submitToL1(proposalId).unwrap()
      enqueueSnackbar(r === 3
        ? 'Submitted for approval — L1 will review; final sign-off on a sub-10% deal rests with the Functional Head.'
        : 'Submitted to L1 Pricing Reviewer — awaiting approval.', { variant: 'success' })
      navigate(PATHS.slQueue)
    } catch (e) {
      enqueueSnackbar(e?.data?.message || e?.message || 'Submit failed — the deal must be in “Generated” state.', { variant: 'error' })
    }
  }
  const doPush = async () => {
    if (!proposalId) return
    try {
      await pushToPricing(proposalId).unwrap()
      // Backend has no order-template endpoint; the SL downloads/sends it manually.
      enqueueSnackbar('Pushed to Pricing — download the order template and upload billing data when the brand returns it.', { variant: 'warning' })
      navigate(PATHS.slQueue)
    } catch (e) { enqueueSnackbar(e?.message || 'Push failed.', { variant: 'error' }) }
  }
  // System margin (guide §7): GENERATED only; marginPct ≥ system minimum (10%).
  const doSystemMargin = async (marginPct) => {
    if (!versionId) { enqueueSnackbar('Generate the proposal first — there’s no version to re-margin yet.', { variant: 'warning' }); return }
    const m = Number(marginPct)
    if (!(m >= 10)) { enqueueSnackbar('System margin must be at least 10%.', { variant: 'warning' }); return }
    try {
      const res = await systemMargin({ versionId, body: { marginPct: m } }).unwrap()
      onRegenerated(res)
      enqueueSnackbar(`Regenerated at system margin ${m}% — now ${res?.bucket || '—'} · Route ${res?.route ?? '—'}.`, { variant: 'success' })
    } catch (e) { enqueueSnackbar(e?.data?.message || e?.message || 'System-margin regenerate failed.', { variant: 'error' }) }
  }
  // FSC (guide §8): GENERATED only + route in [2, 3].
  const doFsc = async (fscPct) => {
    if (!versionId) { enqueueSnackbar('Generate the proposal first — there’s no version to apply FSC to yet.', { variant: 'warning' }); return }
    const r = genResult?.route
    if (r !== 2 && r !== 3) { enqueueSnackbar('Fuel surcharge applies only to Route 2 / 3 deals (it’s disabled on Route 1).', { variant: 'warning' }); return }
    const f = Number(fscPct)
    if (!(f > 0)) { enqueueSnackbar('Enter a fuel surcharge % greater than zero.', { variant: 'warning' }); return }
    try {
      const res = await fsc({ versionId, body: { fscPct: f } }).unwrap()
      onRegenerated(res)
      enqueueSnackbar(`Fuel surcharge ${f}% applied — now ${res?.bucket || '—'} · Route ${res?.route ?? '—'}.`, { variant: 'success' })
    } catch (e) { enqueueSnackbar(e?.data?.message || e?.message || 'FSC apply failed.', { variant: 'error' }) }
  }
  const step4Busy = submitState.isLoading || pushState.isLoading || smState.isLoading || fscState.isLoading
  // Save draft — force an immediate snapshot save (don't rely on the 1s debounce, since we
  // navigate away right after), then leave the wizard. PROD-BUG-02: this snapshot is what
  // Resume restores the full wizard from.
  const doSaveDraft = async () => {
    if (proposalId) {
      try { await saveDraftState({ proposalId, draftState: JSON.stringify(snapshotState()) }).unwrap() } catch { /* best-effort */ }
    }
    enqueueSnackbar('Draft saved — find it under My Commercials to resume.', { variant: 'success' })
    navigate(PATHS.slDashboard)
  }

  // QA fast-fill (canPrefill only): the courier master feeds a REAL parent name into the
  // pre-filled preference so Generate doesn't trip on a made-up courier. Same args as
  // Step 3's own query → shared RTK cache, no extra request.
  const { data: prefillCouriers = [] } = useGetAllCourierPartnersQuery({ offset: 0, limit: 15000, excludes: 'sla' }, { skip: !canPrefill })
  const doPrefill = () => {
    setCardType('blended')
    setCardVariant(null)
    setBrand('Aurel Wellness')
    setRegisteredCompany('Aurel Wellness Private Limited')
    setCategory('Beauty & Personal Care')
    setHubspot('HS-10421')
    setEnterpriseSize('Small')
    setTotVol('10000')
    setBrandTotalVol('25000')
    setMinWt('0.2')
    setMaxWt('5')
    setAvgWt('0.6')
    setCodShare('55')
    setRtoShare('18')
    setCurrentRvp('4')
    setAvgInvoiceValue('899')
    setRevPot('12')
    const pks = [{ id: 1, pin: '110001', vol: '6000' }, { id: 2, pin: '400001', vol: '4000' }]
    setPickups(pks)
    pks.forEach((p) => resolvePin(p.pin))
    setZoneSplit(RFI_ZONES.map((z, i) => ({ zone: z, pct: String([30, 20, 25, 15, 10][i]) })))
    // D2C wizard redesign (Aug 2026) — wtTiers/wtSplit must agree (tierSlabRanges(wtTiers)),
    // one row per individual slab (no "band" grouping), and the structure must fit within
    // Max weight (5 kg). The first band is always exactly 1 slab: 0.5kg×1 (0–0.5 kg) +
    // 1kg×3 (0.5–3.5 kg) = 3.5 kg total → 4 slabs.
    setWtTiers([{ size: 0.5, count: 1 }, { size: 1, count: 3 }])
    setWtSplit([
      { from: '0', to: '0.5', pct: '25' }, { from: '0.5', to: '1.5', pct: '25' },
      { from: '1.5', to: '2.5', pct: '25' }, { from: '2.5', to: '3.5', pct: '25' },
    ])
    setRfiMode('pct')
    setRepAll(true)
    setPerSvcSplit(false)
    const first = prefillCouriers.find((c) => c.parent || c.name)
    const cp = first?.parent || first?.name || 'Delhivery'
    setServices(['srf'])
    setSvcConfig({
      srf: {
        ...defaultSvcCfg(),
        saved: true,
        _seeded: true,
        vol: '10000',
        mode: 'margin',
        margin: '15',
        cities: pks.map((p) => ({ pin: p.pin, value: cityFor(p.pin) || p.pin, vol: p.vol })),
        cod: { flat: '30', pct: '1.5', rtoPct: '80' },
        primary: [{ parent: cp }],
      },
    })
    setShowErr(false)
    enqueueSnackbar('Wizard pre-filled with test data (FWD Surface, Target Margin) — adjust anything, then walk the steps.', { variant: 'success' })
  }

  const toggleService = (key) =>
    setServices((prev) => (prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]))

  // SDD/NDD locks the service list to itself; every card pick resets the blended/courier-wise
  // sub-choice so an earlier card's choice never silently carries over onto the new one.
  const onPickCardType = (key) => {
    if (key === cardType) return
    setCardType(key)
    setCardVariant(null)
    // Restricted cards (SDD/NDD, H&B) scope the picker to a different service set, so clear
    // the prior selection — a regular pick doesn't carry into SDD/NDD or H&B (Large-only).
    if (key === 'sddndd' || key === 'hb') setServices([])
  }

  const addPickup = () => setPickups((p) => [...p, { id: Date.now(), pin: '', vol: '' }])
  const removePickup = (id) => setPickups((p) => p.filter((x) => x.id !== id))
  const setPickup = (id, field, val) => {
    setPickups((p) => p.map((x) => (x.id === id ? { ...x, [field]: val } : x)))
    if (field === 'pin') resolvePin(val)
  }

  const pickupTotal = pickups.reduce((s, p) => s + (parseFloat(p.vol) || 0), 0)
  // Sum of svcConfig[k].vol across the selected services — the SAME field the Service
  // type distribution section (Step 1) writes into and Step 3 reads, so this is correct
  // regardless of whether each service was entered by absolute count or by split %.
  const svcVolTotal = services.reduce((s, k) => s + (parseFloat(svcConfig[k]?.vol) || 0), 0)
  const zoneTotal = zoneSplit.reduce((s, z) => s + (parseFloat(z.pct) || 0), 0)
  const wtTotal = wtSplit.reduce((s, w) => s + (parseFloat(w.pct) || 0), 0)

  // ── Metro zone gate (sales feedback A1/A2) ────────────────────────────────────────────
  // classifyZone() yields "Metros" only when BOTH ends are metro, so with no metro pickup a
  // Metros split is guaranteed-fictitious volume. The backend rejects it at generate
  // (D2cProposalService FB-07); this mirrors the SAME rule at the zone-split step so the work
  // isn't lost three steps later.
  //
  // Mirrors it EXACTLY, including the permissiveness: block only when EVERY pickup row
  // affirmatively resolves non-metro in d2c_pincode_master. A blank row, a pin still
  // resolving, or a pin the master doesn't carry (source: 'fallback' → isMetro null) is
  // inconclusive and leaves Metro open — the master is partial by design, and gating on the
  // local zoneTag heuristic instead would block deals the backend accepts.
  const metroBlocked = pickups.length > 0 && pickups.every((r) => {
    const info = pinCache[r.pin]
    return /^\d{6}$/.test(r.pin || '') && info?.inMaster === true && info.isMetro === false
  })
  // Every zone array in play for the current RFI mode — what the gate must check.
  const effectiveZoneSplits = () => {
    if (rfiMode === 'matrix') { try { return [deriveSplits().zoneSplit] } catch { return [] } }
    if (perSvcSplit) return services.map((k) => svcSplits[k]?.zones).filter(Boolean)
    return [zoneSplit]
  }
  const metroPctEntered = () => effectiveZoneSplits().some((zs) => (zs || []).some((z) => isMetroZone(z.zone) && (parseFloat(z.pct) || 0) > 0))
  const METRO_BLOCK_MSG = 'The zone-wise split allocates volume to Metro, but no pickup location is in a metro city. Add a metro pickup PIN on Step 1, or move the Metro % to another zone.'

  // BUG-07: per-service RFI splits. OFF (default) = one split explicitly applied to
  // every service; ON = each service gets its own zone + weight split, and rfiEntries
  // are built per service instead of repeating one split.
  const [perSvcSplit, setPerSvcSplit] = useState(false)
  const [svcSplits, setSvcSplits] = useState({}) // key → { zones: [...], wts: [...] }
  const togglePerSvcSplit = (on) => {
    setPerSvcSplit(on)
    if (on) {
      // Seed each service from the current global split so nothing is lost on switch.
      setSvcSplits((prev) => {
        const next = { ...prev }
        services.forEach((k) => {
          if (!next[k]) {
            // Seed only the zones this service actually serves (SDD/NDD excludes ROI + Special),
            // carrying over any % already set on the global split.
            next[k] = {
              zones: zonesForService(k).map((zn) => { const g = zoneSplit.find((r) => r.zone === zn); return { zone: zn, pct: g ? g.pct : '' } }),
              wts: wtSplit.map((w) => ({ ...w })),
              tiers: wtTiers.map((t) => ({ ...t })),
            }
          }
        })
        return next
      })
    }
  }

  // ── Draft autosave (PROD-BUG-02) ─────────────────────────────────────
  // The full wizard snapshot — everything Resume needs to restore the deal exactly.
  const snapshotState = useCallback(() => ({
    v: 1, cardType, cardVariant, services, brand, hubspot, category, maxWt, totVol, revPot, avgWt,
    codShare, rtoShare, avgInvoiceValue,
    registeredCompany, enterpriseSize, brandTotalVol, minWt, currentRvp,
    pickups, zoneSplit, wtSplit, wtTiers,
    rfiMode, repAll, matrixData, svcConfig, perSvcSplit, svcSplits, active,
  }), [cardType, cardVariant, services, brand, hubspot, category, maxWt, totVol, revPot, avgWt, codShare,
    rtoShare, avgInvoiceValue, registeredCompany, enterpriseSize, brandTotalVol, minWt, currentRvp,
    pickups, zoneSplit, wtSplit, wtTiers, rfiMode, repAll,
    matrixData, svcConfig, perSvcSplit, svcSplits, active])

  // Debounced autosave once a draft exists — captures every edit (incl. Step 3, which the
  // SL fills without a further step-advance). Skipped until resume-hydration completes so we
  // never overwrite a saved draft with half-loaded state. Fire-and-forget; errors ignored.
  useEffect(() => {
    if (!proposalId) return
    // STANDARD serves immediately (DRAFT→SENT) and has no resumable wizard state — autosaving
    // draft-state would fire against a now-SENT deal and 400. Skip it for Standard.
    if (cardType === 'standard') return
    if (resume && !hydrated) return
    const snap = JSON.stringify(snapshotState())
    const t = setTimeout(() => { try { saveDraftState({ proposalId, draftState: snap }).unwrap().catch(() => {}) } catch { /* ignore */ } }, 1000)
    return () => clearTimeout(t)
  }, [proposalId, cardType, resume, hydrated, snapshotState, saveDraftState])

  // ── validation gates ───────────────────────────────────────────────
  // Hard Stop 2 (Decision Flow v7): "weight ranges may not overlap" — QA BUG-12:
  // 0–0.5 and 0.4–1 previously sailed through to Commercial Expectations.
  const bandOverlapError = (bands) => {
    const rows = (bands || [])
      .map((b) => ({ from: parseFloat(b.from), to: parseFloat(b.to) }))
      .filter((b) => !Number.isNaN(b.from) && !Number.isNaN(b.to))
    for (const b of rows) {
      if (b.to <= b.from) return `Weight band ${b.from}–${b.to} kg is invalid — "To" must be greater than "From".`
    }
    const sorted = [...rows].sort((a, b) => a.from - b.from)
    for (let i = 1; i < sorted.length; i += 1) {
      if (sorted[i].from < sorted[i - 1].to) {
        return `Weight bands ${sorted[i - 1].from}–${sorted[i - 1].to} kg and ${sorted[i].from}–${sorted[i].to} kg overlap — weight ranges may not overlap.`
      }
    }
    return ''
  }
  const validate = (target) => {
    if (target >= 1) {
      if ((cardType === 'sddndd' || cardType === 'hb') && !cardVariant) return 'Pick Blended or Courier-wise for this commercial type.'
      if (!brand.trim()) return 'Brand name is required.'
      if (!registeredCompany.trim()) return 'Registered company name is required.'
      if (!category) return 'Product category must be selected.'
      if (!enterpriseSize) return 'Enterprise size must be selected.'
      if (!String(maxWt || '').trim()) return 'Max shipment weight is required.'
      if (!String(minWt || '').trim()) return 'Min shipment weight is required.'
      if (!(parseFloat(totVol) > 0)) return 'Total monthly volume must be greater than zero.'
      if (!(parseFloat(brandTotalVol) > 0)) return 'Total monthly shipments of the brand must be greater than zero.'
      if (parseFloat(totVol) >= parseFloat(brandTotalVol)) return 'Shipments allocated to Prozo must be less than the brand’s total monthly volume.'
      if (!String(avgWt || '').trim()) return 'Avg shipment weight is required.'
      if (!String(codShare || '').trim() || !(parseFloat(codShare) >= 0) || parseFloat(codShare) > 100)
        return 'COD share is required and must be between 0% and 100%.'
      if (!String(rtoShare || '').trim() || !(parseFloat(rtoShare) >= 0) || parseFloat(rtoShare) > 100)
        return 'Current RTO % is required and must be between 0% and 100%.'
      if (!String(currentRvp || '').trim() || !(parseFloat(currentRvp) >= 0) || parseFloat(currentRvp) > 100)
        return 'Current RVP % is required and must be between 0% and 100%.'
      if (!(parseFloat(avgInvoiceValue) > 0)) return 'Average invoice value must be greater than zero.'
      if (!String(revPot || '').trim()) return 'Monthly revenue potential is required.'
      if (pickups.some((p) => (p.pin || '').trim() && !/^\d{6}$/.test((p.pin || '').trim())))
        return 'Pickup PIN codes must be valid 6-digit pincodes.'
      {
        const pins = pickups.map((p) => (p.pin || '').trim()).filter(Boolean)
        const dupPin = pins.find((pin, i) => pins.indexOf(pin) !== i)
        if (dupPin) return `Pickup PIN ${dupPin} is entered more than once — duplicate pincodes are not allowed.`
        const badCityPin = pins.find((pin) => pin.length === 6 && !(pinCache[pin]?.city || cityStateFor(pin)))
        if (badCityPin) return `Pickup PIN ${badCityPin} — city/zone could not be resolved. Use a valid pincode.`
      }
      if (Math.round(pickupTotal) !== Math.round(parseFloat(totVol) || 0))
        return `Pickup volumes (${pickupTotal}) must equal the total monthly volume (${totVol}).`
      if (services.length === 0) return 'Select at least one service.'
      {
        const blankSvc = services.find((k) => !(parseFloat(svcConfig[k]?.vol) > 0))
        if (blankSvc) return `${labelForKey(blankSvc)} has no volume entered in Service type distribution — every selected service needs a share of the total.`
      }
      if (Math.round(svcVolTotal) !== Math.round(parseFloat(totVol) || 0))
        return `Per-service volumes (${svcVolTotal}) must add up to the shipments allocated to Prozo (${totVol}) — check Service type distribution above.`
    }
    if (target >= 2) {
      if (rfiMode === 'matrix') {
        const idxs = repAll ? [0] : pickups.map((_, i) => i)
        for (const i of idxs) {
          const t = Math.round(matrixTotal(matrixRows(i)))
          if (t !== 100) return `Pickup ${repAll ? '(all)' : (pickups[i]?.pin || `#${i + 1}`)} matrix must total 100% (currently ${t}%).`
          const bandErr = bandOverlapError(matrixRows(i))
          if (bandErr) return bandErr
        }
      } else if (rfiMode === 'pct') {
        if (perSvcSplit) {
          // BUG-07: each service's own split must independently reconcile (Stop 2).
          for (const k of services) {
            const sp = svcSplits[k]
            if (!sp) return `${labelForKey(k)}: splits missing — toggle "differentiate by service" again to seed them.`
            const zt = Math.round(sp.zones.reduce((s, z) => s + (parseFloat(z.pct) || 0), 0))
            if (zt !== 100) return `${labelForKey(k)}: zone-wise split must total 100% (currently ${zt}%).`
            const wt = Math.round(sp.wts.reduce((s, w) => s + (parseFloat(w.pct) || 0), 0))
            if (wt !== 100) return `${labelForKey(k)}: weight-wise split must total 100% (currently ${wt}%).`
            const bandErr = bandOverlapError(sp.wts)
            if (bandErr) return `${labelForKey(k)}: ${bandErr}`
          }
        } else {
          if (Math.round(zoneTotal) !== 100) return `Zone-wise split must total 100% (currently ${zoneTotal}%).`
          if (Math.round(wtTotal) !== 100) return `Weight-wise split must total 100% (currently ${wtTotal}%).`
          const bandErr = bandOverlapError(wtSplit)
          if (bandErr) return bandErr
        }
      }
      // copy / file modes: no inline total check (copy loads the quick split; file is parsed server-side)
      // Runs for EVERY rfiMode, including copy/file — the backend gate at generate does too,
      // and a copied or uploaded split can carry a Metro % just as easily as a typed one.
      if (metroBlocked && metroPctEntered()) return METRO_BLOCK_MSG
    }
    if (target >= 3) {
      // Step 3 no longer has a per-service "Save" click to gate on — the same live
      // validator that drives each service's status banner is what gates Continue here.
      for (const k of services) {
        const cfg = svcConfig[k] || defaultSvcCfg()
        const svcTiers = (perSvcSplit ? svcSplits[k]?.tiers : null) || wtTiers
        const err = validateServiceCfg({ cfg, label: labelForKey(k), isReverse: serviceMeta(k).isReverse, tiers: svcTiers, rateCardLibrary, dealRateMode, serviceType: serviceMeta(k).apiValue })
        if (err) return err
      }
    }
    return ''
  }

  const goStep = async (target) => {
    if (target > active) {
      const err = validate(target)
      // The inline field-level red text is easy to miss — especially when the failing
      // field sits behind a sub-tab (e.g. Step 2's Structure/% Share split) that isn't
      // the one currently open. A popup makes the actual problem impossible to miss.
      if (err) { setShowErr(true); enqueueSnackbar(err, { variant: 'error' }); return }
    }
    // Leaving Step 1 → create the DRAFT (POST /proposals) once.
    if (active === 0 && target === 1 && !proposalId) {
      try {
        const res = await createProposal(buildCreatePayload({ hubspot, brand, cardType: createCardKey, services, revPot })).unwrap()
        setProposalId(res.proposalId)
      } catch (e) {
        // Surface the server message (e.g. Hard Stop 1: HubSpot ID required for this
        // user) — RTK Query puts it on e.data.message, not e.message.
        enqueueSnackbar(e?.data?.message || e?.message || 'Could not create the proposal.', { variant: 'error' })
        return
      }
    }
    // Entering Step 3 → carry forward from Step 1 (once per service): pickup cities,
    // max weight, default mode (Surface→Target Rate Card, others→Target Margin).
    if (target === 2) {
      const seedCities = pickups.filter((p) => p.pin).map((p) => ({ pin: p.pin, value: pinCache[p.pin]?.city || cityFor(p.pin) || p.pin, vol: p.vol }))
      setSvcConfig((prev) => {
        const next = { ...prev }
        services.forEach((k) => {
          const cur = next[k] || defaultSvcCfg()
          // Seed the default mode once (skip if user-saved or already seeded, e.g. a template copy).
          const base = (cur.saved || cur._seeded)
            ? cur
            : { ...cur, _seeded: true, mode: serviceMeta(k).isSurface ? 'ratecard' : 'margin' }
          // Cities always fill from the current Step-1 pickups when still empty and not
          // user-saved — so a template-copied service still picks up the new prospect's
          // cities. Max weight is no longer per-service (deal-level Step 1 governs the
          // canonical slabs directly) — nothing to seed there any more.
          const cities = (!base.saved && (!base.cities || !base.cities.length) && seedCities.length) ? seedCities : base.cities
          next[k] = { ...base, cities }
        })
        return next
      })
    }
    // Leaving Step 3 → run the pricing engine (POST /generate).
    if (active === 2 && target === 3) {
      if (!proposalId) { enqueueSnackbar('Proposal was not created — go back to Step 1.', { variant: 'error' }); return }
      const eff = rfiMode === 'matrix' ? deriveSplits() : { zoneSplit, wtSplit }
      // BUG-07: per-service splits (quick-split mode only) → true per-service rfiEntries.
      const perSvc = rfiMode === 'pct' && perSvcSplit ? svcSplits : null
      // sddndd/hb + the courier-wise sub-choice send COURIER_WISE as the generate-time
      // rateCardMode only (effectiveCardKey/dealRateMode computed at component scope) — the
      // create-time cardType (doc-template selection) is untouched.
      const { payload, warnings } = buildGeneratePayload({ cardType: effectiveCardKey, services, svcConfig, pickups, zoneSplit: eff.zoneSplit, wtSplit: eff.wtSplit, wtTiers, svcSplits: perSvc, totVol, codShare, rtoShare, avgInvoiceValue, pinCache, registeredCompany, enterpriseSize, brandTotalVol, minWt, currentRvp })
      if (!payload.rfiEntries || !payload.rfiEntries.length) {
        enqueueSnackbar('The RFI zone × weight split (Step 2) is empty — go back to RFI Details and fill both splits to 100% before generating.', { variant: 'error' })
        return
      }
      // Standard-mode services: resolve the pre-built GTM card (service × dealRateMode ×
      // prospect tier) and stash it on the version's capturedInputs — free-form, no backend
      // change — so it persists and flows to review/approval and the client. No pricing runs
      // for these; the card is handed over as-is.
      const standardCards = {}
      services.forEach((k) => {
        if ((svcConfig[k] || {}).mode !== 'standard') return
        const tier = (svcConfig[k] || {}).prospectSize || 'SME'
        const svcEnum = SERVICE_MAP[serviceCodeToKey(k)]
        const { card, exact } = matchStandardCard(rateCardLibrary, { serviceType: svcEnum, rateCardType: dealRateMode, prospectTier: tier })
        if (card) standardCards[svcEnum] = { id: card.id, name: card.name, fileUri: card.fileUri || '', rateCardType: dealRateMode, prospectTier: tier, exact }
      })
      if (Object.keys(standardCards).length) payload.capturedInputs = { ...(payload.capturedInputs || {}), standardCards }
      try {
        const res = await generate({ proposalId, body: payload }).unwrap()
        setVersionId(res.versionId)
        setGenResult(res)
        setGenWarnings(warnings)
      } catch (e) { enqueueSnackbar(e?.data?.message || e?.message || 'Generate failed.', { variant: 'error' }); return }
    }
    setShowErr(false)
    setActive(target)
    window.scrollTo({ top: 0 })
  }

  const matrixPreview = useMemo(() => {
    const tv = parseFloat(totVol) || 0
    return wtSplit.map((w) => ({
      band: `${w.from}–${w.to} kg`,
      ...RFI_ZONES.reduce((acc, z, i) => {
        const zp = parseFloat(zoneSplit[i]?.pct) || 0
        acc[z] = Math.round((tv * (parseFloat(w.pct) || 0) / 100) * (zp / 100))
        return acc
      }, {}),
    }))
  }, [totVol, wtSplit, zoneSplit])

  // ── Per-pickup matrix helpers ──────────────────────────────────────────────
  const newMatrixRow = (from = 0, to = 0.5) => ({ from, to, counts: [0, 0, 0, 0, 0] })
  const matrixRows = (i) => matrixData[i] || [newMatrixRow(0, 0.5), newMatrixRow(0.5, 2)]
  const setMatrixRows = (i, rows) => setMatrixData((m) => ({ ...m, [i]: rows }))
  const matrixTotal = (rows) => rows.reduce((s, r) => s + r.counts.reduce((a, c) => a + (Number(c) || 0), 0), 0)
  // Volume-weighted marginals from the matrices → drive the API rfiEntries.
  const deriveSplits = () => {
    const idxs = repAll ? [0] : pickups.map((_, i) => i)
    const zoneAcc = [0, 0, 0, 0, 0]
    const bandMap = {}
    idxs.forEach((i) => {
      const w = (Number(pickups[i]?.vol) || 0) || 1
      matrixRows(i).forEach((r) => {
        const rowSum = r.counts.reduce((a, c) => a + (Number(c) || 0), 0)
        r.counts.forEach((c, zi) => { zoneAcc[zi] += (Number(c) || 0) * w })
        const key = `${r.from}-${r.to}`
        bandMap[key] = bandMap[key] || { from: r.from, to: r.to, acc: 0 }
        bandMap[key].acc += rowSum * w
      })
    })
    const zSum = zoneAcc.reduce((a, b) => a + b, 0) || 1
    const zoneSplitM = RFI_ZONES.map((z, zi) => ({ zone: z, pct: Math.round((zoneAcc[zi] / zSum) * 100) }))
    const bands = Object.values(bandMap)
    const bSum = bands.reduce((a, b) => a + b.acc, 0) || 1
    const wtSplitM = bands.map((b) => ({ from: b.from, to: b.to, pct: Math.round((b.acc / bSum) * 100) }))
    return { zoneSplit: zoneSplitM, wtSplit: wtSplitM }
  }

  // STANDARD hands over a pre-built card — no engine, no version, no approval. It short-circuits
  // the 4-step wizard: pick a format + volume → serve → download, DRAFT→SENT in one action.
  if (cardType === 'standard') {
    return (
      <StandardFlow
        {...{ cardType, onPickCardType, brand, setBrand, hubspot, setHubspot, revPot, setTotVol,
          createProposal, proposalId, setProposalId, cards: standardMasterCards, cardsLoading: standardCardsLoading }}
      />
    )
  }

  // Gates the footer's Continue/Generate button and its hint on Step 3 — the same live
  // per-service validator that drives each service's status banner, not a "saved" flag.
  const step3Complete = services.length > 0 && services.every((k) => {
    const svcTiers = (perSvcSplit ? svcSplits[k]?.tiers : null) || wtTiers
    return !validateServiceCfg({ cfg: svcConfig[k] || defaultSvcCfg(), label: labelForKey(k), isReverse: serviceMeta(k).isReverse, tiers: svcTiers, rateCardLibrary, dealRateMode, serviceType: serviceMeta(k).apiValue })
  })

  return (
    <Box sx={{ px: 3 }}>
      <PageTitle>New Proposal · {WIZARD_STEPS[active].label}</PageTitle>

      {/* Progress journey (Option C) — each step is an icon chip on a grounded, tinted panel:
          done chips carry a check, the current chip is solid brand, upcoming are muted. The
          step's "export this page" pill lives in the same header, so nothing floats in white. */}
      <Box sx={{
        background: 'linear-gradient(180deg, #FBFCFE, #F1F6FE)', border: '1px solid', borderColor: 'divider',
        borderRadius: '14px', px: { xs: 1.5, md: 2 }, py: 1.5, mb: 1.5, boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}>
        {/* Stepper fills the full width — the connectors stretch so chips span edge to edge */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {WIZARD_STEPS.map((s, i) => {
            const done = i < active
            const on = i === active
            const Icon = STEP_ICONS[i] || DescriptionOutlinedIcon
            return (
              <React.Fragment key={s.key}>
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 1, pl: 0.6, pr: 1.6, py: 0.7, borderRadius: '999px',
                  border: '1.5px solid', whiteSpace: 'nowrap', transition: 'all .18s ease',
                  borderColor: on ? 'primary.main' : done ? '#BBD3F5' : 'divider',
                  bgcolor: on ? 'primary.main' : '#fff',
                  color: on ? '#fff' : done ? 'text.primary' : 'text.disabled',
                  boxShadow: on ? '0 6px 16px rgba(32,101,209,0.25)' : 'none',
                  opacity: (!on && !done) ? 0.85 : 1,
                  fontSize: { xs: 12, sm: 13 }, fontWeight: 700,
                }}>
                  <Box sx={{
                    width: 26, height: 26, borderRadius: '50%', display: 'grid', placeItems: 'center', flexShrink: 0,
                    bgcolor: on ? 'rgba(255,255,255,0.22)' : done ? 'primary.main' : '#EAF1FC',
                    color: on ? '#fff' : done ? '#fff' : 'primary.main',
                  }}>
                    {done ? <CheckIcon sx={{ fontSize: 16 }} /> : <Icon sx={{ fontSize: 15 }} />}
                  </Box>
                  {s.label}
                </Box>
                {i < WIZARD_STEPS.length - 1 && (
                  <Box sx={{ flex: 1, minWidth: 12, height: 2, borderRadius: 2, bgcolor: done ? 'primary.main' : 'divider', display: { xs: 'none', sm: 'block' } }} />
                )}
              </React.Fragment>
            )
          })}
        </Box>
      </Box>

      {/* Export for the current step — sits on the canvas below the stepper bar, right-aligned.
          The QA fast-fill pill (canPrefill logins only) shares the same row on every step. */}
      {(exportMeta || canPrefill) && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2.5 }}>
          {canPrefill && (
            <Button
              onClick={doPrefill}
              sx={{
                textTransform: 'none', fontSize: 13.5, fontWeight: 800, borderRadius: '999px', px: 2, py: 0.65,
                color: '#7C3AED', bgcolor: '#F5F0FE', border: '1.5px solid #DDD0F7', boxShadow: '0 1px 2px rgba(16,24,40,0.06)',
                whiteSpace: 'nowrap', flexShrink: 0,
                '&:hover': { bgcolor: '#EDE4FD', borderColor: '#7C3AED' },
              }}
            >
              ⚡ Pre-fill test data
            </Button>
          )}
          {exportMeta && (
          <Button
            onClick={() => exportFnRef.current && exportFnRef.current()}
            disabled={exportMeta.disabled}
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
            sx={{
              // Subtler than a primary CTA — visible (blue tint) without competing for attention.
              textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '999px', px: 1.75, py: 0.55,
              color: '#2065D1', bgcolor: '#F5F9FE', border: '1px solid #DCE7FA',
              whiteSpace: 'nowrap', flexShrink: 0,
              '&:hover': { bgcolor: '#EAF1FC', borderColor: '#B8D2F5' },
              '&.Mui-disabled': { color: '#B0B9C7', bgcolor: '#F6F7FA', borderColor: '#E7EAF0' },
            }}
          >
            {exportMeta.label}
          </Button>
          )}
        </Box>
      )}

      {active === 0 && (
        <Step1
          {...{ cardType, onPickCardType, cardVariant, setCardVariant, services, toggleService, brand, setBrand, hubspot, setHubspot, category, setCategory, maxWt, setMaxWt, totVol, setTotVol, revPot, setRevPot, avgWt, setAvgWt, codShare, setCodShare, rtoShare, setRtoShare, avgInvoiceValue, setAvgInvoiceValue, registeredCompany, setRegisteredCompany, enterpriseSize, setEnterpriseSize, brandTotalVol, setBrandTotalVol, minWt, setMinWt, currentRvp, setCurrentRvp, svcConfig, setSvcConfig, svcVolTotal, pickups, addPickup, removePickup, setPickup, pickupTotal, pinCache, showErr, exportHost }}
        />
      )}
      {active === 1 && <Step2 {...{ zoneSplit, setZoneSplit, wtSplit, setWtSplit, wtTiers, setWtTiers, zoneTotal, wtTotal, matrixPreview, maxWt, rfiMode, setRfiMode, repAll, setRepAll, activePk, setActivePk, pickups, metroBlocked, matrixRows, setMatrixRows, matrixTotal, newMatrixRow, services, perSvcSplit, togglePerSvcSplit, svcSplits, setSvcSplits, setSvcConfig, showErr, exportHost }} />}
      {active === 2 && <Step3 {...{ services, svcConfig, setSvcConfig, pickups, pinCache, wtTiers, perSvcSplit, svcSplits, rtoShare, codShare, rateCardLibrary, dealRateMode, showErr, exportHost }} />}
      {active === 3 && (
        <Step4
          cardType={cardType} services={services} result={genResult} warnings={genWarnings}
          proposalId={proposalId} versionId={versionId} busy={step4Busy}
          brand={brand} hubspot={hubspot} category={category} maxWt={maxWt} totVol={totVol} revPot={revPot} avgWt={avgWt}
          registeredCompany={registeredCompany} enterpriseSize={enterpriseSize} brandTotalVol={brandTotalVol} minWt={minWt} currentRvp={currentRvp}
          pickups={pickups} pinCache={pinCache} svcConfig={svcConfig}
          codShare={codShare} rtoShare={rtoShare} avgInvoiceValue={avgInvoiceValue}
          rfiMode={rfiMode} zoneSplit={zoneSplit} wtSplit={wtSplit} wtTiers={wtTiers} perSvcSplit={perSvcSplit} svcSplits={svcSplits}
          rateCardLibrary={rateCardLibrary} dealRateMode={dealRateMode}
          onSystemMargin={doSystemMargin} onFsc={doFsc} onSaveDraft={doSaveDraft} onEditInputs={() => setActive(1)}
          exportHost={exportHost}
        />
      )}

      {/* Wizard footer — its own card at the end of the page, below all step content
          (not sticky/floating — per explicit product call, it scrolls with the page).
          Toasts anchor top-center (useWizardSnackbar) so they never cover these buttons. */}
      <Box sx={{
        mt: 2,
        display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5,
        bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
        borderRadius: '12px', boxShadow: '0 1px 3px rgba(16,24,40,0.06)',
      }}>
        <Button variant="outlined" sx={{ ...btn, px: 2.5, borderColor: '#AFBBCC', color: 'text.primary', fontWeight: 700, '&:hover': { borderColor: 'primary.main', color: 'primary.main', bgcolor: '#F5F9FE' } }} disabled={busy} onClick={() => (active === 0 ? navigate(-1) : goStep(active - 1))}>
          {active === 0 ? 'Cancel' : '← Back'}
        </Button>
        <Box sx={{ flex: 1 }} />
        {active === 2 && !step3Complete && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Fill every service (green ✓) to generate the proposal.</Typography>
        )}
        {active < 3 ? (
          <Button variant="contained" sx={btn} disabled={busy || (active === 2 && !step3Complete)} onClick={() => goStep(active + 1)}>
            {busy ? (active === 2 ? 'Generating…' : 'Saving…') : active === 2 ? 'Generate & Review Proposal →' : `Continue → ${WIZARD_STEPS[active + 1].label}`}
          </Button>
        ) : genResult?.route === 3 ? (
          // Aug-2026 loss-deal flow (QA #6): sub-10% deals SUBMIT like any other (L1
          // reviews, only FH can approve). Push-to-Pricing stays as the secondary path
          // for deals that genuinely need the pricing desk's order-level data.
          <>
            <Button variant="outlined" sx={btn} disabled={step4Busy || !proposalId} onClick={doPush}>
              {step4Busy ? 'Working…' : 'Push to Pricing (order-level data)'}
            </Button>
            <Button variant="contained" sx={btn} disabled={step4Busy || !proposalId} onClick={doSubmitToL1}>
              {step4Busy ? 'Working…' : 'Submit for approval'}
            </Button>
          </>
        ) : (genResult?.route === 1 || genResult?.route === 2) ? (
          <Button variant="contained" sx={btn} disabled={step4Busy || !proposalId} onClick={doSubmitToL1}>
            {step4Busy ? 'Working…' : 'Submit to L1 Pricing Reviewer'}
          </Button>
        ) : (
          // No valid route (engine didn't return 1/2/3 — usually missing courier commercials).
          <Button variant="contained" sx={btn} disabled>Submit to L1 — route unavailable</Button>
        )}
      </Box>
    </Box>
  )
}

/* ──────────── Standard-mode flow (short-circuits the 4-step wizard) ──────────── */
// STANDARD is a whole pre-built card handed over as-is: no pricing engine, no version, no
// approval. Pick a format + expected volume → POST /standard-card serves the file and moves
// the deal DRAFT→SENT in one action. RVP auto-attaches; >45,000 routes to the custom desk.
function StandardFlow({ cardType, onPickCardType, brand, setBrand, hubspot, setHubspot, revPot, setTotVol,
  createProposal, proposalId, setProposalId, cards = [], cardsLoading = false }) {
  const { enqueueSnackbar } = useWizardSnackbar()
  const navigate = useNavigate()
  const [serveStandard, serveState] = useServeStandardCardMutation()
  const [format, setFormat] = useState(null)
  const [shipments, setShipments] = useState('')
  const [result, setResult] = useState(null)
  const [showErr, setShowErr] = useState(false)
  const availableFmts = useMemo(() => new Set((cards || []).map((c) => c.cardFormat)), [cards])
  const shipNum = Number(shipments)
  const busy = serveState.isLoading
  const btn = { textTransform: 'none', borderRadius: '8px', fontSize: 13 }

  const dl = (url) => (url ? window.open(url, '_blank', 'noopener') : enqueueSnackbar('No file on this card.', { variant: 'warning' }))

  const doServe = async () => {
    setShowErr(true)
    if (!brand.trim() || !format || !(shipNum > 0)) return
    const fmt = STANDARD_FORMATS.find((f) => f.key === format)
    try {
      let pid = proposalId
      if (!pid) {
        const res = await createProposal(buildCreatePayload({ hubspot, brand, cardType: 'standard', services: [], revPot })).unwrap()
        pid = res.proposalId
        setProposalId(pid)
      }
      const res = await serveStandard({ proposalId: pid, body: { cardFormat: fmt.api, expectedShipments: shipNum } }).unwrap()
      setResult(res)
      if (!res.aboveCeiling) enqueueSnackbar('Standard rate card served — the deal is now Sent.', { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(e?.data?.message || e?.message || 'Could not serve the standard rate card.', { variant: 'error' })
    }
  }

  // >45k → drop into the normal engine wizard, format + volume pre-filled.
  const goCustom = () => {
    if (setTotVol) setTotVol(String(shipNum))
    setResult(null)
    onPickCardType({ blended: 'blended', cpwise: 'cpwise', sdd: 'sddndd', ndd: 'sddndd', hnb: 'hb' }[format] || 'blended')
  }

  return (
    <Box sx={{ px: 3 }}>
      <PageTitle>New Proposal · Standard rate card</PageTitle>

      <SectionCard title="1 · Pick the commercial type" subtitle="Standard hands over a pre-built Prozo card as-is — no pricing engine and no approval step. Pick another card to price with the engine.">
        <ChoiceCards
          value={cardType}
          onChange={onPickCardType}
          minWidth={220}
          options={COMMERCIAL_TYPES.map((t) => ({ value: t.key, title: t.title, desc: t.desc, icon: CT_ICONS[t.key] }))}
        />
      </SectionCard>

      {result && result.aboveCeiling ? (
        <SectionCard title="Above standard paper" subtitle="This volume is beyond the standard rate-card ceiling.">
          <Alert severity="info" sx={{ fontSize: 13 }}>{result.message || `Above ${result.ceiling || 45000} monthly shipments standard paper ends — route to the custom pricing desk.`}</Alert>
          <Button variant="contained" sx={{ ...btn, mt: 1.5 }} onClick={goCustom}>Continue to custom pricing →</Button>
        </SectionCard>
      ) : result ? (
        <SectionCard title="Standard rate card served" subtitle="The deal is Sent — this card goes to the client as-is. No pricing or approval runs on Standard.">
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {/* Tier name from the backend (D2 tiers: Startups … Platinum); band range kept alongside. */}
              <Pill label={`${result.cardFormat}${result.tier ? ` · ${result.tier}` : ''}${result.band ? ` · ${result.band}` : ''}`} styleKey="approved" />
              <Pill label={`${(result.committedShipments || shipNum).toLocaleString('en-IN')} shipments/mo committed`} styleKey="neutral" />
              <Pill label="Sent" styleKey="approved" />
            </Stack>
            <Button variant="contained" sx={btn} onClick={() => dl(result.forward?.url)}>⬇ {result.forward?.fileName || 'Forward rate card'}</Button>
            {result.rvp ? (
              <Button variant="outlined" sx={btn} onClick={() => dl(result.rvp?.url)}>⬇ {result.rvp?.fileName || 'RVP rate card'}</Button>
            ) : result.rvpMissing ? (
              <Alert severity="warning" sx={{ fontSize: 12 }}>No RVP card is uploaded for this format yet — forward card only.</Alert>
            ) : null}
            <Box><Button variant="text" sx={btn} onClick={() => navigate(PATHS.slHistory)}>← Back to Proposal History</Button></Box>
          </Stack>
        </SectionCard>
      ) : (
        <SectionCard title="2 · Standard rate card" subtitle="Pick the card format and the client's expected monthly volume. No service types, pincodes or margins — the file is pre-decided.">
          {cardsLoading && <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>Loading available cards…</Typography>}
          {!cardsLoading && !cards.length && <Alert severity="warning" sx={{ fontSize: 12, mb: 1.5 }}>No standard rate cards are uploaded yet — ask the Functional Head to add them.</Alert>}
          <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>Which standard rate card?</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {STANDARD_FORMATS.map((f) => {
              const has = availableFmts.has(f.api)
              const on = format === f.key
              return (
                <Box key={f.key} onClick={() => has && setFormat(f.key)}
                  sx={{ cursor: has ? 'pointer' : 'not-allowed', opacity: has ? 1 : 0.45, border: '1.5px solid',
                    borderColor: on ? 'primary.main' : (showErr && !format ? 'error.main' : 'divider'),
                    bgcolor: on ? 'rgba(32,101,209,0.06)' : '#fff', borderRadius: '8px', px: 2, py: 1, minWidth: 108, textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 12, color: on ? 'primary.main' : 'text.primary' }}>{f.label}</Typography>
                  {!has && <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>no card</Typography>}
                </Box>
              )
            })}
          </Stack>
          {showErr && !format && <Typography sx={{ fontSize: 11, color: 'error.main', mb: 1.5 }}>Pick a card format.</Typography>}
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" required label="Brand name" value={brand} onChange={(e) => setBrand(e.target.value)} error={showErr && !brand.trim()} {...helperProps(showErr && !brand.trim() ? 'Brand name is required.' : undefined)} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" label="HubSpot Deal ID" value={hubspot} onChange={(e) => setHubspot(e.target.value)} placeholder="e.g. HS-93481" InputProps={infoAdorn('From the HubSpot deal URL. Optional / mandatory per FH config.')} /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth size="small" required type="number" label="Expected monthly shipments" value={shipments} onChange={(e) => setShipments(e.target.value)} error={showErr && !(shipNum > 0)} InputProps={infoAdorn('Becomes the committed volume on the agreement; also resolves the band for Blended / Courier-wise.')} {...helperProps(showErr && !(shipNum > 0) ? 'Enter the expected monthly volume.' : undefined)} /></Grid>
          </Grid>
          <Button variant="contained" sx={{ ...btn, mt: 2 }} disabled={busy} onClick={doServe}>{busy ? 'Getting card…' : 'Get Standard Rate Card'}</Button>
        </SectionCard>
      )}

      <Stack direction="row" sx={{ mt: 1 }}>
        <Button variant="text" sx={btn} disabled={busy} onClick={() => navigate(-1)}>Cancel</Button>
      </Stack>
    </Box>
  )
}

/* ───────────────────────── Step 1 ───────────────────────── */
// Icons for the commercial-type choice cards (Step 1 + the Standard-flow switch).
const CT_ICONS = { blended: LayersOutlinedIcon, cpwise: AccountTreeOutlinedIcon, standard: DescriptionOutlinedIcon }

// Per-step icon for the Option-C chip stepper, indexed to WIZARD_STEPS.
const STEP_ICONS = [PersonOutlineIcon, AssignmentOutlinedIcon, CurrencyRupeeIcon, FlagOutlinedIcon]

/**
 * useHeaderExport — lets a wizard step publish its "export this step" action up to the
 * shared stepper header (Option C), so the export pill lives in the header panel instead
 * of floating in the page body. `host` is { fnRef, setMeta } from the parent. The handler
 * is kept on a ref (updated every render) so the pill always runs the latest closure —
 * no stale exports — while label/disabled changes drive a re-render via setMeta.
 */
function useHeaderExport(host, fn, label, disabled = false) {
  if (host) host.fnRef.current = fn
  useEffect(() => {
    if (!host) return undefined
    host.setMeta({ label, disabled })
    return () => host.setMeta(null)
  }, [host, label, disabled])
}

function Step1(props) {
  const { cardType, onPickCardType, cardVariant, setCardVariant, services, toggleService, brand, setBrand, hubspot, setHubspot,
    exportHost,
    category, setCategory, maxWt, setMaxWt, totVol, setTotVol, revPot, setRevPot,
    avgWt, setAvgWt, codShare, setCodShare, rtoShare, setRtoShare,
    avgInvoiceValue, setAvgInvoiceValue,
    registeredCompany, setRegisteredCompany, enterpriseSize, setEnterpriseSize,
    brandTotalVol, setBrandTotalVol, minWt, setMinWt, currentRvp, setCurrentRvp,
    svcConfig, setSvcConfig, svcVolTotal,
    pickups, addPickup, removePickup, setPickup, pickupTotal, pinCache = {}, showErr = false } = props
  // Service type distribution (d2c-scoping-v8.html) — split "Shipments allocated to
  // Prozo" across the selected services, by absolute count or by %. Writes straight
  // into svcConfig[k].vol, the SAME field Step 3's "Monthly volume — this service"
  // already reads — so this is where that number gets decided, not a parallel copy
  // of it. Mode/pct are UI-only scratch state (not autosaved): the derived vol is
  // already persisted via svcConfig, which is all that's needed to resume correctly.
  const [svcVolMode, setSvcVolMode] = useState('volume') // 'volume' | 'split'
  const [svcSplitPct, setSvcSplitPct] = useState({}) // { [serviceKey]: pct string }
  const setSvcVol = (k, val) => setSvcConfig((prev) => ({ ...prev, [k]: { ...(prev[k] || defaultSvcCfg()), vol: val } }))
  const setSvcSplit = (k, pct) => {
    setSvcSplitPct((prev) => ({ ...prev, [k]: pct }))
    const v = Math.round(((parseFloat(totVol) || 0) * (parseFloat(pct) || 0)) / 100)
    setSvcVol(k, v > 0 ? String(v) : '')
  }
  const svcSplitTotal = services.reduce((s, k) => s + (parseFloat(svcSplitPct[k]) || 0), 0)
  // QA-03 — City/Zone must resolve and pincodes may not repeat across pickup rows.
  const pinCounts = pickups.reduce((m, p) => { const k = (p.pin || '').trim(); if (k) m[k] = (m[k] || 0) + 1; return m }, {})
  const pinIssue = (p) => {
    const v = (p || '').trim()
    if (!v) return null
    if (!/^\d{6}$/.test(v)) return '6-digit PIN'
    if (!(pinCache[v]?.city || cityStateFor(v))) return 'City/zone not found'
    if (pinCounts[v] > 1) return 'Duplicate pincode'
    return null
  }
  const badPin = (p) => showErr && !!pinIssue(p)
  const pickupMismatch = showErr && Math.round(pickupTotal) !== Math.round(parseFloat(totVol) || 0)
  // FB-04 — for an all-reverse commercial the origin is the customer and the warehouse is the
  // DROP point, so this section captures Drop PINs. Forward (and mixed, pending product Q6) keep "Pickup".
  const allReverse = services.length > 0 && services.every((k) => serviceMeta(k).isReverse)
  const locWord = allReverse ? 'Drop' : 'Pickup'
  const badVol = (v) => showErr && (!(parseFloat(v) > 0) || pickupMismatch)

  // City·State + zone auto-filled from the pincode master (#24); falls back to the local heuristic.
  const cityState = (pin) => {
    const info = pinCache[pin]
    if (info?.city) return `${info.city}${info.state ? ` · ${info.state}` : ''}`
    return cityStateFor(pin) || '—'
  }
  const zoneOf = (pin) => pinCache[pin]?.zoneTag || (pin ? zoneForPin(pin) : null)

  const pickupCols = [
    { key: 'i', label: '#', width: 36, render: (r) => pickups.indexOf(r) + 1 },
    // Hard-masked: digits only, max 6 — the box can't accept an invalid pincode (feedback #4).
    { key: 'pin', label: `${locWord} PIN code`, render: (r) => <TextField size="small" value={r.pin} onChange={(e) => setPickup(r.id, 'pin', e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="e.g. 110001" sx={{ width: 130 }} inputProps={{ inputMode: 'numeric', maxLength: 6 }} error={badPin(r.pin)} {...helperProps(badPin(r.pin) ? pinIssue(r.pin) : undefined)} /> },
    // FB-12 Tier-1 — inline serviceability chip from the already-cached pinCache[pin].isServiceable
    // (the GET /masters/pincodes/{pin} resolve). Warning surface only — never blocks entry.
    { key: 'svc', label: 'Serviceable', width: 116, render: (r) => {
      if (!/^\d{6}$/.test(r.pin || '')) return <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>—</Typography>
      const s = pinCache[r.pin]?.isServiceable
      const [txt, bg, fg] = s == null ? ['Unknown', '#EEF0F4', '#64748b'] : s ? ['Serviceable', '#E2F1EA', '#177A53'] : ['Not serviceable', '#FCE8E6', '#A32C21']
      return <Box component="span" sx={{ fontSize: 10.5, fontWeight: 600, px: 1, py: 0.25, borderRadius: '99px', bgcolor: bg, color: fg, whiteSpace: 'nowrap' }}>{txt}</Box>
    } },
    { key: 'city', label: 'City · State (auto)', render: (r) => <Typography sx={{ fontSize: 12, color: badPin(r.pin) && pinIssue(r.pin) !== '6-digit PIN' ? 'error.main' : 'text.secondary' }}>{cityState(r.pin)}</Typography> },
    { key: 'zone', label: 'Zone (auto)', render: (r) => { const z = zoneOf(r.pin); return z ? <Pill label={z} styleKey={z === 'Metro' ? 'pendingL1' : z === 'Special' ? 'orange' : 'neutral'} /> : '—' } },
    { key: 'vol', label: 'Monthly volume', render: (r) => <TextField size="small" value={r.vol} onChange={(e) => setPickup(r.id, 'vol', sanitizeInt(e.target.value))} sx={{ width: 110 }} error={badVol(r.vol)} {...helperProps(badVol(r.vol) ? (parseFloat(r.vol) > 0 ? 'Must add up to the total' : 'Enter volume') : undefined)} /> },
    { key: 'x', label: '', width: 40, render: (r) => <IconButton size="small" onClick={() => removePickup(r.id)}><DeleteOutlineIcon fontSize="small" /></IconButton> },
  ]

  const volBand = (() => { const v = parseFloat(totVol) || 0; return v < 1000 ? 'SME courier-wise card (<1,000/mo)' : v >= 10000 ? 'Enterprise courier-wise card (≥10,000/mo)' : 'between tiers — blended recommended' })()
  const totVolHelp = `Count of shipments — must equal the pickup volumes. Auto card tier: ${volBand}.`
  const revPotBlank = !String(revPot || '').trim()
  const revPotErrMsg = revPotBlank ? 'Monthly revenue potential is required.' : undefined

  // Export every field on this step — filled or blank — so it can be compared later
  // against what the generated proposal actually shows.
  const exportStep1 = () => {
    const aoa = [
      ['Prozo D2C — Prospect Details (Step 1)'],
      ['Field', 'Value'],
      ['Commercial type', COMMERCIAL_TYPES.find((t) => t.key === cardType)?.title || cardType || ''],
      ['Services selected', services.length ? services.map(labelForKey).join(', ') : '(none selected)'],
      ['HubSpot Deal ID', hubspot || ''],
      ['Brand name', brand || ''],
      ['Registered company name', registeredCompany || ''],
      ['Product category', category || ''],
      ['Enterprise size', enterpriseSize || ''],
      ['Total monthly shipments of the brand', brandTotalVol || ''],
      ['Max shipment weight (kg)', maxWt || ''],
      ['Min shipment weight (kg)', minWt || ''],
      ['Total monthly volume (shipments/mo)', totVol || ''],
      ['Monthly revenue potential (₹ lacs)', revPot || ''],
      ['Avg shipment weight (kg)', avgWt || ''],
      ['Average invoice value (₹)', avgInvoiceValue || ''],
      ['COD share (% of shipments)', codShare || ''],
      ['Current RTO (% of shipments)', rtoShare || ''],
      ['Current RVP (% of shipments)', currentRvp || ''],
    ]
    const pickupAoa = [
      ['#', `${locWord} PIN`, 'City · State', 'Zone', 'Monthly volume'],
      ...pickups.map((p, i) => [i + 1, p.pin || '', cityState(p.pin), zoneOf(p.pin) || '', p.vol || '']),
    ]
    downloadWorkbook('D2C_Step1_ProspectDetails.xlsx', [{ name: 'Prospect Details', aoa }, { name: 'Pickup Locations', aoa: pickupAoa }])
  }

  // Subtle uppercase caption that heads each field group in "Brand & deal" — adds scannable
  // structure so the form reads as a few small groups, not one wall of inputs.
  const grpCap = { fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.disabled', mb: 1 }

  useHeaderExport(exportHost, exportStep1, 'Export Step 1 data (.xlsx)')

  return (
    <>
      <SectionCard title={<>1 · Commercial type <NoteInfoIcon body="How the rate card is packaged. Forward auto-includes RTO + COD; reverse (RVP) auto-includes QC. Penalty-clause commercials are built with Pricing directly — not self-serve." /></>} subtitle="How the rate card is packaged — pick one.">
        <ChoiceCards
          value={cardType}
          onChange={onPickCardType}
          minWidth={220}
          options={COMMERCIAL_TYPES.map((t) => ({ value: t.key, title: t.title, desc: t.desc, icon: CT_ICONS[t.key] }))}
        />
        {/* Per-card detail only for the one card with no backend mapping (Add-on). */}
        {!CARD_TYPE_MAP[cardType] && <GapNote id={`card_${cardType}`} />}
        {(cardType === 'sddndd' || cardType === 'hb') && (
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.75 }}>Rate card mode</Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {[
                { key: 'blended', label: 'Blended', desc: 'One rate card across all couriers.' },
                { key: 'cpwise', label: 'Courier-wise', desc: 'Separate rate card per courier.' },
              ].map((v) => (
                <Box key={v.key} onClick={() => setCardVariant(v.key)}
                  sx={{ cursor: 'pointer', border: '1.5px solid', borderColor: cardVariant === v.key ? 'primary.main' : (showErr && !cardVariant ? 'error.main' : 'divider'), bgcolor: cardVariant === v.key ? 'rgba(32,101,209,0.06)' : '#fff', borderRadius: '8px', px: 1.5, py: 1, minWidth: 150 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 12, color: cardVariant === v.key ? 'primary.main' : 'text.primary' }}>{v.label}</Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>{v.desc}</Typography>
                </Box>
              ))}
            </Stack>
            {showErr && !cardVariant && <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.75 }}>Pick Blended or Courier-wise to continue.</Typography>}
          </Box>
        )}
      </SectionCard>

      <SectionCard title={<>2 · Services <NoteInfoIcon body="Live from the serviceability master. Pick every service to price — each maps to a standard commercial." /></>} subtitle="Pick the services to price."
        sx={showErr && services.length === 0 ? { border: '1px solid', borderColor: 'error.main' } : undefined}>
        <ServiceSelectorFunnel services={services} onToggle={toggleService} cardType={cardType} showErr={showErr} />
      </SectionCard>

      <SectionCard title="3 · Brand & deal" subtitle="The prospect's numbers — these feed the pricing engine.">
        <Typography sx={grpCap}>Brand</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" required label="Brand name" value={brand} onChange={(e) => setBrand(e.target.value)} error={showErr && !brand.trim()} {...helperProps(showErr && !brand.trim() ? 'Brand name is required.' : undefined)} /></Grid>
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" required label="Registered company name" value={registeredCompany} onChange={(e) => setRegisteredCompany(e.target.value)} error={showErr && !registeredCompany.trim()} InputProps={infoAdorn('Legal entity name — may differ from the brand/trade name above.')} {...helperProps(showErr && !registeredCompany.trim() ? 'Registered company name is required.' : undefined)} /></Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField select fullWidth size="small" required label="Category" value={category} onChange={(e) => setCategory(e.target.value)}
              error={showErr && !category} {...helperProps(showErr && !category ? 'Product category must be selected.' : undefined)}>
              {['Apparel', 'Footwear', 'FMCG / Food', 'Beauty & Personal Care', 'Ayurvedic Medicines', 'Jewellery', 'Electronics Accessories', 'Luggage / Bags', 'Heavy & Bulky', 'Other'].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" label="HubSpot ID" value={hubspot} onChange={(e) => setHubspot(e.target.value)} placeholder="e.g. HS-93481" InputProps={infoAdorn('From the HubSpot deal URL. Mandatory / optional — set per user by FH.')} /></Grid>
        </Grid>

        <Typography sx={{ ...grpCap, mt: 2 }}>Enterprise size<NoteInfoIcon body="MSME classification, notification S.O. 1364(E), effective 1 April 2025. Investment and turnover must both be within the limit." /></Typography>
        <ChoiceCards
          value={enterpriseSize}
          onChange={setEnterpriseSize}
          minWidth={190}
          options={[
            { value: 'Micro', title: 'Micro', desc: 'turnover up to ₹10 cr' },
            { value: 'Small', title: 'Small', desc: 'turnover ₹10 to ₹100 cr' },
            { value: 'Medium', title: 'Medium', desc: 'turnover ₹100 to ₹500 cr' },
            { value: 'Large', title: 'Large', desc: 'turnover above ₹500 cr' },
          ]}
        />
        {showErr && !enterpriseSize && <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.75, mb: 1 }}>Enterprise size must be selected.</Typography>}

        <Typography sx={{ ...grpCap, mt: 2 }}>Volume &amp; weight</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" required label="Shipments allocated to Prozo" value={totVol} onChange={(e) => setTotVol(sanitizeInt(e.target.value))} error={showErr && !(parseFloat(totVol) > 0)} InputProps={infoAdorn(totVolHelp, '/mo')} {...helperProps(showErr && !(parseFloat(totVol) > 0) ? 'Enter a monthly volume greater than zero.' : undefined)} /></Grid>
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" required label="Brand total volume" value={brandTotalVol} onChange={(e) => setBrandTotalVol(sanitizeInt(e.target.value))} error={showErr && !(parseFloat(brandTotalVol) > 0)} InputProps={infoAdorn('Across ALL logistics partners, not just Prozo — Monthly volume above is the share priced here.', '/mo')} {...helperProps(showErr && !(parseFloat(brandTotalVol) > 0) ? 'Enter the brand’s total monthly shipments, greater than zero.' : undefined)} /></Grid>
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" required label="Min weight" value={minWt} onChange={(e) => setMinWt(sanitizeNum(e.target.value))} error={showErr && !String(minWt || '').trim()} InputProps={infoAdorn('Lightest item shipped.', 'kg')} {...helperProps(showErr && !String(minWt || '').trim() ? 'Min shipment weight is required.' : undefined)} /></Grid>
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" required label="Max weight" value={maxWt} onChange={(e) => setMaxWt(sanitizeNum(e.target.value))} error={showErr && !String(maxWt || '').trim()} InputProps={infoAdorn('Heaviest single shipment. Caps the slab ladder and courier eligibility.', 'kg')} {...helperProps(showErr && !String(maxWt || '').trim() ? 'Max shipment weight is required.' : undefined)} /></Grid>
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" required label="Avg weight" value={avgWt} onChange={(e) => setAvgWt(sanitizeNum(e.target.value))} error={showErr && !String(avgWt || '').trim()} InputProps={infoAdorn('A rough average is fine; slab detail comes in Step 2.', 'kg')} {...helperProps(showErr && !String(avgWt || '').trim() ? 'Avg shipment weight is required.' : undefined)} /></Grid>
        </Grid>

        {services.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography sx={{ ...grpCap, mb: 0, flex: 1 }}>Service type distribution<NoteInfoIcon body="How the shipments allocated to Prozo split across the services picked above — this is what feeds each service's Monthly volume in Step 3." /></Typography>
              <Chip label="Volume" onClick={() => setSvcVolMode('volume')} variant={svcVolMode === 'volume' ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600, fontSize: 12, bgcolor: svcVolMode === 'volume' ? 'primary.main' : 'transparent', color: svcVolMode === 'volume' ? '#fff' : 'text.secondary' }} />
              <Chip label="Split %" onClick={() => setSvcVolMode('split')} variant={svcVolMode === 'split' ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600, fontSize: 12, bgcolor: svcVolMode === 'split' ? 'primary.main' : 'transparent', color: svcVolMode === 'split' ? '#fff' : 'text.secondary' }} />
            </Stack>
            <Grid container spacing={1.5}>
              {services.map((k) => (
                <Grid item xs={12} sm={6} md={4} key={k}>
                  {svcVolMode === 'volume' ? (
                    <TextField fullWidth size="small" label={labelForKey(k)} value={svcConfig[k]?.vol || ''} onChange={(e) => setSvcVol(k, sanitizeInt(e.target.value))} InputProps={infoAdorn('Shipments/month for this service.', '/mo')}
                      error={showErr && !(parseFloat(svcConfig[k]?.vol) > 0)} {...helperProps(showErr && !(parseFloat(svcConfig[k]?.vol) > 0) ? 'Required — enter this service’s share.' : undefined)} />
                  ) : (
                    <TextField fullWidth size="small" label={labelForKey(k)} value={svcSplitPct[k] || ''} onChange={(e) => setSvcSplit(k, sanitizePct(e.target.value))} InputProps={infoAdorn('% of the total allocated to Prozo.', '%')}
                      error={showErr && !(parseFloat(svcConfig[k]?.vol) > 0)} {...helperProps(showErr && !(parseFloat(svcConfig[k]?.vol) > 0) ? 'Required — enter this service’s share.' : undefined)} />
                  )}
                </Grid>
              ))}
            </Grid>
            <Typography sx={{ fontSize: 12, fontWeight: 600, mt: 1, color: svcVolMode === 'volume'
              ? (svcVolTotal === (parseFloat(totVol) || 0) ? 'success.main' : 'error.main')
              : (Math.abs(svcSplitTotal - 100) < 0.1 ? 'success.main' : 'error.main') }}>
              {svcVolMode === 'volume'
                ? `Total: ${svcVolTotal} (must equal ${Math.round(parseFloat(totVol) || 0)} to Prozo)`
                : `Total: ${Math.round(svcSplitTotal * 10) / 10}% (must equal 100%)`}
            </Typography>
          </Box>
        )}

        <Typography sx={{ ...grpCap, mt: 2 }}>Economics &amp; returns</Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" required label="Revenue potential (₹ lacs)" value={revPot} onChange={(e) => setRevPot(sanitizeNum(e.target.value))} error={showErr && revPotBlank} InputProps={infoAdorn('Estimated monthly billing to the client, in ₹ lakhs.')} {...helperProps(showErr && revPotErrMsg ? revPotErrMsg : undefined)} /></Grid>
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" required label="Average invoice value (₹)" value={avgInvoiceValue} onChange={(e) => setAvgInvoiceValue(sanitizeNum(e.target.value))} error={showErr && !(parseFloat(avgInvoiceValue) > 0)} InputProps={infoAdorn('Used for COD % charges and the COD rate floors/ceilings.')} {...helperProps(showErr && !(parseFloat(avgInvoiceValue) > 0) ? 'Required — must be greater than zero.' : undefined)} /></Grid>
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" required label="COD share" value={codShare} onChange={(e) => setCodShare(sanitizePct(e.target.value))} InputProps={infoAdorn('Orders paid cash-on-delivery.', '%')} error={showErr && (!String(codShare || '').trim() || !(parseFloat(codShare) >= 0) || parseFloat(codShare) > 100)} {...helperProps(showErr && (!String(codShare || '').trim() || !(parseFloat(codShare) >= 0) || parseFloat(codShare) > 100) ? 'Required — 0% to 100%.' : undefined)} /></Grid>
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" required label="Current RTO" value={rtoShare} onChange={(e) => setRtoShare(sanitizePct(e.target.value))} InputProps={infoAdorn('Total orders returned undelivered (of all forward).', '%')} error={showErr && (!String(rtoShare || '').trim() || !(parseFloat(rtoShare) >= 0) || parseFloat(rtoShare) > 100)} {...helperProps(showErr && (!String(rtoShare || '').trim() || !(parseFloat(rtoShare) >= 0) || parseFloat(rtoShare) > 100) ? 'Required — 0% to 100%.' : undefined)} /></Grid>
          <Grid item xs={12} sm={6} md={4}><TextField fullWidth size="small" required label="Current RVP" value={currentRvp} onChange={(e) => setCurrentRvp(sanitizePct(e.target.value))} InputProps={infoAdorn('% of forward shipments picked back from the customer today — not the RVP pricing formula in Step 3.', '%')} error={showErr && (!String(currentRvp || '').trim() || !(parseFloat(currentRvp) >= 0) || parseFloat(currentRvp) > 100)} {...helperProps(showErr && (!String(currentRvp || '').trim() || !(parseFloat(currentRvp) >= 0) || parseFloat(currentRvp) > 100) ? 'Required — 0% to 100%.' : undefined)} /></Grid>
        </Grid>
      </SectionCard>

      <SectionCard title={`4 · ${locWord} locations`} subtitle={allReverse ? 'Add every return warehouse. Zone mapping starts at the drop PIN. Drop volumes must add up to the total monthly volume.' : 'Add every warehouse. Zone mapping starts at the pickup PIN. Pickup volumes must add up to the total monthly volume.'}>
        <DataTable columns={pickupCols} rows={pickups} getRowKey={(r) => r.id} />
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={btn} onClick={addPickup}>Add {locWord.toLowerCase()} location</Button>
          <Pill label={`${locWord} total: ${pickupTotal} / ${totVol || 0}`} styleKey={Math.round(pickupTotal) === Math.round(parseFloat(totVol) || 0) ? 'approved' : 'returned'} />
        </Stack>
        {pickupMismatch && <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5 }}>{locWord} volumes must add up to the total monthly volume.</Typography>}
      </SectionCard>
    </>
  )
}

const labelForKey = (k) => SERVICES.find((s) => s.key === k)?.label || serviceMeta(k).label

// SDD/NDD only serves nearby lanes — ROI and Special zones don't apply to it, so its
// per-service RFI zone split drops those two. All other services keep the full zone set.
const SDD_ZONES = RFI_ZONES.filter((z) => z !== 'ROI' && z !== 'Special')
const zonesForService = (k) => (serviceMeta(k).isSdd ? SDD_ZONES : RFI_ZONES)

/* Reusable zone-% + weight-% pair of editors (BUG-07: also rendered once per service). */
// Small "%" suffix so every percentage input reads unambiguously as a percentage.
const pctAdornment = { endAdornment: <InputAdornment position="end" disableTypography sx={{ fontSize: 12, color: 'text.secondary' }}>%</InputAdornment> }
// QA FB-15 — numeric sanitizers block negatives, letters and special chars at entry (on type
// AND paste, since React onChange fires for both). oneDot keeps a single decimal point;
// sanitizePct also caps at 100; sanitizeNum = positive decimal; sanitizeInt = positive integer.
const oneDot = (v) => { let s = String(v ?? '').replace(/[^0-9.]/g, ''); const i = s.indexOf('.'); if (i >= 0) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, ''); return s }
const sanitizePct = (v) => { const s = oneDot(v); return Number(s) > 100 ? '100' : s }
const sanitizeNum = (v) => oneDot(v)
const sanitizeInt = (v) => String(v ?? '').replace(/\D/g, '')

// Row indices involved in a weight-band overlap or invalid range (to <= from) — same
// rule as bandOverlapError below, but tells us WHICH rows to flag instead of a message.
function overlapIndices(bands) {
  const parsed = (bands || []).map((b, i) => ({ i, from: parseFloat(b.from), to: parseFloat(b.to) }))
  const bad = new Set()
  parsed.forEach((b) => { if (!Number.isNaN(b.from) && !Number.isNaN(b.to) && b.to <= b.from) bad.add(b.i) })
  const valid = parsed.filter((b) => !Number.isNaN(b.from) && !Number.isNaN(b.to) && b.to > b.from)
  const sorted = [...valid].sort((a, b) => a.from - b.from)
  for (let k = 1; k < sorted.length; k += 1) {
    if (sorted[k].from < sorted[k - 1].to) { bad.add(sorted[k].i); bad.add(sorted[k - 1].i) }
  }
  return bad
}

// Weight-slab structure builder — pick a slab size + count per band instead of typing
// weight ranges (gaps/overlaps become impossible), capped against the deal's Max weight
// (Step 1). Lives in Step 2 now (was Step 3's per-service builder); the structure it
// produces carries "as is" into Step 3's rate-card grid — Step 3 never edits it, only
// fills in ₹ rates against the rows it defines. Arbitrary sizes are fine here — the
// wire only ever snaps boundaries to the 6 backend slabs (bandToSlab) when the actual
// RFI/rate-card payload is built, not in this UI.
// Range-tag palette — one colour per band, cycling. Purely cosmetic (matches a band here
// to its rows in Step 3's compact rate grid at a glance); carries no meaning beyond identity.
const BAND_TAG_COLORS = [
  { fg: '#0E1626', bg: '#EEF0F4' },
  { fg: '#3A5BD9', bg: '#EEF2FE' },
  { fg: '#0A7F58', bg: '#EBF7F2' },
  { fg: '#6636C9', bg: '#F3EEFC' },
  { fg: '#A85B08', bg: '#FDF5E9' },
  { fg: '#BC2F46', bg: '#FCEFF1' },
]

function TierBuilder({ tiers, setTiers, maxWt, largeParcel = false }) {
  // Large-parcel services (…_LARGE, 10–50 kg): slabs run in 5/10 kg strides — the
  // first slab is 5 kg, per the 14-Aug review ("service type is large only → the
  // starting weight slab should start with 5 kg").
  // Regular parcels (14-Aug review, clarified): the first slab is FIXED at 0.5 kg
  // (no 1 kg option), and every later band offers 0.5 kg alongside 1/2/3.
  const BASE_SIZES = largeParcel ? [5, 10] : [0.5]
  const TIER_SIZES = largeParcel ? [5, 10] : [0.5, 1, 2, 3]
  const MAX_BANDS = 4
  const MAX_SLAB_COUNT = 20
  const MIN_TIER_SIZE = Math.min(...TIER_SIZES)
  const fmtKg = (n) => String(Math.round(n * 100) / 100)
  const bandWeight = (t) => (Number(t.size) || 0) * Math.max(0, Math.floor(Number(t.count) || 0))
  const structMax = tiers.reduce((s, t) => s + bandWeight(t), 0)
  const maxWtNum = Number(maxWt) || 0
  const hasMaxCap = maxWtNum > 0
  const remainingCap = hasMaxCap ? Math.round((maxWtNum - structMax) * 100) / 100 : Infinity
  // Every band stays editable (no "locked" bands) — changing one shifts every range
  // after it, which would silently orphan rates already typed against the old ranges
  // (Step 3 clears them on any real change here, see setSharedTiers/setServiceTiers).
  const setBandSize = (i, size) => { if (Number(tiers[i]?.size) !== size) setTiers(tiers.map((t, idx) => (idx === i ? { ...t, size } : t))) }
  const setBandCount = (i, count) => { if (Number(tiers[i]?.count) !== count) setTiers(tiers.map((t, idx) => (idx === i ? { ...t, count } : t))) }
  // fresh=true (default for every band but the first): this band's rate is a brand-new
  // number quoted directly, not built up from the band before it. Feeds the compact rate
  // grid (Step 3) — purely a rate-entry hint, doesn't change the structure itself.
  // Matches the display rule below (t.fresh !== false) — a band with no explicit `fresh`
  // key yet (any band from before this feature shipped) reads as fresh:true, so the guard
  // must use the same normalization or clicking "Continues" on such a band is a no-op
  // (Boolean(undefined) is already false, same as the target value, so the naive guard
  // thinks nothing changed).
  const setBandFresh = (i, fresh) => { if ((tiers[i]?.fresh !== false) !== fresh) setTiers(tiers.map((t, idx) => (idx === i ? { ...t, fresh } : t))) }
  const addBand = () => {
    const size = TIER_SIZES.find((sz) => sz <= remainingCap) || TIER_SIZES[0]
    const count = Number.isFinite(remainingCap) ? Math.max(1, Math.min(MAX_SLAB_COUNT, Math.floor(remainingCap / size))) : 5
    setTiers([...tiers, { size, count, fresh: true }])
  }
  const removeLastBand = () => setTiers(tiers.slice(0, -1))
  // Older drafts can carry a 1 kg first slab from before the 0.5-fixed rule —
  // normalize the stored value so the wire always matches the "(fixed)" label.
  useEffect(() => {
    if (!largeParcel && tiers.length && Number(tiers[0]?.size) !== 0.5) setBandSize(0, 0.5)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [largeParcel, tiers])
  return (
    <Box sx={{ mb: 1.5 }}>
      {hasMaxCap && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: '9px', px: 1.5, py: 1, mb: 1.5 }}>
          Max weight: <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{fmtKg(maxWtNum)} kg</Box> (Step 1)
        </Typography>
      )}
      {/* A real CSS Grid (shared column tracks) — not flexbox — so every row's columns line up
          exactly regardless of that row's own content (2 vs 3 slab-size buttons, the delete
          icon only existing on the last row, etc). minmax(0, …) lets tracks shrink together so
          the table never needs to scroll; whiteSpace:'nowrap' keeps every value intact. */}
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', overflow: 'hidden', mb: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '112px minmax(0,0.85fr) 104px minmax(0,1.5fr) 30px', alignItems: 'center', columnGap: 1.5, px: 1.5, py: 0.85, bgcolor: '#F7F9FB', borderBottom: '1px solid', borderColor: 'divider' }}>
          {['Range', 'Slab size', 'How many', 'Rate is', ''].map((h) => (
            <Typography key={h} sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap' }}>{h}</Typography>
          ))}
        </Box>
        {tiers.map((t, i) => {
          const isBase = i === 0
          const isLast = i === tiers.length - 1
          const sizeOptions = isBase ? BASE_SIZES : TIER_SIZES
          const from = fmtKg(tiers.slice(0, i).reduce((s, o) => s + bandWeight(o), 0))
          const size = Number(t.size) || sizeOptions[0]
          // The first band is always exactly one slab — "how many" only makes sense once
          // there's a running total to add more of the same slab onto.
          const count = isBase ? 1 : Math.max(1, Math.floor(Number(t.count) || 1))
          const to = fmtKg(Number(from) + size * count)
          const tag = BAND_TAG_COLORS[i % BAND_TAG_COLORS.length]
          const fresh = t.fresh !== false
          // The weight cap is a shared total across every band, not just the last —
          // incrementing ANY band's count adds `size` kg to that same total, so the
          // same headroom formula applies uniformly regardless of position.
          const capCount = Number.isFinite(remainingCap) ? count + Math.max(0, Math.floor((remainingCap + 1e-9) / size)) : MAX_SLAB_COUNT
          const maxAllowed = Math.min(MAX_SLAB_COUNT, capCount)
          return (
            <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '112px minmax(0,0.85fr) 104px minmax(0,1.5fr) 30px', alignItems: 'center', columnGap: 1.5, px: 1.5, py: 1, bgcolor: i % 2 ? '#FAFBFC' : 'transparent', borderTop: i === 0 ? 'none' : '1px solid', borderColor: 'divider' }}>
              <Box component="span" sx={{
                display: 'inline-flex', fontFamily: 'monospace', fontSize: 12, fontWeight: 700, borderRadius: '6px',
                px: 1.1, py: 0.5, whiteSpace: 'nowrap', width: 'fit-content', color: tag.fg, bgcolor: tag.bg,
              }}>{from}–{to} kg</Box>
              {sizeOptions.length === 1 ? (
                <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary' }}>{sizeOptions[0]} kg (fixed)</Typography>
              ) : (
                <TextField select size="small" value={sizeOptions.includes(size) ? size : sizeOptions[0]} onChange={(e) => setBandSize(i, Number(e.target.value))} sx={{ width: 96, '& .MuiOutlinedInput-input': { fontSize: 12.5, fontWeight: 600, py: 0.7 } }}>
                  {sizeOptions.map((sz) => <MenuItem key={sz} value={sz} sx={{ fontSize: 12.5 }}>{sz} kg</MenuItem>)}
                </TextField>
              )}
              {isBase ? (
                <Typography sx={{ fontSize: 12.5, color: 'text.disabled' }}>1 (fixed)</Typography>
              ) : (
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <IconButton size="small" aria-label="Decrease slab count" disabled={count <= 1} onClick={() => setBandCount(i, count - 1)} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '7px' }}>−</IconButton>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, minWidth: 18, textAlign: 'center' }}>{count}</Typography>
                  <IconButton size="small" aria-label="Increase slab count" disabled={count >= maxAllowed} onClick={() => setBandCount(i, count + 1)} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '7px' }}>+</IconButton>
                </Stack>
              )}
              {!isBase ? (
                <Box sx={{ display: 'inline-flex', border: '1.5px solid', borderColor: '#D7DEE9', borderRadius: '999px', p: '2px', width: 'fit-content' }}>
                  <Button size="small" onClick={() => setBandFresh(i, false)} sx={{
                    textTransform: 'none', fontSize: 11.5, fontWeight: 700, minWidth: 0, px: 1.3, py: 0.35, borderRadius: '999px',
                    bgcolor: !fresh ? 'primary.main' : 'transparent', color: !fresh ? '#fff' : 'text.secondary',
                  }}>Continues</Button>
                  <Button size="small" onClick={() => setBandFresh(i, true)} sx={{
                    textTransform: 'none', fontSize: 11.5, fontWeight: 700, minWidth: 0, px: 1.3, py: 0.35, borderRadius: '999px',
                    bgcolor: fresh ? 'primary.main' : 'transparent', color: fresh ? '#fff' : 'text.secondary',
                  }}>Fresh base</Button>
                </Box>
              ) : <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>First slab</Typography>}
              {isLast && !isBase
                ? <IconButton size="small" aria-label="Remove this band" onClick={removeLastBand}><DeleteOutlineIcon fontSize="small" /></IconButton>
                : <Box />}
            </Box>
          )
        })}
      </Box>
      {tiers.length < MAX_BANDS && (
        <Button fullWidth startIcon={<AddIcon />} disabled={hasMaxCap && remainingCap < MIN_TIER_SIZE} onClick={addBand} sx={{
          mt: 1, textTransform: 'none', fontSize: 13, fontWeight: 700, borderRadius: '10px', py: 0.9,
          color: 'primary.main', border: '1.5px dashed', borderColor: 'primary.main', bgcolor: '#F3F7FE',
          '&:hover': { bgcolor: '#E7F0FD', borderColor: 'primary.dark' },
          '&.Mui-disabled': { color: 'text.disabled', borderColor: 'divider', bgcolor: 'transparent' },
        }}>Add next weight band</Button>
      )}
      {hasMaxCap && (() => {
        // Surface the structure/max-weight state as a clear coloured alert, not grey text:
        // red = over the cap (must fix), green = exactly full, amber = maxed/too little, blue = headroom left.
        const over = remainingCap < 0
        const full = remainingCap === 0
        const maxed = !over && !full && tiers.length >= MAX_BANDS
        const tooLittle = !over && !full && !maxed && remainingCap < MIN_TIER_SIZE
        const tone = over ? { bg: '#FDECEA', bd: '#F3C6C0', fg: '#B42318', Icon: ErrorOutlineIcon }
          : full ? { bg: '#E7F6EF', bd: '#B7E3CD', fg: '#177A53', Icon: CheckCircleOutlineIcon }
          : (maxed || tooLittle) ? { bg: '#FBF1E2', bd: '#F0D6A8', fg: '#B4650A', Icon: WarningAmberIcon }
          : { bg: '#EAF1FC', bd: '#CFE0F7', fg: '#1E4C8A', Icon: InfoOutlinedIcon }
        const StatusIcon = tone.Icon
        const msg = over ? `Structure is over the ${fmtKg(maxWtNum)} kg max weight by ${fmtKg(-remainingCap)} kg — reduce a band.`
          : full ? `Structure covers the full ${fmtKg(maxWtNum)} kg max weight.`
          : maxed ? `Maximum of ${MAX_BANDS} bands reached.`
          : tooLittle ? `Only ${fmtKg(remainingCap)} kg of headroom left — too little for another slab.`
          : `${fmtKg(remainingCap)} kg of headroom left before the max weight.`
        return (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.9, borderRadius: '8px', bgcolor: tone.bg, border: `1px solid ${tone.bd}` }}>
            <StatusIcon sx={{ fontSize: 18, color: tone.fg, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tone.fg }}>{msg}</Typography>
          </Box>
        )
      })()}
    </Box>
  )
}

function SplitEditor({ zones, wts, onZones, onWts, tiers, onTiers, maxWt, showErr = false, largeParcel = false, metroBlocked = false }) {
  const zTotal = Math.round(zones.reduce((s, z) => s + (parseFloat(z.pct) || 0), 0))
  const wTotal = Math.round(wts.reduce((s, w) => s + (parseFloat(w.pct) || 0), 0))
  const setZone = (i, v) => onZones(zones.map((z, idx) => (idx === i ? { ...z, pct: v } : z)))
  const setWt = (i, v) => onWts(wts.map((w, idx) => (idx === i ? { ...w, pct: v } : w)))
  // Structure and % Share live behind their own sub-tabs (only one shown at a time), so the
  // Weight-wise column stays roughly the same height as Zone-wise and the two sit side by
  // side without forcing a long scroll to enter both splits.
  const [wtTab, setWtTab] = useState('structure')
  // After a failed Continue, an incomplete split turns its whole panel boundary red —
  // the per-field error outlines alone are easy to miss (especially behind the
  // Structure sub-tab), so the panel itself flags which split is the problem.
  const panelSx = (error) => ({
    border: '1.5px dashed', borderColor: error ? '#D92D20' : '#C6CFDC', borderRadius: '12px', p: 2, height: '100%',
    ...(error ? { bgcolor: '#FFFBFA' } : {}),
  })
  const zoneErr = showErr && zTotal !== 100
  const wtErr = showErr && wTotal !== 100
  const totalPill = (total) => <Pill label={total === 100 ? '100% ✓' : `${total}% / 100%`} styleKey={total === 100 ? 'approved' : 'returned'} />
  // Section header — a small dot + bold coloured title, no chrome (deliberately lighter
  // than the bold per-service banner, so the two don't read as the same kind of thing).
  const dotHead = (label) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85 }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
      <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'primary.main', display: 'flex', alignItems: 'center' }}>{label}</Typography>
    </Box>
  )
  const subTab = (key, label, done, error) => {
    const on = wtTab === key
    return (
      <Button
        size="small" disableRipple disableTouchRipple onClick={() => setWtTab(key)}
        sx={{
          textTransform: 'none', fontSize: 12.5, fontWeight: 700, borderRadius: '8px', px: 1.5, py: 0.5, minWidth: 0,
          bgcolor: on ? 'primary.main' : (error ? '#FDECEA' : 'transparent'),
          color: on ? '#fff' : (error ? 'error.main' : 'text.secondary'),
          border: !on && error ? '1px solid #F3C6C0' : '1px solid transparent',
          // MUI's default hover/active overlay washes out a solid background mid-press —
          // pin every interaction state to the same colour explicitly instead.
          '&:hover': { bgcolor: on ? 'primary.main' : (error ? '#FDECEA' : 'rgba(32,101,209,0.08)') },
          '&:active': { bgcolor: on ? 'primary.main' : (error ? '#FDECEA' : 'rgba(32,101,209,0.08)') },
          '&.Mui-focusVisible': { bgcolor: on ? 'primary.main' : (error ? '#FDECEA' : 'rgba(32,101,209,0.08)') },
        }}
      >
        {error ? <ErrorOutlineIcon sx={{ fontSize: 14, mr: 0.4 }} /> : (done && <CheckIcon sx={{ fontSize: 14, mr: 0.4 }} />)}
        {label}
      </Button>
    )
  }
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Box sx={panelSx(zoneErr)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
            {dotHead('Zone-wise split')}
            <Box sx={{ flex: 1 }} />
            {totalPill(zTotal)}
          </Box>
          <DataTable
            bare
            minWidth={0}
            columns={[
              { key: 'zone', label: 'Zone', render: (r) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>{r.zone}</span>
                  {metroBlocked && isMetroZone(r.zone) && <NoteInfoIcon body="No pickup location is in a metro city, so no shipment can be a Metro-to-Metro lane. Add a metro pickup PIN on Step 1 to price this zone." />}
                </Box>
              ) },
              { key: 'pct', label: '% share', align: 'right', render: (r) => {
                const empty = !String(r.pct ?? '').trim()
                // Metro gate: when it's blocked AND still empty there is nothing to fix, so the
                // field is locked outright. When a % is already sitting there the field stays
                // EDITABLE and just turns red — disabling it would trap the user with a value
                // they can no longer clear, which is the rework this fix exists to remove.
                const metroLock = metroBlocked && isMetroZone(r.zone)
                const metroHasPct = metroLock && (parseFloat(r.pct) || 0) > 0
                const metroMsg = metroHasPct ? 'No metro pickup — clear this or add a metro pickup PIN' : (metroLock ? 'No metro pickup location' : undefined)
                return <TextField size="small" value={r.pct} disabled={metroLock && !metroHasPct}
                  onChange={(e) => setZone(zones.indexOf(r), sanitizePct(e.target.value))} sx={{ width: 92 }}
                  inputProps={{ style: { textAlign: 'right' } }} InputProps={pctAdornment}
                  error={metroHasPct || (showErr && (empty || zTotal !== 100))}
                  {...helperProps(metroMsg || (showErr && empty ? 'Required' : undefined))} />
              } },
            ]}
            rows={zones} getRowKey={(r) => r.zone}
          />
          {showErr && zTotal !== 100 && <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5 }}>Zone-wise split must total 100%.</Typography>}
          {/* States the reason once for the panel — the per-field helper text alone doesn't say
              WHY the row is locked, which is what sent users back to Step 1 guessing. */}
          {metroBlocked && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'flex-start', gap: 0.85, px: 1.25, py: 0.9, borderRadius: '8px', bgcolor: '#FFF7ED', border: '1px solid #FCD9A8' }}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: '#B54708', flexShrink: 0, mt: '1px' }} />
              <Typography sx={{ fontSize: 11.5, color: '#B54708', lineHeight: 1.45 }}>
                <b>Metro is unavailable.</b> No pickup location is in a metro city, so nothing ships Metro&nbsp;to&nbsp;Metro. Add a metro pickup PIN on Step&nbsp;1 to price this zone.
              </Typography>
            </Box>
          )}
          {/* Same colour-coded completion banner TierBuilder uses on the right — fills the
              space below a short list with a real status, not filler copy. */}
          {(() => {
            const over = zTotal > 100
            const done = zTotal === 100
            const tone = over ? { bg: '#FDECEA', bd: '#F3C6C0', fg: '#B42318', Icon: ErrorOutlineIcon }
              : done ? { bg: '#E7F6EF', bd: '#B7E3CD', fg: '#177A53', Icon: CheckCircleOutlineIcon }
              : { bg: '#EAF1FC', bd: '#CFE0F7', fg: '#1E4C8A', Icon: InfoOutlinedIcon }
            const StatusIcon = tone.Icon
            const msg = over ? `Over 100% by ${zTotal - 100}%.` : done ? 'Zone split complete.' : `${100 - zTotal}% left to reach 100%.`
            return (
              <Box sx={{ mt: 1.75, display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.9, borderRadius: '8px', bgcolor: tone.bg, border: `1px solid ${tone.bd}` }}>
                <StatusIcon sx={{ fontSize: 18, color: tone.fg, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tone.fg }}>{msg}</Typography>
              </Box>
            )
          })()}
        </Box>
      </Grid>
      <Grid item xs={12} md={8}>
        <Box sx={panelSx(wtErr)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
            {dotHead(<>Weight-wise split<NoteInfoIcon body="Build the slab structure below, then give each individual slab its % share. The same slabs carry into Step 3's rate card as-is." /></>)}
            <Box sx={{ flex: 1 }} />
            {totalPill(wTotal)}
          </Box>
          <Box sx={{ display: 'inline-flex', bgcolor: '#F1F4F9', border: '1px solid', borderColor: 'divider', borderRadius: '10px', p: 0.4, gap: 0.4, mb: 1.5 }}>
            {subTab('structure', 'Structure', false)}
            {subTab('share', '% Share', wTotal === 100)}
          </Box>
          {wtTab === 'structure' ? (
            <TierBuilder tiers={tiers} setTiers={onTiers} maxWt={maxWt} largeParcel={largeParcel} />
          ) : (
            <>
              <DataTable
                bare
                minWidth={0}
                columns={[
                  { key: 'slab', label: 'Slab', render: (r) => <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{r.from}–{r.to} kg</Typography> },
                  { key: 'pct', label: '% share', align: 'right', render: (r) => { const empty = !String(r.pct ?? '').trim(); return <TextField size="small" value={r.pct} onChange={(e) => setWt(wts.indexOf(r), sanitizePct(e.target.value))} sx={{ width: 92 }} inputProps={{ style: { textAlign: 'right' } }} InputProps={pctAdornment} error={showErr && (empty || wTotal !== 100)} {...helperProps(showErr && empty ? 'Required' : undefined)} /> } },
                ]}
                // Index key — a value-derived key remounts the row per keystroke (focus loss).
                rows={wts} getRowKey={(r, i) => i}
              />
              {showErr && wTotal !== 100 && <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5 }}>Weight-wise split must total 100%.</Typography>}
            </>
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

/* ───────────────────────── Step 2 ───────────────────────── */
function Step2(props) {
  const { enqueueSnackbar } = useWizardSnackbar()
  const { zoneSplit, setZoneSplit, wtSplit, setWtSplit, wtTiers, setWtTiers, matrixPreview, maxWt,
    rfiMode, setRfiMode, repAll, setRepAll, activePk, setActivePk, pickups, metroBlocked = false,
    matrixRows, setMatrixRows, matrixTotal, newMatrixRow,
    services = [], perSvcSplit = false, togglePerSvcSplit, svcSplits = {}, setSvcSplits,
    setSvcConfig, showErr = false, exportHost } = props

  // Merge helper: re-derive slab rows from a NEW tiers structure, keeping any % already
  // entered for a slab whose "to" boundary still exists (so resizing/adding a band later
  // doesn't wipe %s that are still valid). One row per individual slab — no "band" concept.
  const mergeSlabPct = (nextTiers, existingWts) => {
    const byTo = Object.fromEntries((existingWts || []).map((w) => [String(w.to), w.pct]))
    return tierSlabRanges(nextTiers).map((s) => ({ from: String(s.from), to: String(s.to), pct: byTo[String(s.to)] || '' }))
  }
  // Structure changed — Step 3's rates are keyed to the OLD row labels (rateRowLabelsFromTiers),
  // so any rate already typed against them is now stale; clear it rather than let it silently
  // point at the wrong slab.
  const clearRates = (keys) => setSvcConfig((prev) => {
    const next = { ...prev }
    keys.forEach((k) => { if (next[k]) next[k] = { ...next[k], rates: {}, compactRates: { first: {}, firstEach: {}, bands: {} }, courierCompactRates: {} } })
    return next
  })
  const setSharedTiers = (nextTiers) => {
    setWtTiers(nextTiers)
    setWtSplit(mergeSlabPct(nextTiers, wtSplit))
    clearRates(services)
  }
  // Large-parcel-only deals (every service …_LARGE): slabs run in 5/10 kg strides,
  // so a structure still sitting on the parcel default (0.5/1 kg first slab) is
  // re-seeded to a 5 kg ladder once — sized to Step 1's max weight when set.
  const largeOnly = services.length > 0 && services.every((k) => serviceMeta(k).isLarge)
  useEffect(() => {
    if (!largeOnly || !wtTiers.length || Number(wtTiers[0]?.size) >= 5) return
    const cap = Number(maxWt) || 0
    const extra = cap > 5 ? Math.max(1, Math.min(20, Math.round((cap - 5) / 5))) : 4
    setSharedTiers([{ size: 5, count: 1 }, { size: 5, count: extra, fresh: true }])
  }, [largeOnly]) // eslint-disable-line react-hooks/exhaustive-deps
  const setServiceTiers = (k, nextTiers) => {
    setSvcSplits((prev) => {
      const base = prev[k] || { zones: zonesForService(k).map((zn) => ({ zone: zn, pct: '' })), wts: wtSplit, tiers: wtTiers }
      return { ...prev, [k]: { ...base, tiers: nextTiers, wts: mergeSlabPct(nextTiers, base.wts) } }
    })
    clearRates([k])
  }

  const matrixCols = [{ key: 'band', label: 'Weight slab' }, ...RFI_ZONES.map((z) => ({ key: z, label: z, align: 'right' }))]

  // ── Per-pickup full % matrix ────────────────────────────────────────────────
  const pk = repAll ? 0 : activePk
  const rows = matrixRows(pk)
  const setCell = (ri, zi, v) => setMatrixRows(pk, rows.map((r, idx) => idx === ri ? { ...r, counts: r.counts.map((c, ci) => ci === zi ? v : c) } : r))
  const setBound = (ri, field, v) => setMatrixRows(pk, rows.map((r, idx) => idx === ri ? { ...r, [field]: v } : r))
  const addRow = () => setMatrixRows(pk, [...rows, newMatrixRow(rows[rows.length - 1]?.to || 0, (Number(rows[rows.length - 1]?.to) || 0) + 0.5)])
  const removeRow = (ri) => setMatrixRows(pk, rows.filter((_, idx) => idx !== ri))
  const mTotal = matrixTotal(rows)

  // Export every field on this step — filled or blank — mode-aware (whichever of the 4
  // RFI input modes is active), so it can be compared later against the generated proposal.
  const sheetNameFor2 = (lbl) => String(lbl).replace(/[\\/?*[\]:]/g, '-').slice(0, 31)
  const exportStep2 = () => {
    const sheets = [{ name: 'Mode', aoa: [['RFI input mode', { pct: 'Quick % split', matrix: 'Full % matrix', copy: 'Copy from a previous prospect', file: 'Upload file' }[rfiMode] || rfiMode], ['Split differs by service type', perSvcSplit ? 'Yes' : 'No']] }]
    if (rfiMode === 'pct') {
      if (perSvcSplit && services.length) {
        services.forEach((k) => {
          const sp = svcSplits[k] || { zones: zoneSplit, wts: wtSplit }
          sheets.push({ name: sheetNameFor2(`${labelForKey(k)} split`), aoa: [
            ['Zone-wise split (% of shipments)'], ['Zone', '% share'], ...sp.zones.map((z) => [z.zone, z.pct || '']), [],
            ['Weight-wise split (% of shipments)'], ['From (kg)', 'To (kg)', '% share'], ...sp.wts.map((w) => [w.from, w.to, w.pct || '']),
          ] })
        })
      } else {
        sheets.push({ name: 'Zone split', aoa: [['Zone', '% share'], ...zoneSplit.map((z) => [z.zone, z.pct || ''])] })
        sheets.push({ name: 'Weight split', aoa: [['From (kg)', 'To (kg)', '% share'], ...wtSplit.map((w) => [w.from, w.to, w.pct || ''])] })
      }
    } else if (rfiMode === 'matrix') {
      const n = repAll ? 1 : Math.max(pickups.length, 1)
      for (let i = 0; i < n; i++) {
        const r = matrixRows(i)
        sheets.push({ name: repAll ? 'All pickups' : sheetNameFor2(`Pickup ${pickups[i]?.pin || i + 1}`), aoa: [['From (kg)', 'To (kg)', ...RFI_ZONES], ...r.map((row) => [row.from, row.to, ...row.counts])] })
      }
    } else {
      sheets.push({ name: 'Note', aoa: [[`This step was left in "${rfiMode === 'file' ? 'Upload file' : 'Copy from a previous prospect'}" mode — switch to Quick % split or Full matrix to capture values directly.`]] })
    }
    downloadWorkbook('D2C_Step2_RFIDetails.xlsx', sheets)
  }

  // Shared mode tab strip (mockup's 4 RFI input modes) + the step export, so every
  // mode's returned JSX gets both without repeating the row.
  useHeaderExport(exportHost, exportStep2, 'Export Step 2 data (.xlsx)')

  const modeTabs = (
    <Box sx={{ mb: 1.5 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 0.85 }}>How do you want to enter the split?</Typography>
      <SelectCards
        value={rfiMode}
        onChange={setRfiMode}
        options={[
          { value: 'pct', title: 'Quick % split', desc: 'Two simple splits — we cross-multiply them', icon: <PercentIcon /> },
          { value: 'matrix', title: 'Full % matrix', desc: 'Type every weight × zone cell yourself', icon: <GridOnIcon /> },
          { value: 'copy', title: 'Copy a prospect', desc: 'Reuse an earlier proposal’s split', icon: <ContentCopyIcon /> },
          { value: 'file', title: 'Upload a file', desc: 'Fill a template and import', icon: <UploadFileIcon /> },
        ]}
      />
    </Box>
  )

  // Copy from a previous prospect — real: the SL's own prior proposals (listProposals),
  // with that proposal's RFI split (version.rfiEntries) loaded into the Quick % split.
  if (rfiMode === 'copy') {
    return <CopyFromProspect {...{ setZoneSplit, setWtSplit, setRfiMode, enqueueSnackbar, modeTabs }} />
  }

  // Upload file — real client-side template + parse (no backend needed): the filled
  // file's % rows are aggregated into the zone and weight marginals (Quick % split).
  if (rfiMode === 'file') {
    const dlVolTemplate = () => {
      const rows = []
      const pins = pickups.filter((p) => p.pin).map((p) => p.pin)
      const bands = wtSplit.length ? wtSplit : [{ from: '0', to: '0.5' }, { from: '0.5', to: '1' }, { from: '1', to: '2' }]
      ;(pins.length ? pins : ['']).forEach((pin) => bands.forEach((b) => RFI_ZONES.forEach((z) => {
        rows.push({ 'Pickup PIN': pin, 'From (kg)': b.from, 'To (kg)': b.to, Zone: z, '% of shipments': '' })
      })))
      downloadXlsx('D2C_Volume_Distribution_Template.xlsx', rows, 'Volume Distribution')
    }
    const fix100 = (arr) => {
      const s = arr.reduce((a, x) => a + (Number(x.pct) || 0), 0)
      if (s && s !== 100 && arr.length) {
        const i = arr.reduce((mi, x, idx, a) => ((Number(x.pct) || 0) > (Number(a[mi].pct) || 0) ? idx : mi), 0)
        arr[i] = { ...arr[i], pct: (Number(arr[i].pct) || 0) + (100 - s) }
      }
      return arr
    }
    const parseVolFile = async (file) => {
      if (!file) return
      try {
        const wb = await readWorkbook(file)
        const aoa = Object.values(wb)[0] || []
        const head = aoa[0] || []
        const col = (name) => head.findIndex((h) => String(h || '').toLowerCase().includes(name))
        const cZone = col('zone'); const cPct = col('%'); const cFrom = col('from'); const cTo = col('to')
        if (cZone < 0 || cPct < 0) { enqueueSnackbar('Could not find the Zone / % columns — please use the downloaded template.', { variant: 'error' }); return }
        const zAgg = {}; const bAgg = {}
        aoa.slice(1).forEach((r) => {
          const pct = Number(r[cPct]); if (!Number.isFinite(pct) || pct <= 0) return
          const zRaw = String(r[cZone] || '').trim()
          const z = RFI_ZONES.find((x) => x.toLowerCase() === zRaw.toLowerCase() || (zRaw.toLowerCase() === 'metros' && x === 'Metro'))
          if (z) zAgg[z] = (zAgg[z] || 0) + pct
          const from = cFrom >= 0 ? String(r[cFrom] ?? '').trim() : ''
          const to = cTo >= 0 ? String(r[cTo] ?? '').trim() : ''
          if (to !== '') { const k = `${from}|${to}`; bAgg[k] = bAgg[k] || { from, to, acc: 0 }; bAgg[k].acc += pct }
        })
        const zSum = Object.values(zAgg).reduce((a, b) => a + b, 0)
        if (!zSum) { enqueueSnackbar('No % values found in the file — fill the “% of shipments” column and re-upload.', { variant: 'warning' }); return }
        setZoneSplit(fix100(RFI_ZONES.map((z) => ({ zone: z, pct: Math.round(((zAgg[z] || 0) / zSum) * 100) }))))
        const bands = Object.values(bAgg).sort((a, b) => (parseFloat(a.to) || 0) - (parseFloat(b.to) || 0))
        const bSum = bands.reduce((a, b) => a + b.acc, 0) || 1
        if (bands.length) setWtSplit(fix100(bands.map((b) => ({ from: b.from, to: b.to, pct: Math.round((b.acc / bSum) * 100) }))))
        setRfiMode('pct')
        enqueueSnackbar('Distribution imported — review it in Quick % split.', { variant: 'success' })
      } catch { enqueueSnackbar('Could not read the file — please use the downloaded template (.xlsx).', { variant: 'error' }) }
    }
    return (
      <SectionCard title={<>Upload a file <NoteInfoIcon body="Download the template, fill the % of shipments per pickup × weight band × zone, and upload — the splits load into Quick % split for review." /></>} subtitle="Fill the template and import — loads into Quick % split.">
        {modeTabs}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button size="small" variant="outlined" sx={btn} onClick={dlVolTemplate}>⬇ Download template — Volume Distribution</Button>
          <Button size="small" variant="contained" component="label" sx={btn}>Upload filled file<input type="file" hidden accept=".xlsx,.xls" onChange={(e) => parseVolFile(e.target.files?.[0])} /></Button>
        </Stack>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>Columns: Pickup PIN · From (kg) · To (kg) · Zone · % of shipments. All % values together should total 100.</Typography>
      </SectionCard>
    )
  }

  if (rfiMode === 'matrix') {
    return (
      <SectionCard title={<>Full % matrix <NoteInfoIcon body="Each cell is the % of shipments at that weight band × zone, per pickup (or one matrix replicated to all). The whole matrix must total 100%." /></>} subtitle="Type each weight × zone cell — must total 100%.">
        {modeTabs}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Chip label="Same % for all pickups (replicate)" onClick={() => setRepAll(!repAll)} variant={repAll ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600, fontSize: 12, bgcolor: repAll ? '#FDF1E5' : '#fff', color: repAll ? '#E65100' : 'text.secondary', border: '1.5px solid', borderColor: repAll ? '#E65100' : 'divider' }} />
          {!repAll && pickups.filter((p) => p.pin).map((p, i) => (
            <Button key={p.id || i} size="small" variant={activePk === i ? 'contained' : 'outlined'} sx={btn} onClick={() => setActivePk(i)}>
              {`${p.pin || `#${i + 1}`} · ${p.vol || 0}/mo`}
            </Button>
          ))}
        </Stack>
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr>
              <th style={thS}>From</th><th style={thS}>To</th>
              {RFI_ZONES.map((z) => <th key={z} style={thS}>{z} (%)</th>)}
              <th style={{ ...thS, textAlign: 'right' }}>Total</th>
              <th style={thS} />
            </tr></thead>
            <tbody>
              {(() => {
                const badRows = overlapIndices(rows)
                return rows.map((r, ri) => {
                  const rowSum = r.counts.reduce((s, c) => s + (parseFloat(c) || 0), 0)
                  const bad = showErr && badRows.has(ri)
                  return (
                    <tr key={ri}>
                      <td style={tdS}><TextField size="small" value={r.from} onChange={(e) => setBound(ri, 'from', e.target.value)} sx={{ width: 64 }} error={bad} /></td>
                      <td style={tdS}><TextField size="small" value={r.to} onChange={(e) => setBound(ri, 'to', e.target.value)} sx={{ width: 64 }} error={bad} /></td>
                      {RFI_ZONES.map((z, zi) => {
                        const cellEmpty = !String(r.counts[zi] ?? '').trim()
                        return (
                          <td key={z} style={tdS}><TextField size="small" value={r.counts[zi]} onChange={(e) => setCell(ri, zi, sanitizePct(e.target.value))} sx={{ width: 84 }} inputProps={{ style: { textAlign: 'right' } }} InputProps={pctAdornment} error={showErr && (cellEmpty || Math.round(mTotal) !== 100)} {...helperProps(showErr && cellEmpty ? 'Required' : undefined)} /></td>
                        )
                      })}
                      {/* per-weight-band sum column */}
                      <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: Math.round(rowSum) === 100 ? '#1D9E75' : 'inherit' }}>{Math.round(rowSum)}%</td>
                      <td style={tdS}><IconButton size="small" onClick={() => removeRow(ri)} disabled={rows.length === 1}><DeleteOutlineIcon fontSize="small" /></IconButton></td>
                    </tr>
                  )
                })
              })()}
            </tbody>
            {/* per-zone sum row */}
            <tfoot><tr style={{ borderTop: '2px solid #E5E7EB' }}>
              <td style={tdS} />
              <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>Total</td>
              {RFI_ZONES.map((z, zi) => {
                const colSum = rows.reduce((s, r) => s + (parseFloat(r.counts[zi]) || 0), 0)
                return <td key={z} style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>{Math.round(colSum)}%</td>
              })}
              <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, color: Math.round(mTotal) === 100 ? '#1D9E75' : '#DC2626' }}>{Math.round(mTotal)}%</td>
              <td style={tdS} />
            </tr></tfoot>
          </table>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }}>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} sx={btn} onClick={addRow}>Add weight band</Button>
          <Pill label={`Matrix total: ${Math.round(mTotal)}% / 100%`} styleKey={Math.round(mTotal) === 100 ? 'approved' : 'returned'} />
        </Stack>
        {showErr && Math.round(mTotal) !== 100 && <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5 }}>The matrix must total 100%.</Typography>}
        {showErr && overlapIndices(rows).size > 0 && <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5 }}>Weight ranges may not overlap.</Typography>}
      </SectionCard>
    )
  }

  return (
    <SectionCard title={<>Volume distribution <NoteInfoIcon body="How shipments spread across delivery zones and weight bands, in %. Give the two splits separately; we cross-multiply them into the full slab × zone matrix. Need lane-level control? Switch to the full % matrix per pickup." /></>} subtitle="How shipments spread across zones and weight bands — give each split; we combine them.">
      {modeTabs}
      {/* BUG-07: one split for all services (explicit) vs a split per service. */}
      {/* Prominent (pink) but compact — sized to its content so the switch stays right next to
          the label, not banished to the far right of a full-width block. */}
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 1.5, pl: 1, pr: 1.25, py: 0.6, borderRadius: '10px', border: '1px solid', borderColor: perSvcSplit ? '#D8B4C6' : '#ECDCE4', bgcolor: '#FBF3F7', boxShadow: perSvcSplit ? '0 1px 6px rgba(150,90,120,0.14)' : 'none' }}>
        <Box sx={{ width: 26, height: 26, flex: '0 0 auto', borderRadius: '7px', bgcolor: '#F2E3EB', color: '#9B6480', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CallSplitIcon sx={{ fontSize: 16 }} />
        </Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#7C4A62', whiteSpace: 'nowrap' }}>Split differs by service type</Typography>
        <Switch size="small" checked={perSvcSplit} onChange={(e) => togglePerSvcSplit(e.target.checked)} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#AE7595' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#AE7595' } }} />
        <NoteInfoIcon body="On = each selected service gets its own zone & weight split below. Off = one split applies to every service." />
      </Box>

      {perSvcSplit ? (
        services.map((k, idx) => {
          const missing = !svcSplits[k]
          // Limit to the zones this service serves (SDD/NDD drops ROI + Special), keeping any
          // % already entered — so the split reads and validates over the right zone set.
          const base = svcSplits[k] || { zones: zoneSplit, wts: wtSplit, tiers: wtTiers }
          const sp = {
            zones: zonesForService(k).map((zn) => { const f = base.zones.find((r) => r.zone === zn); return { zone: zn, pct: f ? f.pct : '' } }),
            wts: base.wts,
            tiers: base.tiers || wtTiers,
          }
          // Its own bold colour — the ONE strong-colour banner on this page, reserved for
          // naming the service, so it never reads as the same kind of thing as the Zone/Weight
          // dot-headers inside (which stay deliberately lighter).
          const zTot = Math.round(sp.zones.reduce((s, z) => s + (parseFloat(z.pct) || 0), 0))
          const wTot = Math.round(sp.wts.reduce((s, w) => s + (parseFloat(w.pct) || 0), 0))
          const bannerPillSx = (ok) => (ok ? { bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' } : { bgcolor: '#fff', color: '#B91C1C' })
          return (
            <Box key={k} sx={{ borderRadius: '16px', bgcolor: '#FAFBFD', border: '1px solid', borderColor: showErr && missing ? 'error.main' : 'divider', p: 2, mb: idx < services.length - 1 ? 3 : 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 2, py: 1.5, borderRadius: '12px', background: 'linear-gradient(90deg, #103A75, #2065D1)', mb: 2, boxShadow: '0 4px 14px rgba(32,101,209,0.22)' }}>
                <Box sx={{ width: 26, height: 26, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.22)', color: '#fff', display: 'grid', placeItems: 'center', fontSize: 12.5, fontWeight: 800, flexShrink: 0 }}>{idx + 1}</Box>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>{labelForKey(k)}</Typography>
                <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Pill label={zTot === 100 ? '✓ Zone 100%' : `Zone ${zTot}%`} sx={bannerPillSx(zTot === 100)} />
                  <Pill label={wTot === 100 ? '✓ Weight 100%' : `Weight ${wTot}%`} sx={bannerPillSx(wTot === 100)} />
                </Box>
              </Box>
              {showErr && missing && <Typography sx={{ fontSize: 11, color: 'error.main', mb: 1 }}>Splits missing for {labelForKey(k)} — edit any value below to seed it, or toggle "differentiate by service" off then on again.</Typography>}
              <SplitEditor
                zones={sp.zones} wts={sp.wts} tiers={sp.tiers} maxWt={maxWt} showErr={showErr}
                largeParcel={serviceMeta(k).isLarge} metroBlocked={metroBlocked}
                onZones={(zones) => setSvcSplits((p) => ({ ...p, [k]: { ...(p[k] || sp), zones } }))}
                onWts={(wts) => setSvcSplits((p) => ({ ...p, [k]: { ...(p[k] || sp), wts } }))}
                onTiers={(next) => setServiceTiers(k, next)}
              />
            </Box>
          )
        })
      ) : (
        <SplitEditor zones={zoneSplit} wts={wtSplit} tiers={wtTiers} maxWt={maxWt} showErr={showErr} largeParcel={largeOnly} metroBlocked={metroBlocked} onZones={(z) => setZoneSplit(z)} onWts={(w) => setWtSplit(w)} onTiers={setSharedTiers} />
      )}
      {!perSvcSplit && (() => {
        // Add a per-band sum column and a per-zone sum row (+ grand total) to the preview.
        const withRowTotal = matrixPreview.map((r) => ({ ...r, __total: Math.round(RFI_ZONES.reduce((s, z) => s + (Number(r[z]) || 0), 0)) }))
        const totalRow = { band: 'Total', __total: Math.round(matrixPreview.reduce((s, r) => s + RFI_ZONES.reduce((a, z) => a + (Number(r[z]) || 0), 0), 0)) }
        RFI_ZONES.forEach((z) => { totalRow[z] = Math.round(matrixPreview.reduce((s, r) => s + (Number(r[z]) || 0), 0)) })
        const cols = [...matrixCols, { key: '__total', label: 'Total', align: 'right' }]
        return (
          <>
            <Typography sx={{ ...subHead, mt: 2 }}>Resulting matrix preview (shipments / month — all pickups)</Typography>
            <DataTable columns={cols} rows={[...withRowTotal, totalRow]} getRowKey={(r) => r.band} />
          </>
        )
      })()}
    </SectionCard>
  )
}

/* ───────────────────────── Step 3 ───────────────────────── */
// Default per-service config (rich shape feeding the Generate payload).
export function defaultSvcCfg() {
  return {
    saved: false,
    _seeded: false, // carry-forward seeding from Step 1 applied once
    vol: '', // shipments/month for this service — set in Step 1's Service type distribution
    cities: [], // selected pickup/drop cities [{value, vol}] — seeded from Step 1 pickups
    mode: 'margin', // margin | ratecard | standard(prospect size)
    margin: '',
    // rates[label][zone] for Target Rate Card mode — label is a literal weight-range
    // string ("0–0.5 kg", …) from rateRowLabelsFromTiers. D2C wizard redesign (Aug 2026):
    // the tier structure that produces these labels is built once in Step 2 (shared, or
    // per service when "Split differs by service type" is on) and carries here as-is —
    // no per-service structure-building UI in Step 3 any more, just rate entry.
    rates: {},
    // compactRates: the compact "base + increment" entry the SL actually fills — one
    // field per band (first / firstEach / per-band base+each), expanded into `rates`
    // above via expandCompactRates on every change. See compactRateRows (apiMap.js).
    compactRates: { first: {}, firstEach: {}, bands: {} },
    // courierCompactRates: COURIER_WISE deals only — one compact block per preferred
    // courier PARENT (shared structure, per-courier ₹ — recipe spec §2 rule 3).
    courierCompactRates: {},
    cod: { flat: '', pct: '', rtoPct: '' }, // forward charges (COD flat / COD % of value / RTO % of forward)
    evidence: 'No', // "Evidence shared?" (Target Rate Card)
    rvpMode: 'linked', // linked | dedicated (reverse)
    rvp: { minCost: '', pctOfForward: '', qc: '', formulaType: 'higher' }, // reverse charges; formulaType: flat | pct | higher (UI-only, not sent)
    prospectSize: 'SME', // SME | ENTERPRISE (standard mode)
    primary: [], // selected courier objects (cpAccountCode → cpPreference)
    blacklist: [], // selected courier objects (no API field → capturedInputs)
  }
}

/* Copy from a previous prospect — lists the SL's own prior proposals and loads the
   selected one's RFI split (version.rfiEntries) into the Quick % split. All real data. */
function CopyFromProspect({ setZoneSplit, setWtSplit, setRfiMode, enqueueSnackbar, modeTabs }) {
  const { data, isLoading } = useListProposalsQuery({ page: 0, size: 100 })
  const [fetchVersion, { isFetching }] = useLazyGetVersionQuery()
  // Only proposals that already have a generated version carry an RFI split to copy.
  const prospects = (Array.isArray(data) ? data : []).filter((p) => p.currentVersionId)

  // Nudge a rounded split to total exactly 100% (so the Quick % split passes validation).
  const fix100 = (arr) => {
    const s = arr.reduce((a, x) => a + (x.pct || 0), 0)
    if (s && s !== 100 && arr.length) {
      const i = arr.reduce((mi, x, idx, a) => (x.pct > a[mi].pct ? idx : mi), 0)
      arr[i] = { ...arr[i], pct: arr[i].pct + (100 - s) }
    }
    return arr
  }

  const applyFrom = async (p) => {
    try {
      const v = await fetchVersion(p.currentVersionId).unwrap()
      const rfi = v?.rfiEntries || []
      if (!rfi.length) { enqueueSnackbar('That prospect has no RFI split to copy.', { variant: 'warning' }); return }
      // Zone marginals: aggregate splitPct by zone, map API zone → UI zone, normalise to 100%.
      const zRev = {}
      RFI_ZONES.forEach((z) => { zRev[toApiZone(z)] = z })
      const zAgg = {}
      rfi.forEach((e) => { const ui = zRev[e.zone] || e.zone; zAgg[ui] = (zAgg[ui] || 0) + (Number(e.splitPct) || 0) })
      const zSum = RFI_ZONES.reduce((s, z) => s + (zAgg[z] || 0), 0) || 1
      const zone = fix100(RFI_ZONES.map((z) => ({ zone: z, pct: Math.round(((zAgg[z] || 0) / zSum) * 100) })))
      // Weight marginals: aggregate by slab (kg), order, build contiguous bands.
      const wAgg = {}
      rfi.forEach((e) => { const k = parseFloat(String(e.weightSlab)) || 0; wAgg[k] = (wAgg[k] || 0) + (Number(e.splitPct) || 0) })
      const slabs = Object.keys(wAgg).map(Number).sort((a, b) => a - b)
      const wSum = slabs.reduce((s, k) => s + wAgg[k], 0) || 1
      let prev = 0
      const wt = fix100(slabs.map((k) => { const band = { from: String(prev), to: String(k), pct: Math.round((wAgg[k] / wSum) * 100) }; prev = k; return band }))
      setZoneSplit(zone)
      if (wt.length) setWtSplit(wt)
      setRfiMode('pct')
      enqueueSnackbar(`Copied RFI split from ${p.brandName || 'prospect'} — review it in Quick % split.`, { variant: 'success' })
    } catch (e) { enqueueSnackbar(e?.message || 'Could not load that prospect’s RFI.', { variant: 'error' }) }
  }

  return (
    <SectionCard title="Enter RFI details — copy from a previous prospect" subtitle="Reuse the volume split from one of your earlier proposals. Pick a prospect; its zone & weight split loads into Quick % split for review.">
      {modeTabs}
      <DataTable
        columns={[
          { key: 'b', label: 'Prospect', render: (r) => <b>{r.brandName || r.dealId || '—'}</b> },
          { key: 'h', label: 'HubSpot', render: (r) => r.hubspotDealId || '—' },
          { key: 's', label: 'Status', render: (r) => r.status || '—' },
          { key: 'a', label: '', render: (r) => <Button size="small" variant="outlined" sx={btn} disabled={isFetching} onClick={() => applyFrom(r)}>Use this</Button> },
        ]}
        rows={prospects} getRowKey={(r) => r.dealId || r.id}
        emptyText={isLoading ? 'Loading prospects…' : 'No earlier proposals with an RFI split yet.'}
      />
    </SectionCard>
  )
}

// Single source of truth for "is this service's Step 3 form complete" — the same rules the
// old per-service "✓ Save inputs" button used to enforce only on click (and only if you
// remembered to click it). Now it runs live off whatever's actually in the fields, so a
// service's status is always truthful — no separate save step to forget.
function validateServiceCfg({ cfg, label, isReverse, tiers = [], rateCardLibrary = [], dealRateMode = 'BLENDED', serviceType }) {
  const isLinkedRvp = isReverse && cfg.rvpMode === 'linked'
  if (!cfg.vol) return `${label} — monthly volume is required (set in Step 1's Service type distribution).`
  if (!isLinkedRvp && cfg.mode === 'margin' && !(parseFloat(cfg.margin) >= 10)) return `${label} — target margin must be at least 10%.`
  // Standard mode hands over a pre-built card as-is — incomplete if the library has no
  // card for this service × rate-card type × tier (nothing to send otherwise).
  if (!isLinkedRvp && cfg.mode === 'standard'
      && !matchStandardCard(rateCardLibrary, { serviceType: SERVICE_MAP[serviceCodeToKey(serviceType)] || serviceType, rateCardType: dealRateMode, prospectTier: cfg.prospectSize || 'SME' }).card) {
    return `${label} — no standard card published for ${dealRateMode === 'COURIER_WISE' ? 'Courier-wise' : 'Blended'} · ${cfg.prospectSize || 'SME'}. Ask the FH to add one, or pick another mode.`
  }
  if (!(cfg.primary || []).length) return `${label} — at least one preferred courier is required.`
  if (isReverse && cfg.rvpMode === 'linked'
      && !String(cfg.rvp.minCost ?? '').trim() && !String(cfg.rvp.pctOfForward ?? '').trim()) {
    return `${label} — reverse (RVP) needs a minimum RVP cost and/or a % of forward.`
  }
  if (!isLinkedRvp && cfg.mode === 'ratecard') {
    const rows = rateRowLabelsFromTiers(tiers)
    if (!rows.length) return `${label} — no weight slabs to price. Build the structure in Step 2.`
    if (dealRateMode === 'COURIER_WISE') {
      // Courier-wise: rates are captured per courier (shared structure, per-courier ₹ —
      // recipe spec §2 rule 3). Every preferred courier's compact grid must be complete.
      const cRows = compactRateRows(tiers)
      for (const { parent } of (cfg.primary || [])) {
        if (!parent) continue
        const c = cfg.courierCompactRates?.[parent]
        let missing = 0
        cRows.forEach(({ bandIndex, kind }) => RFI_ZONES.forEach((z) => {
          const v = kind === 'first' ? c?.first?.[z] : kind === 'firstEach' ? c?.firstEach?.[z] : c?.bands?.[bandIndex]?.[kind]?.[z]
          if (v == null || String(v).trim() === '') missing += 1
        }))
        if (missing) return `${label} — ${parent}: rate card incomplete (${missing} empty cell${missing === 1 ? '' : 's'}). Courier-wise deals need every preferred courier's card filled.`
      }
    } else {
      const missing = []
      rows.forEach((r) => RFI_ZONES.forEach((z) => {
        const v = cfg.rates[r]?.[z]
        if (v == null || String(v).trim() === '') missing.push(`${r} × ${z}`)
      }))
      if (missing.length) return `${label} — client target rate card must be fully filled (${missing.length} empty cell${missing.length === 1 ? '' : 's'}, first: ${missing[0]}).`
    }
    // Forward rate-card deals price COD flat ₹ / RTO % exactly as entered; a blank fell back to
    // the engine's default rails (₹25 / 80% for surface) while the PDF printed no COD/RTO line at
    // all. Both are required now — 0 is a valid entry (Oct 2026). COD % of value stays optional.
    if (!isReverse) {
      if (!String(cfg.cod?.flat ?? '').trim()) return `${label} — COD fee (flat ₹) is required. Enter 0 if no COD charge.`
      if (!String(cfg.cod?.rtoPct ?? '').trim()) return `${label} — RTO charge (% of forward) is required. Enter 0 if no RTO charge.`
    }
  }
  if (cfg.evidence === 'Yes' && !(cfg.evidenceCount > 0)) return `${label} — "Evidence shared = Yes" needs at least one attached file.`
  return ''
}

function Step3({ services, svcConfig, setSvcConfig, pickups = [], pinCache = {}, wtTiers = [], perSvcSplit = false, svcSplits = {}, rtoShare, codShare, rateCardLibrary = [], dealRateMode = 'BLENDED', showErr = false, exportHost }) {
  // The weight-slab structure that carries into this step's rate-card grid: built in
  // Step 2, shared across every service unless "Split differs by service type" is on,
  // in which case each service's own structure (if it has one yet) wins.
  const tiersFor = (k) => (perSvcSplit ? svcSplits[k]?.tiers : null) || wtTiers
  const { enqueueSnackbar } = useWizardSnackbar()
  const setCfg = (key, patch) => setSvcConfig((p) => ({ ...p, [key]: { ...(p[key] || defaultSvcCfg()), ...patch } }))
  // One-way scroll (meeting feedback) — every selected service's full form is shown at
  // once, stacked top to bottom; no tab/pill switcher and no per-service Save button.
  // Status is the SAME live validator that gates Continue at the wizard footer, so it's
  // always truthful — no separate "saved" flag to forget to click.
  const errFor = (k) => {
    const cfg = svcConfig[k] || defaultSvcCfg()
    return validateServiceCfg({ cfg, label: labelFor(k), isReverse: serviceMeta(k).isReverse, tiers: tiersFor(k), rateCardLibrary, dealRateMode, serviceType: serviceMeta(k).apiValue })
  }
  const svcStatus = (k) => {
    const cfg = svcConfig[k] || defaultSvcCfg()
    if (!errFor(k)) return 'ok'
    const started = !!(cfg.vol || cfg.margin || (cfg.primary || []).length || Object.keys(cfg.rates || {}).length)
    return started ? 'warn' : 'pending'
  }
  // Layout-1 shell (approved mockup): one service at a time behind pill tabs, with a
  // "Save & next" chain — PRESENTATION ONLY. The live validator above stays the single
  // source of truth (no separate saved flag); the chain button just runs it and advances.
  const [activeSvcK, setActiveSvcK] = useState(null)
  const [chainErrK, setChainErrK] = useState(null) // service whose inline errors were revealed by a failed chain click
  // Fallback courier list (generic) — used only if the D2C /masters/couriers endpoint isn't available.
  const { data: fallbackCouriers = [] } = useGetAllCourierPartnersQuery({ offset: 0, limit: 15000, excludes: 'sla' })
  // Live service-type catalog (code → display name) so rich serviceability codes render with
  // their real names in the per-service blocks. Legacy keys/enums fall back to serviceMeta.
  const { data: svcTypeData } = useGetServiceabilityServiceTypesQuery()
  const svcNameByCode = useMemo(() => {
    const raw = svcTypeData?.responseObj?.serviceTypes || svcTypeData?.data?.serviceTypes || svcTypeData?.serviceTypes || []
    const m = {}
    ;(Array.isArray(raw) ? raw : []).forEach((st) => { if (st.code) m[st.code] = st.name || st.code })
    return m
  }, [svcTypeData])
  const labelFor = (k) => SERVICES.find((s) => s.key === k)?.label || svcNameByCode[k] || serviceMeta(k).label
  // Pickup cities carried from Step 1 — the source for cities-in-scope chips.
  const pickupCities = pickups.filter((p) => p.pin).map((p) => ({ pin: p.pin, name: pinCache[p.pin]?.city || cityFor(p.pin) || p.pin, vol: p.vol }))

  // ── "Prefer Excel?" workbook — built from THIS proposal's real per-service state ──
  const [wbBusy, setWbBusy] = useState(false)
  const sheetNameFor = (lbl) => lbl.replace(/[\\/?*[\]:]/g, '-').slice(0, 31)
  const modeLabel = (m) => ({ margin: 'Target Margin', ratecard: 'Target Rate Card', standard: 'Standard' }[m] || m)
  const downloadWb = () => {
    const sheets = services.map((k) => {
      const cfg = svcConfig[k] || defaultSvcCfg()
      const lbl = labelFor(k)
      const cities = (cfg.cities || []).map((c) => c.value).filter(Boolean).join(', ')
      const aoa = [
        ['Prozo D2C — Commercial Expectations', lbl],
        ['Field', 'Value'],
        ['Service (do not edit)', lbl],
        ['Monthly volume (set in Step 1 — do not edit)', cfg.vol || ''],
        ['Cities in scope', cities],
        ['Input mode (Target Margin / Target Rate Card / Standard)', modeLabel(cfg.mode)],
        ['Target margin %', cfg.margin || ''],
        ['COD flat (Rs)', cfg.cod?.flat || ''],
        ['COD % of value', cfg.cod?.pct || ''],
        ['RTO % of forward', cfg.cod?.rtoPct || ''],
        [],
        ['Target Rate Card — fill only if Input mode = Target Rate Card (structure built in Step 2)'],
        ['Slab', ...RFI_ZONES],
        ...rateRowLabelsFromTiers(tiersFor(k)).map((r) => [r, ...RFI_ZONES.map((z) => cfg.rates?.[r]?.[z] || '')]),
      ]
      return { name: sheetNameFor(lbl), aoa }
    })
    const readme = [
      ['How to fill this workbook'],
      ['1. One tab per service type in this proposal.'],
      ['2. Context rows (Service, Monthly volume, Cities) are carried from earlier steps — do not change. Monthly volume comes from Step 1\'s Service type distribution; Max weight and the Prepaid/COD RTO split come from Step 1 too and no longer have per-service fields.'],
      ['3. Fill Target margin % OR the Target Rate Card grid, depending on the Input mode shown in each tab.'],
      ['4. COD flat / COD % / RTO % are optional forward charges.'],
      ['5. Save, then use “Upload filled workbook” on the same screen.'],
    ]
    downloadWorkbook('D2C_Commercial_Expectations.xlsx', [{ name: 'ReadMe', aoa: readme }, ...sheets])
  }
  const uploadWb = async (file) => {
    if (!file) return
    setWbBusy(true)
    try {
      const data = await readWorkbook(file)
      const byName = {}
      services.forEach((k) => { byName[sheetNameFor(labelFor(k))] = k })
      let imported = 0
      Object.entries(data).forEach(([sheet, aoa]) => {
        const k = byName[sheet]
        if (!k) return
        const kv = {}
        aoa.forEach((row) => { if (row && row.length >= 2 && typeof row[0] === 'string') kv[row[0].trim()] = row[1] })
        const patch = {}
        // Context fields are importable too — on a fresh proposal they're blank, and the
        // whole point of the workbook is filling them offline (feedback: upload "not working"
        // because a filled Monthly volume never landed in the form). Monthly volume/Max
        // weight/SOW/Prepaid+COD RTO are no longer per-service (Step 1 owns them) — not imported.
        const m = kv['Target margin %']
        if (m !== undefined && m !== '') patch.margin = String(m)
        const cod = { ...(svcConfig[k]?.cod || {}) }
        if (kv['COD flat (Rs)'] != null && kv['COD flat (Rs)'] !== '') cod.flat = String(kv['COD flat (Rs)'])
        if (kv['COD % of value'] != null && kv['COD % of value'] !== '') cod.pct = String(kv['COD % of value'])
        if (kv['RTO % of forward'] != null && kv['RTO % of forward'] !== '') cod.rtoPct = String(kv['RTO % of forward'])
        patch.cod = cod
        const hIdx = aoa.findIndex((row) => row && row[0] === 'Slab')
        if (hIdx >= 0) {
          const zones = aoa[hIdx].slice(1)
          const rates = { ...(svcConfig[k]?.rates || {}) }
          for (let i = hIdx + 1; i < aoa.length; i++) {
            const row = aoa[i]
            if (!row || !row[0]) continue
            const rl = String(row[0])
            rates[rl] = { ...(rates[rl] || {}) }
            zones.forEach((z, zi) => { const v = row[zi + 1]; if (v != null && v !== '') rates[rl][z] = String(v) })
          }
          patch.rates = rates
        }
        setCfg(k, patch)
        imported += 1
      })
      enqueueSnackbar(imported
        ? `Imported ${imported} service tab(s) from the workbook — review the values in the blocks below. Everything is sent to pricing when you Generate (no separate upload call).`
        : 'No matching service tabs found — please use the downloaded template.', { variant: imported ? 'success' : 'warning' })
    } catch {
      enqueueSnackbar('Could not read the workbook — please use the downloaded template.', { variant: 'error' })
    } finally { setWbBusy(false) }
  }

  // Read-only snapshot of every field on this step — filled or blank, including courier
  // preference/blacklist/evidence (which the fill-and-reupload workbook above doesn't
  // carry, since re-upload never consumes them) — for comparing against the generated
  // proposal later. Distinct from downloadWb: that one is a two-way fill/upload pair;
  // this is a one-way audit export.
  const exportStep3 = () => {
    const sheets = services.map((k) => {
      const cfg = svcConfig[k] || defaultSvcCfg()
      const lbl = labelFor(k)
      const isFwd = serviceMeta(k).isForward
      const cities = (cfg.cities || []).map((c) => c.value).filter(Boolean).join(', ')
      const primary = (cfg.primary || []).map((c, i) => `${i + 1}. ${c.parent}`).join('; ')
      const blacklist = (cfg.blacklist || []).map((c) => c.parent).join(', ')
      // Prepaid/COD RTO % — both get Step 1's Current RTO % directly, same as
      // buildGeneratePayload (product decision — not split by COD share). Shown here
      // (unlike the fill/upload workbook above) since this export is a read-only
      // "what's actually being sent" snapshot, not something the SL edits.
      const rtoNum = Number(rtoShare) || 0
      const codRto = isFwd && rtoNum > 0 ? rtoNum : ''
      const prepaidRto = isFwd && rtoNum > 0 ? rtoNum : ''
      const aoa = [
        ['Prozo D2C — Commercial Expectations snapshot (Step 3)', lbl],
        ['Field', 'Value'],
        ['Service', lbl],
        ['Complete?', svcStatus(k) === 'ok' ? 'Yes' : 'No'],
        ['Monthly volume (Step 1)', cfg.vol || ''],
        ['Cities in scope', cities],
        ['Input mode', modeLabel(cfg.mode)],
        ['Target margin %', cfg.margin || ''],
        ['COD flat (Rs)', cfg.cod?.flat || ''],
        ['COD % of value', cfg.cod?.pct || ''],
        ['RTO % of forward', cfg.cod?.rtoPct || ''],
        ['Prepaid RTO % of shipments (calculated from Step 1)', prepaidRto],
        ['COD RTO % of shipments (calculated from Step 1)', codRto],
        ['Courier preference (priority order)', primary || '(none — Prozo defaults)'],
        ['Blacklisted couriers', blacklist || 'None'],
        ['Evidence shared?', cfg.evidenceShared ? 'Yes' : 'No'],
        [],
        ['Target Rate Card — populated only if Input mode = Target Rate Card (structure built in Step 2)'],
        ['Slab', ...RFI_ZONES],
        ...rateRowLabelsFromTiers(tiersFor(k)).map((r) => [r, ...RFI_ZONES.map((z) => cfg.rates?.[r]?.[z] || '')]),
      ]
      return { name: sheetNameFor(lbl), aoa }
    })
    downloadWorkbook('D2C_Step3_CommercialExpectations_Snapshot.xlsx', sheets.length ? sheets : [{ name: 'No services', aoa: [['Select at least one service in Step 1.']] }])
  }

  useHeaderExport(exportHost, exportStep3, 'Export Step 3 data (.xlsx)', !services.length)

  return (
    <>
      {services.length === 0 && <Alert severity="warning">No services selected — go back to Step 1.</Alert>}
      {services.length > 0 && (() => {
        // Layout-1 shell (approved mockup): pill tabs on top, ONE service mounted at a
        // time, "Save & next" chain at the bottom of each service. Presentation only —
        // the live validator (errFor/svcStatus) is untouched and still gates Continue.
        const active = services.includes(activeSvcK) ? activeSvcK : services[0]
        const activeIdx = services.indexOf(active)
        const nextK = activeIdx < services.length - 1 ? services[activeIdx + 1] : null
        const doneCount = services.filter((k) => svcStatus(k) === 'ok').length
        const cfg = svcConfig[active] || defaultSvcCfg()
        const cities = (cfg.cities || []).map((c) => c.value).filter(Boolean)
        const cityChip = cities.length ? `Cities: ${cities.slice(0, 3).join(' · ')}${cities.length > 3 ? ` +${cities.length - 3}` : ''}` : 'Cities: from Step 1 pickups'
        const slabRows = rateRowLabelsFromTiers(tiersFor(active))
        const slabMax = (tiersFor(active) || []).reduce((s, t) => s + (Number(t.size) || 0) * Math.max(0, Math.floor(Number(t.count) || 0)), 0)
        const statusPill = (k) => {
          const st = svcStatus(k)
          return st === 'ok' ? <Pill label="✓ Complete" styleKey="approved" /> : <Pill label={st === 'warn' ? 'In progress' : 'Not started'} sx={{ bgcolor: st === 'warn' ? '#FBF1E2' : '#F4F6F8', color: st === 'warn' ? '#B4650A' : 'text.secondary' }} />
        }
        // Chain: validate THIS service with the same validator that gates Continue;
        // pass → advance to the next pill (top of form), fail → reveal inline errors.
        const onChain = () => {
          const err = errFor(active)
          if (err) { setChainErrK(active); enqueueSnackbar(err, { variant: 'warning' }); return }
          setChainErrK(null)
          if (nextK) { setActiveSvcK(nextK); window.scrollTo({ top: 0, behavior: 'smooth' }) }
          else { enqueueSnackbar('All service inputs look complete — continue to Generate & Review below.', { variant: 'success' }) }
        }
        return (
          <Box sx={{ borderRadius: '12px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', boxShadow: '0 1px 2px rgba(16,24,40,0.04)', p: 2.75 }}>
            {/* Everything lives ON the card — no floating utility rows on the canvas
                (feedback: the stacked sparse rows above the card read as dead space).
                Top row: service pills (click to jump anywhere; ✓ appears as the live
                validator passes) + ready counter + the offline workbook round-trip. */}
            <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1, pb: 2, mb: 2.25, borderBottom: '1px solid', borderColor: 'divider' }}>
              {services.map((k) => {
                const on = k === active
                const ok = svcStatus(k) === 'ok'
                return (
                  <Box
                    key={k} component="button" onClick={() => setActiveSvcK(k)}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', font: 'inherit',
                      border: '1px solid', borderColor: on ? 'primary.main' : 'rgba(145,158,171,0.32)', borderRadius: '999px',
                      px: 2.25, py: 1, bgcolor: on ? 'primary.main' : '#fff', fontSize: 13, fontWeight: 600,
                      color: on ? '#fff' : ok ? '#177A53' : 'text.secondary', transition: 'all .15s ease',
                    }}
                  >
                    {ok && <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
                    {labelFor(k)}
                  </Box>
                )
              })}
              <Stack direction="row" alignItems="center" sx={{ ml: 'auto', gap: 1, flexWrap: 'wrap' }}>
                {services.length > 1 && (
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'primary.dark', whiteSpace: 'nowrap', mr: 0.5 }}>{doneCount} of {services.length} ready</Typography>
                )}
                <Button size="small" variant="outlined" sx={btn} onClick={downloadWb}>⬇ Fillable workbook</Button>
                <Button size="small" variant="outlined" component="label" sx={btn} disabled={wbBusy}>
                  {wbBusy ? 'Reading…' : '⬆ Upload filled'}
                  <input type="file" hidden accept=".xlsx,.xls" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadWb(f) }} />
                </Button>
              </Stack>
            </Stack>

            <Box>
              {/* Service header — name + live status + mode, volume on the right. */}
              <Stack direction="row" alignItems="center" sx={{ flexWrap: 'wrap', gap: 1, mb: 1.25 }}>
                <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: 'primary.dark', mr: 0.5 }}>{labelFor(active)}</Typography>
                {statusPill(active)}
                {cfg.mode && <Pill label={modeLabel(cfg.mode)} styleKey="purple" />}
                <Typography sx={{ ml: 'auto', fontSize: 12.5, color: 'text.secondary' }}>{cfg.vol ? `${Number(cfg.vol).toLocaleString('en-IN')}/mo` : ''}</Typography>
              </Stack>
              {/* Context chips — what Steps 1–2 already fixed for this service. */}
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 2.25 }}>
                <Pill label={`${cfg.vol ? Number(cfg.vol).toLocaleString('en-IN') : '—'} shipments/mo (Step 1)`} sx={{ bgcolor: '#EAF1FC', color: 'primary.dark' }} />
                <Pill label={`Slabs: ${slabRows.length} · 0–${Math.round(slabMax * 100) / 100} kg (Step 2)`} sx={{ bgcolor: '#F4F6F8', color: 'text.secondary' }} />
                <Pill label={cityChip} sx={{ bgcolor: '#F4F6F8', color: 'text.secondary' }} />
              </Stack>

              <ServiceBlock
                serviceKey={active}
                label={labelFor(active)}
                isReverse={serviceMeta(active).isReverse}
                cfg={cfg}
                setCfg={(patch) => setCfg(active, patch)}
                serviceType={serviceMeta(active).apiValue}
                fallbackCouriers={fallbackCouriers}
                pickupCities={pickupCities}
                pinCache={pinCache}
                tiers={tiersFor(active)}
                rtoShare={rtoShare}
                codShare={codShare}
                rateCardLibrary={rateCardLibrary}
                dealRateMode={dealRateMode}
                showErr={showErr || chainErrK === active}
              />

              {/* Save & next chain — the default path through multiple services; pills
                  above stay clickable for jumping around. Single-service deals just Save. */}
              <Stack direction="row" sx={{ mt: 3, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button variant="contained" sx={btn} onClick={onChain}>
                  {nextK ? `✓ Save & next: ${labelFor(nextK)} →` : '✓ Save inputs'}
                </Button>
              </Stack>
            </Box>
          </Box>
        )
      })()}
    </>
  )
}

/* One collapsible commercial-expectations block per service type, in exactly 3 bold
   sections ("3 clear sections" mockup) — Pricing, Volume/scope/returns, Courier
   preference — each with an accent-bar header; everything else (rate structure,
   COD commercials, cities, RTO split, excluded) is a quiet uppercase sub-caption. */
function ServiceBlock({ label, isReverse, cfg, setCfg, serviceType, fallbackCouriers = [], pinCache = {}, tiers = [], rateCardLibrary = [], dealRateMode = 'BLENDED', rtoShare, codShare, showErr = false }) {
  const { enqueueSnackbar } = useWizardSnackbar()
  const [outputMode, setOutputMode] = useState('short') // short (client sentence) | long (full slab table) — Output preview below the compact rate grid
  const [outputOpen, setOutputOpen] = useState(false) // Output starts collapsed — a preview, not something everyone needs to look at
  // D2C Courier Selection Pivot (D2C_PRICING_PIVOT_FE_INTEGRATION.md) — the SL now picks
  // a courier PARENT only (e.g. "Shadowfax"); the backend auto-resolves the cheapest
  // eligible account per lane. Scoped by THIS accordion's own serviceType so the backend
  // does the eligibility filtering (incl. _REG vs _LARGE variants, isExternal exclusion)
  // — no Service Type or Account Code step needed on our side anymore.
  const { data: d2cCouriers = [] } = useGetCouriersQuery({ serviceType })
  const couriers = d2cCouriers.length ? d2cCouriers : fallbackCouriers
  // showErr now comes from the wizard footer's failed Continue/Generate attempt (Step 3's
  // parent), not a local "Save" click — one consistent trigger, same as every other step.
  const parentOf = (c) => c.parent || c.name || c.cpAccountCode || c.code
  // A preference/blacklist entry is just { parent } now (no account code — the backend
  // resolves that per lane) — exclude parents already selected either way.
  const selectedParents = new Set([...(cfg.primary || []), ...(cfg.blacklist || [])].map((p) => p.parent))
  const availableCp = couriers.filter((c) => !selectedParents.has(parentOf(c)))
  const cpParents = [...new Set(availableCp.map(parentOf).filter(Boolean))].sort()
  // Selected/blacklisted rows show just the parent name now — there's no account
  // code to show; the backend resolves the actual account per lane automatically.
  const cpLabel = (c) => (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography noWrap sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3 }}>{c.parent}</Typography>
    </Box>
  )
  const swap = (arr, i, j) => { const a = [...arr]; const t = a[i]; a[i] = a[j]; a[j] = t; return a }

  // Cities-in-scope are inherited from Step 1 (pickup/drop locations) and carried on cfg.cities
  // for the exports/payload — no per-service city picker here (removed as redundant).

  // D2C wizard redesign (Aug 2026) — the weight-slab structure is now built once in
  // Step 2 (shared, or per service when "Split differs by service type" is on) and
  // carries "as is" into this grid — there's no builder here any more, just ₹ rate
  // entry against the rows the structure defines.
  const rows = rateRowLabelsFromTiers(tiers)
  // Compact rate entry (Aug 2026 rate-card revamp) — one field per band (first slab,
  // each-additional per band, base-rate only for "fresh" bands) instead of one field
  // per individual slab. Every edit re-expands into cfg.rates via expandCompactRates,
  // so the full slab × zone table (and everything downstream that reads it — the
  // generate payload, the validator) stays exactly as populated as it is today.
  const compactRows = compactRateRows(tiers)
  // COURIER_WISE deals capture the SAME compact grid once per preferred courier
  // (shared structure, per-courier ₹ — recipe spec §2 rule 3). The tabs above the
  // grid pick which courier's card is being edited; cfg.rates always mirrors the
  // FIRST preferred courier (the single target card the engine receives).
  const courierWise = dealRateMode === 'COURIER_WISE'
  const courierParents = (cfg.primary || []).map((c) => c.parent).filter(Boolean)
  const [activeCourier, setActiveCourier] = useState(courierParents[0] || null)
  useEffect(() => {
    if (courierWise && !courierParents.includes(activeCourier)) setActiveCourier(courierParents[0] || null)
  }, [courierWise, courierParents.join('|')]) // eslint-disable-line react-hooks/exhaustive-deps
  const compact = (courierWise ? cfg.courierCompactRates?.[activeCourier] : cfg.compactRates) || { first: {}, firstEach: {}, bands: {} }
  const setCompact = (nextCompact) => {
    if (courierWise) {
      if (!activeCourier) return
      const courierCompactRates = { ...(cfg.courierCompactRates || {}), [activeCourier]: nextCompact }
      const patch = { courierCompactRates }
      if (activeCourier === courierParents[0]) patch.rates = expandCompactRates(tiers, nextCompact, RFI_ZONES)
      setCfg(patch)
    } else {
      setCfg({ compactRates: nextCompact, rates: expandCompactRates(tiers, nextCompact, RFI_ZONES) })
    }
  }
  const setCompactFirst = (zone, v) => setCompact({ ...compact, first: { ...compact.first, [zone]: v } })
  const setCompactFirstEach = (zone, v) => setCompact({ ...compact, firstEach: { ...compact.firstEach, [zone]: v } })
  const setCompactBand = (bandIndex, field, zone, v) => {
    const bands = compact.bands || {}
    const band = bands[bandIndex] || {}
    setCompact({ ...compact, bands: { ...bands, [bandIndex]: { ...band, [field]: { ...(band[field] || {}), [zone]: v } } } })
  }
  // Is a courier's compact card completely filled? Drives the ✓ on its tab.
  const compactComplete = (c) => !!c && compactRows.length > 0 && compactRows.every(({ bandIndex, kind }) => RFI_ZONES.every((z) => {
    const v = kind === 'first' ? c.first?.[z] : kind === 'firstEach' ? c.firstEach?.[z] : c.bands?.[bandIndex]?.[kind]?.[z]
    return v != null && String(v).trim() !== ''
  }))
  // What the Output preview expands: the ACTIVE courier's card in courier-wise mode
  // (cfg.rates only mirrors courier #1), the single blended card otherwise.
  const outRates = courierWise ? expandCompactRates(tiers, compact, RFI_ZONES) : cfg.rates
  const bandRange = (bandIndex) => {
    const from = tiers.slice(0, bandIndex).reduce((s, o) => s + (Number(o.size) || 0) * Math.max(0, Math.floor(Number(o.count) || 0)), 0)
    const size = Number(tiers[bandIndex]?.size) || 0
    const count = Math.max(0, Math.floor(Number(tiers[bandIndex]?.count) || 0))
    const fmt = (n) => String(Math.round(n * 100) / 100)
    return { from: fmt(from), to: fmt(from + size * count), size, count }
  }

  const isLinkedRvp = isReverse && cfg.rvpMode === 'linked'
  // Rate-card rows only feed the API in Target Rate Card mode (buildGeneratePayload
  // reads cfg.rates directly); Target Margin sends targetMarginPct only.
  const showStructure = !isLinkedRvp && cfg.mode === 'ratecard'
  const courierErr = showErr && !(cfg.primary || []).length
  // Card-wide hierarchy (approved "3 clear sections" mockup): exactly 3 bold
  // section headers — Pricing / Volume, scope & returns / Courier preference —
  // each with an accent bar and real top spacing. Everything else (cities, RTO
  // split, rate structure, COD commercials, excluded) is a quiet uppercase
  // sub-caption so it never competes with a section header.
  const majHeadSx = { fontSize: 15, fontWeight: 700, color: 'text.primary', display: 'flex', alignItems: 'center', mb: 2 }
  const barSx = { width: 4, height: 17, borderRadius: '2px', bgcolor: 'primary.main', display: 'inline-block', mr: 1.125, flex: '0 0 auto' }
  const majSectSx = { mt: 3.75, pt: 2.75, borderTop: '1px solid', borderColor: 'divider' }
  const minorSx = { fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mt: 2.5, mb: 1.125, display: 'flex', alignItems: 'center' }
  // Every text/select field on this card gets a firmer border than MUI's default
  // (a faint ~23%-opacity grey) — next to the bolder section headers and elevated
  // pricing cards, that default reads as "blended into the background."
  const fieldSx = {
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#C7D0E0' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
  }

  const modeLbl = isLinkedRvp ? 'Linked to FWD Surface' : (cfg.mode === 'ratecard' ? 'Client target' : (cfg.mode === 'standard' ? `Standard · ${cfg.prospectSize}` : 'Target margin'))

  // Identity (name, live status) is the banner in Step 3's parent, above this card —
  // this block is just the form, always mounted, always visible (one-way scroll).
  return (
    <Box sx={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderRadius: '10px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', ...compactPlaceholderSx }}>
      <Box sx={{ px: 2.25, pt: 2, pb: 2.25 }}>
      {!!(modeLbl || cfg.vol) && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>
          {modeLbl}{cfg.vol ? ` · ${cfg.vol}/mo` : ''}
        </Typography>
      )}

      {/* Card-specific fields — everything the SELECTED pricing card needs (reverse structure,
          method + its inputs, rate grid, COD/RTO, evidence) lives in this one panel, so choosing
          a different card visibly reframes this whole block. Volume & Courier below stay common. */}
      <Box sx={{ border: '1px solid', borderColor: '#DCE3EE', borderRadius: '12px', bgcolor: '#FBFCFE', p: { xs: 1.5, md: 2 }, mb: 2.75 }}>

      {/* Reverse: linked vs dedicated */}
      {isReverse && (
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={subHead}>Reverse structure <Typography component="span" sx={{ fontSize: 12, fontWeight: 400, color: 'text.secondary' }}>— always quoted with &amp; without QC; no air/express RVP</Typography></Typography>
          <ChoiceCards
            value={cfg.rvpMode}
            onChange={(v) => setCfg({ rvpMode: v })}
            minWidth={240}
            options={[
              { value: 'linked', title: 'Linked to FWD Surface (default — ~90% of deals)', desc: 'RVP = min RVP cost + forward leg, or % of forward — whichever is higher' },
              { value: 'dedicated', title: 'Dedicated RVP rate card', desc: 'separate zone × slab card, with & without QC' },
            ]}
          />
        </Box>
      )}

      {/* Linked RVP → formula-type picker (mirrors the merchant Pricing Wizard's
          Commercials step) + only the field(s) that formula needs, + a live preview.
          UI only — the payload is unchanged: still just rvpMinCostInr/rvpPctOfForward/
          qcChargeInr, whichever is filled (apiMap.js buildGeneratePayload). */}
      {isLinkedRvp && (() => {
        const ft = cfg.rvp.formulaType || 'higher'
        const rvpBothEmpty = !String(cfg.rvp.minCost ?? '').trim() && !String(cfg.rvp.pctOfForward ?? '').trim()
        const rvpErr = showErr && rvpBothEmpty
        const setFormulaType = (next) => {
          const patch = { formulaType: next }
          if (next === 'flat') patch.pctOfForward = ''
          if (next === 'pct') patch.minCost = ''
          setCfg({ rvp: { ...cfg.rvp, ...patch } })
        }
        const SAMPLE_FWD = 50
        const minCost = Number(cfg.rvp.minCost) || 0
        const pctOfForward = Number(cfg.rvp.pctOfForward) || 0
        const qc = Number(cfg.rvp.qc) || 0
        const flatVal = SAMPLE_FWD + minCost
        const pctVal = SAMPLE_FWD * (pctOfForward / 100)
        const nqc = ft === 'flat' ? flatVal : ft === 'pct' ? pctVal : Math.max(flatVal, pctVal)
        const qcVal = nqc + qc
        const nqcExpr = ft === 'flat' ? 'FWD + min cost' : ft === 'pct' ? 'FWD × %' : 'higher of (FWD + min cost) and (FWD × %)'
        return (
          <>
            <Grid container spacing={1.5} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth select size="small" sx={fieldSx} label="Formula type" value={ft} onChange={(e) => setFormulaType(e.target.value)}>
                  <MenuItem value="flat">Min cost only</MenuItem>
                  <MenuItem value="pct">% of forward only</MenuItem>
                  <MenuItem value="higher">Higher of both</MenuItem>
                </TextField>
              </Grid>
              {(ft === 'flat' || ft === 'higher') && (
                <Grid item xs={12} sm={4}><TextField fullWidth size="small" sx={fieldSx} label="Minimum RVP cost (₹)" value={cfg.rvp.minCost} onChange={(e) => setCfg({ rvp: { ...cfg.rvp, minCost: sanitizeNum(e.target.value) } })} error={rvpErr} InputProps={infoAdorn('Added over the forward leg cost.')} {...helperProps(rvpErr ? 'At least one of min cost / % of forward is required.' : undefined)} /></Grid>
              )}
              {(ft === 'pct' || ft === 'higher') && (
                <Grid item xs={12} sm={4}><TextField fullWidth size="small" sx={fieldSx} label="RVP cost as % of forward" value={cfg.rvp.pctOfForward} onChange={(e) => setCfg({ rvp: { ...cfg.rvp, pctOfForward: sanitizePct(e.target.value) } })} InputProps={infoAdorn('e.g. 130 ⇒ 130% of forward.', '%')} error={rvpErr} {...helperProps(rvpErr ? 'At least one of min cost / % of forward is required.' : undefined)} /></Grid>
              )}
              <Grid item xs={12} sm={4}><TextField fullWidth size="small" sx={fieldSx} label="QC charge (₹ per pickup)" value={cfg.rvp.qc} onChange={(e) => setCfg({ rvp: { ...cfg.rvp, qc: sanitizeNum(e.target.value) } })} InputProps={infoAdorn('Optional — blank uses the Functional-Head default QC charge.')} /></Grid>
            </Grid>
            <Typography sx={{ fontSize: 12, color: 'primary.dark', mb: 1.5 }}>
              Preview (indicative): at Forward = ₹{SAMPLE_FWD} → RVP (no QC) = ₹{Math.round(nqc)} · RVP (with QC) = ₹{Math.round(qcVal)} &nbsp;({nqcExpr}; with QC = above + QC charge).
              <br />QC pricing is pending — generated rates currently match the non-QC rate; the QC charge is billed separately.
            </Typography>
          </>
        )
      })()}

      {/* ② Input commercials — mode segmented control (forward + dedicated RVP) */}
      {!isLinkedRvp && (
        <>
          {/* Pricing — the card's first and most important section. The choice itself
              lives in its own bounded, tinted panel (approved mockup) so it reads as a
              distinct decision, separate from the field it produces and from the rest
              of the form below; a real gap (mb) after the panel does the rest — no
              connector, banner or "your input" label needed, since picking a card
              already visibly changes what renders next. */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={majHeadSx}>
              <Box component="span" sx={barSx} />Pricing<Box component="span" sx={{ color: '#dc2626', ml: 0.3 }}>*</Box>
              <NoteInfoIcon body="Pick how Prozo builds this service's rate card. Target Margin → you give a margin ask; Target Rate Card → the client's own rates per slab; Standard → a pre-built SME/Enterprise card, no pricing." />
            </Typography>
            <SelectCards
              value={cfg.mode}
              onChange={(v) => setCfg({ mode: v })}
              minWidth={240}
              options={[
                { value: 'margin', title: 'Target Margin', desc: 'you give the margin ask; we build the card to it', icon: <PercentIcon /> },
                { value: 'ratecard', title: 'Target Rate Card', desc: 'the client’s rates, checked lane by lane', icon: <GridOnIcon /> },
              ]}
            />
          </Box>

          {cfg.mode === 'margin' && (
            <Grid container spacing={1.5} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={5}><TextField fullWidth size="small" required sx={fieldSx} label="Target margin (%)" value={cfg.margin} onChange={(e) => setCfg({ margin: sanitizePct(e.target.value) })} InputProps={infoAdorn('The rate card is built to your margin ask per Prozo’s pricing playbook. Minimum 10%.', '%')}
                error={showErr && !(parseFloat(cfg.margin) >= 10)} {...helperProps(showErr && !(parseFloat(cfg.margin) >= 10) ? 'Required — minimum 10%.' : undefined)} /></Grid>
            </Grid>
          )}

          {cfg.mode === 'standard' && (
            <>
              <ChoiceCards
                label="Prospect size"
                value={cfg.prospectSize}
                onChange={(v) => setCfg({ prospectSize: v })}
                options={[
                  { value: 'SME', title: 'SME', desc: 'smaller prospect — standard SME rate card' },
                  { value: 'ENTERPRISE', title: 'Enterprise (ENT)', desc: 'large prospect — standard Enterprise rate card' },
                ]}
              />
              {/* Auto-matched GTM card: service × dealRateMode × tier → the pre-built card,
                  handed over as-is. No pricing runs for Standard. */}
              {(() => {
                const tier = cfg.prospectSize || 'SME'
                const typeLabel = dealRateMode === 'COURIER_WISE' ? 'Courier-wise' : 'Blended'
                const { card, exact } = matchStandardCard(rateCardLibrary, { serviceType: SERVICE_MAP[serviceCodeToKey(serviceType)] || serviceType, rateCardType: dealRateMode, prospectTier: tier })
                if (!card) {
                  return <Alert severity="warning" sx={{ fontSize: 12, mb: 1.5 }}>No standard card published for <b>{label} · {typeLabel} · {tier}</b>. Ask the Functional Head to add one under Rules &amp; Library, or switch to Target Margin / Target Rate Card.</Alert>
                }
                return (
                  <Alert severity={exact ? 'success' : 'info'} sx={{ fontSize: 12, mb: 1.5 }}>
                    Standard card applied: <b>{card.name}</b> <span style={{ color: '#64748b' }}>({typeLabel} · {tier}, from the GTM Toolkit)</span> — sent to the client as-is, no margin or rate entry.
                    {!exact && ' Closest match — no exact card for this exact combination; confirm it fits.'}
                    {card.fileUri && <> <Link component="button" underline="hover" sx={{ fontSize: 12 }} onClick={() => openS3Doc(card.fileUri, (m) => enqueueSnackbar(m, { variant: 'error' }))}>Open ↗</Link></>}
                  </Alert>
                )
              })()}
            </>
          )}

          {/* D2C wizard redesign (Aug 2026) — the structure is built in Step 2 and carried
              here "as is"; Step 3 fills the compact grid below against it. */}
      {showStructure && !rows.length && (
        <Typography sx={{ fontSize: 12, color: 'text.secondary', bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: '9px', px: 1.5, py: 1, mb: 1.5 }}>No slabs — build the structure in Step 2.</Typography>
      )}

      {/* Courier-wise: one tab per preferred courier — same structure, each courier's
          own ₹ card. ✓ appears when that courier's grid is completely filled. */}
      {cfg.mode === 'ratecard' && courierWise && (
        courierParents.length ? (
          <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', bgcolor: '#F1F4F9', border: '1px solid', borderColor: 'divider', borderRadius: '10px', p: 0.4, gap: 0.4, mb: 1.25 }}>
            {courierParents.map((p) => {
              const on = p === activeCourier
              const done = compactComplete(cfg.courierCompactRates?.[p])
              return (
                <Button key={p} size="small" disableRipple onClick={() => setActiveCourier(p)} sx={{
                  textTransform: 'none', fontSize: 12.5, fontWeight: 700, borderRadius: '8px', px: 1.5, py: 0.5, minWidth: 0,
                  bgcolor: on ? 'primary.main' : 'transparent', color: on ? '#fff' : 'text.secondary',
                  '&:hover': { bgcolor: on ? 'primary.main' : 'rgba(32,101,209,0.08)' },
                }}>
                  {done && <CheckIcon sx={{ fontSize: 14, mr: 0.4 }} />}
                  {p}
                </Button>
              )
            })}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 12, color: '#B4650A', bgcolor: '#FBF1E2', border: '1px solid #F0D6A8', borderRadius: '9px', px: 1.5, py: 1, mb: 1.25 }}>
            Courier-wise deal — add preferred couriers below first; each gets its own rate card on this shared structure.
          </Typography>
        )
      )}

      {/* Compact rate grid — one row per FIELD (first slab, each-additional per band,
          base-rate only for fresh bands) instead of one row per individual slab. Rows
          tinted blue are "fresh base" bands — a brand-new quoted number, not built up
          from the row above. Expands into the same full slab × zone table Step 4 and
          the generate payload already read (cfg.rates), via expandCompactRates. */}
          {cfg.mode === 'ratecard' && (!courierWise || !!activeCourier) && (
            <Box sx={{ mb: 1.5, border: '1px solid #E7EAF0', borderRadius: '10px', overflowX: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              {!!rows.length && (
                <Typography sx={{ fontSize: 12, color: '#1E4C8A', bgcolor: '#EAF1FC', borderBottom: '1px solid #CFE0F7', px: 1.5, py: 0.9 }}>
                  <Box component="span" sx={{ fontWeight: 800 }}>{compactRows.length} row{compactRows.length === 1 ? '' : 's'}</Box> × {RFI_ZONES.length} zones = <Box component="span" sx={{ fontWeight: 800 }}>{compactRows.length * RFI_ZONES.length} rates</Box> to fill — the full card has {rows.length} slab{rows.length === 1 ? '' : 's'} × {RFI_ZONES.length} zones = {rows.length * RFI_ZONES.length}.
                </Typography>
              )}
              <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr><th style={thS4}>What you&apos;re quoting</th>{RFI_ZONES.map((z) => <th key={z} style={{ ...thS4, textAlign: 'right' }}>{z} (₹)</th>)}</tr></thead>
                <tbody>
                  {compactRows.map((r, ri) => {
                    const { bandIndex, kind } = r
                    const tag = BAND_TAG_COLORS[bandIndex % BAND_TAG_COLORS.length]
                    const { from, to, size, count } = bandRange(bandIndex)
                    const fresh = tiers[bandIndex]?.fresh !== false
                    let tagText = ''; let title = ''; let sub = ''
                    if (kind === 'first') { tagText = 'FIRST'; title = `First slab (${from}–${to} kg)`; sub = 'flat rate' }
                    else if (kind === 'firstEach') { tagText = `EACH · ${from}–${to} kg`; title = `Each additional ${size} kg`; sub = `${count - 1} more slab${count - 1 === 1 ? '' : 's'}, added on top of the first slab above` }
                    else if (kind === 'base') { tagText = `BASE · ${from}–${to} kg`; title = `Base rate at ${from} kg`; sub = 'quoted directly, not built up from the band before it' }
                    else { tagText = `EACH · ${from}–${to} kg`; title = `Each additional ${size} kg`; sub = `${count} slab${count === 1 ? '' : 's'}, added on top of the ${fresh ? 'base above' : 'running total'}` }
                    const getVal = (z) => (kind === 'first' ? compact.first?.[z] : kind === 'firstEach' ? compact.firstEach?.[z] : compact.bands?.[bandIndex]?.[kind]?.[z])
                    const setVal = (z, v) => { if (kind === 'first') setCompactFirst(z, v); else if (kind === 'firstEach') setCompactFirstEach(z, v); else setCompactBand(bandIndex, kind, z, v) }
                    return (
                      <tr key={`${bandIndex}-${kind}`} style={{ background: kind === 'base' ? '#EAF1FC' : (ri % 2 ? '#FAFBFC' : '#fff') }}>
                        <td style={{ ...tdS4, ...(kind === 'base' ? { boxShadow: 'inset 3px 0 0 #2065D1' } : {}) }}>
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <Box component="span" sx={{ flexShrink: 0, mt: 0.2, fontFamily: 'monospace', fontSize: 10.5, fontWeight: 700, borderRadius: '6px', px: 0.9, py: 0.35, whiteSpace: 'nowrap', color: tag.fg, bgcolor: tag.bg }}>{tagText}</Box>
                            <Box>
                              <Typography sx={{ fontSize: 12.5, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                {title}{kind === 'base' && <Pill label="FRESH" styleKey="pendingL1" />}
                              </Typography>
                              <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{sub}</Typography>
                            </Box>
                          </Stack>
                        </td>
                        {RFI_ZONES.map((z) => {
                          const v = getVal(z)
                          const cellEmpty = v == null || String(v).trim() === ''
                          return (
                            <td key={z} style={tdS4}><TextField size="small" placeholder="₹" value={v || ''} onChange={(e) => setVal(z, sanitizeNum(e.target.value))} sx={{ width: 88, ...fieldSx }} inputProps={{ style: { textAlign: 'right' } }} error={showErr && cellEmpty} {...helperProps(showErr && cellEmpty ? 'Required' : undefined)} /></td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </Box>

              {/* Output — same 6 (or so) compact rows above, shown two ways: Short is the
                  compact client sentence per zone; Long is the exact full slab table you'd
                  see today (auto-computed from cfg.rates, not typed by hand). */}
              {!!rows.length && (
                <Box sx={{ borderTop: '1px solid #E7EAF0' }}>
                  <Box onClick={() => setOutputOpen((v) => !v)} sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 1.1, cursor: 'pointer', userSelect: 'none',
                    bgcolor: '#EAF1FC', borderBottom: '1px solid', borderColor: outputOpen ? '#CFE0F7' : 'transparent',
                    '&:hover': { bgcolor: '#DFEBFC' },
                  }}>
                    <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: 'primary.dark', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Output</Typography>
                    <Typography sx={{ fontSize: 12, color: '#1E4C8A', fontWeight: 500 }}>— client sentence &amp; full table{courierWise && activeCourier ? ` · ${activeCourier}` : ''}</Typography>
                    <Box sx={{ flex: 1 }} />
                    <ExpandMoreIcon sx={{ fontSize: 20, color: 'primary.main', transition: 'transform 0.15s', transform: outputOpen ? 'rotate(180deg)' : 'none' }} />
                  </Box>
                  <Collapse in={outputOpen}>
                  <Box sx={{ px: 1.5, pb: 1.5 }}>
                  <Box sx={{ display: 'inline-flex', bgcolor: '#F1F4F9', border: '1px solid', borderColor: 'divider', borderRadius: '10px', p: '3px', mb: 1.25 }}>
                    {[['short', 'Short form'], ['long', 'Long form']].map(([key, lbl]) => (
                      <Button key={key} size="small" onClick={() => setOutputMode(key)} sx={{
                        textTransform: 'none', fontSize: 12.5, fontWeight: 700, borderRadius: '8px', px: 1.8, py: 0.5, minWidth: 0,
                        bgcolor: outputMode === key ? 'primary.main' : 'transparent', color: outputMode === key ? '#fff' : 'text.secondary',
                      }}>{lbl}</Button>
                    ))}
                  </Box>
                  {outputMode === 'short' ? (
                    <Stack spacing={1}>
                      {RFI_ZONES.map((z) => {
                        const line = compactRows.map((r) => {
                          const { bandIndex, kind } = r
                          const v = kind === 'first' ? compact.first?.[z] : kind === 'firstEach' ? compact.firstEach?.[z] : compact.bands?.[bandIndex]?.[kind]?.[z]
                          const amt = v == null || String(v).trim() === '' ? '—' : `₹${v}`
                          const { from, to, size } = bandRange(bandIndex)
                          if (kind === 'first') return `${amt} First slab (${from}–${to} kg)`
                          if (kind === 'firstEach') return `then ${amt} each additional ${size} kg`
                          if (kind === 'base') return `${amt} (Base rate at ${from} kg)`
                          return `then ${amt} each additional ${size} kg`
                        }).join('  ·  ')
                        return (
                          <Box key={z} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', px: 1.5, py: 1, bgcolor: '#FAFBFD' }}>
                            <Typography sx={{ fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'primary.main' }}>{z}</Typography>
                            <Typography sx={{ fontSize: 12.5, lineHeight: 1.7, mt: 0.25 }}>{line}</Typography>
                          </Box>
                        )
                      })}
                    </Stack>
                  ) : (
                    <Box sx={{ border: '1px solid #E7EAF0', borderRadius: '10px', overflowX: 'auto' }}>
                      <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%' }}>
                        <thead><tr><th style={thS4}>Weight slab</th>{RFI_ZONES.map((z) => <th key={z} style={{ ...thS4, textAlign: 'right' }}>{z} (₹)</th>)}</tr></thead>
                        <tbody>
                          {rows.map((r, ri) => (
                            <tr key={r} style={{ background: ri % 2 ? '#FAFBFC' : '#fff' }}>
                              <td style={{ ...tdS4, fontWeight: 700 }}>{r}</td>
                              {RFI_ZONES.map((z) => <td key={z} style={{ ...tdS4, textAlign: 'right' }}>{outRates[r]?.[z] ?? '—'}</td>)}
                            </tr>
                          ))}
                        </tbody>
                      </Box>
                    </Box>
                  )}
                  </Box>
                  </Collapse>
                </Box>
              )}

              {!isReverse && (
                <>
                  <Typography sx={minorSx}>COD &amp; RTO — client target commercials <Typography component="span" sx={{ fontSize: 11, fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'text.secondary' }}>(auto-included on every forward card)</Typography></Typography>
                  <Grid container spacing={1.5} sx={{ maxWidth: 760 }}>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" required sx={fieldSx} label="COD fee — flat (₹ / order)" value={cfg.cod.flat} onChange={(e) => setCfg({ cod: { ...cfg.cod, flat: sanitizeNum(e.target.value) } })} InputProps={infoAdorn('Required — enter 0 if no COD charge.')}
                      error={showErr && !String(cfg.cod.flat ?? '').trim()} {...helperProps(showErr && !String(cfg.cod.flat ?? '').trim() ? 'Required — enter 0 if not charged.' : undefined)} /></Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" sx={fieldSx} label="COD fee — % of order value" value={cfg.cod.pct} onChange={(e) => setCfg({ cod: { ...cfg.cod, pct: sanitizePct(e.target.value) } })} InputProps={infoAdorn('Blank = auto-included from the library card', '%')} /></Grid>
                    <Grid item xs={12} sm={4}><TextField fullWidth size="small" required sx={fieldSx} label="RTO charge (% of forward)" value={cfg.cod.rtoPct} onChange={(e) => setCfg({ cod: { ...cfg.cod, rtoPct: sanitizePct(e.target.value) } })} InputProps={infoAdorn('Required — enter 0 if no RTO charge.', '%')}
                      error={showErr && !String(cfg.cod.rtoPct ?? '').trim()} {...helperProps(showErr && !String(cfg.cod.rtoPct ?? '').trim() ? 'Required — enter 0 if not charged.' : undefined)} /></Grid>
                  </Grid>
                </>
              )}
              <Typography sx={minorSx}>Evidence shared?</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                <TextField select size="small" value={cfg.evidence} onChange={(e) => setCfg({ evidence: e.target.value })} sx={{ width: 200, ...fieldSx }}>
                  <MenuItem value="No">No</MenuItem>
                  <MenuItem value="Yes">Yes — invoices / rates</MenuItem>
                </TextField>
                {cfg.evidence === 'Yes' && (
                  <Button size="small" variant="outlined" component="label" sx={{ ...btn, ...(showErr && !cfg.evidenceCount ? { borderColor: 'error.main', color: 'error.main' } : {}) }}>
                    Upload evidence<input type="file" hidden multiple onChange={(e) => { const n = e.target.files?.length || 0; if (n) { setCfg({ evidenceCount: n }); enqueueSnackbar(`${n} evidence file(s) attached`, { variant: 'success' }) } }} />
                  </Button>
                )}
                {cfg.evidence === 'Yes' && !!cfg.evidenceCount && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{cfg.evidenceCount} file(s) attached</Typography>}
                {cfg.evidence === 'Yes' && !cfg.evidenceCount && showErr && <Typography sx={{ fontSize: 11, color: 'error.main' }}>At least one evidence file is required.</Typography>}
              </Stack>
            </Box>
          )}
        </>
      )}
      </Box>

      {/* Courier preference — search-select adds straight to the priority list (pick
          → added, no separate confirm step); "Excluded" only appears once something
          is blacklisted, so the common case (nothing excluded) stays uncluttered. */}
      <Box sx={majSectSx}>
        <Typography sx={majHeadSx}><Box component="span" sx={barSx} />Courier preference <Box component="span" sx={{ color: '#dc2626', ml: 0.3 }}>*</Box><NoteInfoIcon body="Search to add a courier; use ↑/↓ to set priority, or blacklist to exclude it." /></Typography>
        <Autocomplete
          key={(cfg.primary || []).length}
          size="small" options={cpParents}
          onChange={(_, val) => { if (val) setCfg({ primary: [...(cfg.primary || []), { parent: val }] }) }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Search and add a courier…" InputProps={{ ...params.InputProps, startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'text.secondary', ml: 0.25, mr: 0.5 }} /> }} sx={fieldSx} />
          )}
          sx={{ maxWidth: 340, mb: 1.5 }}
        />
        {!(cfg.primary || []).length && <Typography sx={{ fontSize: 12, color: courierErr ? 'error.main' : 'text.secondary', mb: 1 }}>{courierErr ? 'Required — search above to add at least one preferred courier.' : 'No preference set yet — search above to add one.'}</Typography>}
        <Stack spacing={0.5}>
          {(cfg.primary || []).map((c, i) => (
            <Box key={c.parent} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: '8px', pl: 1, pr: 0.5, py: 0.25, bgcolor: 'background.paper' }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, width: 16, textAlign: 'center', color: 'text.secondary' }}>{i + 1}</Typography>
              {cpLabel(c)}
              <IconButton size="small" aria-label="Move up" disabled={i === 0} onClick={() => setCfg({ primary: swap(cfg.primary, i, i - 1) })}>↑</IconButton>
              <IconButton size="small" aria-label="Move down" disabled={i === cfg.primary.length - 1} onClick={() => setCfg({ primary: swap(cfg.primary, i, i + 1) })}>↓</IconButton>
              <Button size="small" sx={{ ...btn, fontSize: 12, minWidth: 0, px: 0.75 }} onClick={() => setCfg({ primary: cfg.primary.filter((_, idx) => idx !== i), blacklist: [...(cfg.blacklist || []), c] })}>blacklist</Button>
              <IconButton size="small" aria-label="Remove" onClick={() => setCfg({ primary: cfg.primary.filter((_, idx) => idx !== i) })}><DeleteOutlineIcon fontSize="small" /></IconButton>
            </Box>
          ))}
        </Stack>

        {!!(cfg.blacklist || []).length && (
          <>
            <Typography sx={minorSx}>Excluded<NoteInfoIcon body="Fully excluded from allocation — restore to make it eligible again." /></Typography>
            <Stack spacing={0.5}>
              {(cfg.blacklist || []).map((c, i) => (
                <Box key={c.parent} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid', borderColor: 'divider', borderRadius: '8px', pl: 1, pr: 0.5, py: 0.25, bgcolor: 'background.paper' }}>
                  {cpLabel(c)}
                  <Button size="small" sx={{ ...btn, fontSize: 12 }} onClick={() => setCfg({ blacklist: cfg.blacklist.filter((_, idx) => idx !== i), primary: [...(cfg.primary || []), c] })}>restore</Button>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </Box>

      </Box>
    </Box>
  )
}

/* ───────────────────────── Step 4 ───────────────────────── */
function Step4({ cardType, services, result, warnings = [], busy = false, proposalId, versionId, brand, hubspot, category, maxWt, totVol, revPot, avgWt, registeredCompany, enterpriseSize, brandTotalVol, minWt, currentRvp, pickups = [], pinCache = {}, svcConfig = {}, codShare, rtoShare, avgInvoiceValue, rfiMode, zoneSplit = [], wtSplit = [], wtTiers = [], perSvcSplit = false, svcSplits = {}, rateCardLibrary = [], dealRateMode = 'BLENDED', onSystemMargin, onFsc, onSaveDraft, onEditInputs, exportHost }) {
  // Same resolution as Step 3: the deal-wide structure, or this service's own if
  // "Split differs by service type" was on when it was built.
  const tiersFor = (k) => (perSvcSplit ? svcSplits[k]?.tiers : null) || wtTiers
  const { enqueueSnackbar } = useWizardSnackbar()
  // Real result from POST /generate. SL sees the BUCKET + ROUTE only — never the
  // margin number (dealMarginPct is returned but deliberately not shown here).
  const route = result?.route ?? null
  const bucketKey = result?.bucket ? BUCKET_LABEL_TO_KEY[result.bucket] : null
  const bucket = bucketKey ? BUCKETS[bucketKey] : null
  const [smPct, setSmPct] = useState('20')
  const [fscPct, setFscPct] = useState('5')

  const svcLabel = useCallback((k) => SERVICES.find((s) => s.key === k)?.label || k, [])
  const modeLabel = (m) => ({ margin: 'Target Margin', ratecard: 'Target Rate Card', standard: 'Standard' }[m] || m)
  const pinLabel = (pin) => pinCache[pin]?.city ? `${pinCache[pin].city} (${pin})` : pin

  // ── Generated output files — built from REAL version data, never mock ──────────
  // computedLanes is the pricing engine's output (SL sees it with cost/margin masked);
  // the Client Proposal doc is the real S3 artifact from the backend.
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId })
  const { data: proposalDoc } = useGetProposalDocQuery(proposalId, { skip: !proposalId })
  const lanes = useMemo(() => version?.computedLanes || [], [version])
  const allocRows = useMemo(() => allocationBdRows(lanes), [lanes])
  const svcRows = useMemo(() => serviceabilityRows(lanes), [lanes])
  const [preview, setPreview] = useState(null) // { title, rows }

  const openClientProposal = () => {
    // The OFFICIAL doc only exists post-Share (post-approval) — but the on-demand Export
    // Preview (FE Integration Guide v3 §1) works at any status, so it's the right default
    // here at Step 4 (nothing has been shared yet). Fall back to the real S3 doc if this
    // version somehow already has one (e.g. resuming a previously-approved draft).
    if (proposalDoc?.uri) { openS3Doc(proposalDoc.uri, (m) => enqueueSnackbar(m, { variant: 'error' })); return }
    exportPreviewPdf(versionId, (m) => enqueueSnackbar(m, { variant: 'error' }))
  }
  const previewCols = (rows) => (rows[0] ? Object.keys(rows[0]).map((k) => ({ key: k, label: k })) : [])
  const safeBrand = (brand || 'proposal').replace(/[^\w-]+/g, '_')
  const sheetNameFor4 = (lbl) => String(lbl).replace(/[\\/?*[\]:]/g, '-').slice(0, 31)

  // Full deal snapshot — every field entered across all 4 steps (filled or blank), plus
  // the actual generated result, in one workbook. The explicit "compare filled vs
  // generated" audit export: everything that went IN, next to what came OUT.
  const exportFullSnapshot = () => {
    const sheets = []
    sheets.push({ name: 'Prospect Details', aoa: [
      ['Field', 'Value'],
      ['Commercial type', COMMERCIAL_TYPES.find((t) => t.key === cardType)?.title || cardType || ''],
      ['Services selected', services.length ? services.map(svcLabel).join(', ') : '(none)'],
      ['HubSpot Deal ID', hubspot || ''],
      ['Brand name', brand || ''],
      ['Registered company name', registeredCompany || ''],
      ['Product category', category || ''],
      ['Enterprise size', enterpriseSize || ''],
      ['Total monthly shipments of the brand', brandTotalVol || ''],
      ['Max shipment weight (kg)', maxWt || ''],
      ['Min shipment weight (kg)', minWt || ''],
      ['Total monthly volume', totVol || ''],
      ['Monthly revenue potential (₹ lacs)', revPot || ''],
      ['Avg shipment weight (kg)', avgWt || ''],
      ['Average invoice value (₹)', avgInvoiceValue || ''],
      ['COD share (% of shipments)', codShare || ''],
      ['Current RTO (% of shipments)', rtoShare || ''],
      ['Current RVP (% of shipments)', currentRvp || ''],
    ] })
    sheets.push({ name: 'Pickup Locations', aoa: [
      ['#', 'Pickup PIN', 'City', 'Monthly volume'],
      ...pickups.map((p, i) => [i + 1, p.pin || '', pinLabel(p.pin), p.vol || '']),
    ] })
    sheets.push({ name: 'RFI Details', aoa: [
      ['RFI input mode', { pct: 'Quick % split', matrix: 'Full % matrix', copy: 'Copy from a previous prospect', file: 'Upload file' }[rfiMode] || rfiMode || ''],
      ['Split differs by service type', perSvcSplit ? 'Yes' : 'No'],
      [],
      ...(perSvcSplit && services.length
        ? services.flatMap((k) => {
          const sp = svcSplits[k] || { zones: zoneSplit, wts: wtSplit }
          return [
            [`${svcLabel(k)} — Zone split`], ['Zone', '% share'], ...sp.zones.map((z) => [z.zone, z.pct || '']), [],
            [`${svcLabel(k)} — Weight split`], ['From (kg)', 'To (kg)', '% share'], ...sp.wts.map((w) => [w.from, w.to, w.pct || '']), [],
          ]
        })
        : [
          ['Zone split'], ['Zone', '% share'], ...zoneSplit.map((z) => [z.zone, z.pct || '']), [],
          ['Weight split'], ['From (kg)', 'To (kg)', '% share'], ...wtSplit.map((w) => [w.from, w.to, w.pct || '']),
        ]),
    ] })
    services.forEach((k) => {
      const cfg = svcConfig[k] || defaultSvcCfg()
      const isFwd = serviceMeta(k).isForward
      const cities = (cfg.cities || []).map((c) => c.value).filter(Boolean).join(', ')
      const primary = (cfg.primary || []).map((c, i) => `${i + 1}. ${c.parent}`).join('; ')
      const blacklist = (cfg.blacklist || []).map((c) => c.parent).join(', ')
      // Prepaid/COD RTO % — both get Step 1's Current RTO % directly, same as buildGeneratePayload.
      const rtoNum = Number(rtoShare) || 0
      const codRto = isFwd && rtoNum > 0 ? rtoNum : ''
      const prepaidRto = isFwd && rtoNum > 0 ? rtoNum : ''
      const svcTiers = (perSvcSplit ? svcSplits[k]?.tiers : null) || wtTiers
      const complete = !validateServiceCfg({ cfg, label: svcLabel(k), isReverse: serviceMeta(k).isReverse, tiers: svcTiers, rateCardLibrary, dealRateMode, serviceType: serviceMeta(k).apiValue })
      sheets.push({ name: sheetNameFor4(`${svcLabel(k)} (Step 3)`), aoa: [
        ['Field', 'Value'],
        ['Complete?', complete ? 'Yes' : 'No'],
        ['Monthly volume (Step 1)', cfg.vol || ''],
        ['Cities in scope', cities],
        ['Input mode', modeLabel(cfg.mode)],
        ['Target margin %', cfg.margin || ''],
        ['COD flat (Rs)', cfg.cod?.flat || ''],
        ['COD % of value', cfg.cod?.pct || ''],
        ['RTO % of forward', cfg.cod?.rtoPct || ''],
        ['Prepaid RTO % of shipments (calculated from Step 1)', prepaidRto],
        ['COD RTO % of shipments (calculated from Step 1)', codRto],
        ['Courier preference (priority order)', primary || '(none — Prozo defaults)'],
        ['Blacklisted couriers', blacklist || 'None'],
        ['Evidence shared?', cfg.evidenceShared ? 'Yes' : 'No'],
        [],
        ['Target Rate Card — populated only if Input mode = Target Rate Card (structure built in Step 2)'],
        ['Slab', ...RFI_ZONES],
        ...rateRowLabelsFromTiers(tiersFor(k)).map((r) => [r, ...RFI_ZONES.map((z) => cfg.rates?.[r]?.[z] || '')]),
      ] })
    })
    sheets.push({ name: 'Generated Result', aoa: [
      ['Field', 'Value'],
      ['Proposal ID', proposalId || '(not generated yet)'],
      ['Version ID', versionId || '(not generated yet)'],
      ['Route', route != null ? `Route ${route}` : '—'],
      ['Bucket', bucket?.label || result?.bucket || '—'],
      ['Priced lanes (this version)', String(lanes.length)],
      ['Known data gaps flagged to you', warnings.length ? warnings.join(', ') : 'None'],
    ] })
    downloadWorkbook(`${safeBrand}_Full_Deal_Snapshot.xlsx`, sheets)
  }

  // ── C3: per-service proposed rate card (slab × zone) from the real lanes ──────
  const RATE_ZONE_ORDER = ['Local', 'Within State', 'Metros', 'ROI', 'Special']
  // Slab×zone grid from a set of lanes — PREPAID rate wins the cell (FB-02: don't let a COD
  // lane show just because it sorted first). Shared by the blended grid and each courier grid.
  const gridFrom = (sl) => {
    const zones = [...new Set(sl.map((l) => l.zone).filter(Boolean))].sort((a, b) => RATE_ZONE_ORDER.indexOf(a) - RATE_ZONE_ORDER.indexOf(b))
    const slabs = [...new Set(sl.map((l) => l.weightSlab).filter(Boolean))].sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0))
    const cell = {}
    sl.forEach((l) => { cell[l.weightSlab] = cell[l.weightSlab] || {}; const cur = cell[l.weightSlab][l.zone]; if (l.revenueRate != null && (cur == null || l.paymentMode === 'PREPAID')) cell[l.weightSlab][l.zone] = l.revenueRate })
    return { zones, slabs, cell }
  }
  const svcGrid = (st) => gridFrom(lanes.filter((l) => l.serviceType === st))
  // FB-02 — Courier-Wise decks emit a rate block per courier; render one grid per courierParent
  // for the service (never a single blended table). One account per parent in this mode.
  const svcCourierGrids = (st) => {
    const byCourier = {}
    lanes.filter((l) => l.serviceType === st).forEach((l) => { const c = l.courierParent || l.courierId || 'Courier'; (byCourier[c] = byCourier[c] || []).push(l) })
    return Object.entries(byCourier).map(([courier, cl]) => ({ courier, ...gridFrom(cl) }))
  }
  // ── C4: the 5 margin buckets → approval routing (deal's bucket highlighted) ──
  const thin = bucketKey === 'BORDERLINE' || bucketKey === 'LOSS'

  // ── Key risk areas — derived from the REAL proposal inputs + generate result ──────
  const risks = useMemo(() => {
    const out = []
    if (bucketKey === 'BORDERLINE' || bucketKey === 'LOSS') out.push(['Thin margin', `Deal landed “${result?.bucket}” — expect ${route === 3 ? 'Functional-Head approval + order-level RFI' : 'mandatory L1 approval'}.`])
    services.forEach((k) => {
      const cfg = svcConfig[k] || {}
      if (!(cfg.primary || []).length) out.push(['No courier preference', `${svcLabel(k)} has no preferred courier — allocation will fall back to defaults.`])
      if ((cfg.blacklist || []).length) out.push(['Restricted couriers', `${svcLabel(k)} blacklists ${cfg.blacklist.length} courier(s) — flagged to L1 regardless of band.`])
    })
    const cod = Number(codShare) || 0
    if (cod >= 60) out.push(['High COD share', `${cod}% COD — collection-fee exposure and higher RTO risk.`])
    const rto = Number(rtoShare) || 0
    if (rto >= 15) out.push(['High RTO', `${rto}% current RTO — reverse cost can erode the forward margin.`])
    const specialPickups = pickups.filter((p) => p.pin && zoneForPin(p.pin) === 'Special')
    if (specialPickups.length) out.push(['Special-zone pickups', `${specialPickups.length} pickup(s) in special zones (J&K / NE / islands) — limited courier serviceability.`])
    if (services.some((s) => serviceMeta(s).isReverse)) out.push(['Reverse logistics', 'RVP in scope — QC and pickup success rates affect realised reverse margin.'])
    return out
  }, [bucketKey, result, route, services, svcConfig, codShare, rtoShare, pickups, svcLabel])

  useHeaderExport(exportHost, exportFullSnapshot, 'Export full deal snapshot (.xlsx)')

  return (
    <>
      {/* ─── HERO — what the deal is + how it graded. The one thing to read on this page. ─── */}
      <SectionCard title={`${brand || 'This prospect'} — deal review`} subtitle="What you’re pricing, and how it graded.">
        {/* Result band — outcome of the whole flow, in its own status colour. */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, p: 2, mb: 2, borderRadius: '12px', bgcolor: bucket ? bucket.bg : 'action.hover', border: '1px solid', borderColor: bucket ? `${bucket.color}66` : 'divider' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 34, flexShrink: 0, color: bucket ? bucket.color : 'text.secondary' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={lblTiny}>Margin band</Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1, color: bucket ? bucket.color : 'text.primary' }}>
              {bucket ? bucket.label : (result?.bucket || 'Awaiting pricing')}
            </Typography>
            {bucket && <Typography sx={{ fontSize: 13, fontWeight: 700, color: bucket.color }}>{bucket.range}</Typography>}
          </Box>
          <Box sx={{ ml: { sm: 'auto' }, textAlign: { xs: 'left', sm: 'right' }, minWidth: 0 }}>
            <Typography sx={lblTiny}>Approval route</Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 800, lineHeight: 1.1, color: 'text.primary' }}>{route != null ? `Route ${route}` : '—'}</Typography>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', maxWidth: 300, lineHeight: 1.4 }}>
              {route === 3 ? 'Below 10% — Functional-Head sign-off; order-level data collected in parallel.'
                : (route === 1 || route === 2) ? 'Mandatory L1 Pricing Reviewer sign-off before sharing.'
                  : 'No route assigned yet — generate first.'}
            </Typography>
          </Box>
        </Box>

        {/* Deal at a glance — size + risk profile, in the app’s stat tiles. */}
        <KpiRow items={[
          { label: 'Monthly volume', value: totVol ? Number(totVol).toLocaleString('en-IN') : '—', delta: 'shipments / mo' },
          { label: 'Revenue potential', value: revPot ? `₹${revPot}L` : '—', delta: 'per month' },
          { label: 'COD share', value: (codShare != null && codShare !== '') ? `${codShare}%` : '—', delta: 'of shipments' },
          { label: 'Current RTO', value: (rtoShare != null && rtoShare !== '') ? `${rtoShare}%` : '—', delta: 'of shipments' },
        ]} />

        {/* Identity chips */}
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          {(COMMERCIAL_TYPES.find((t) => t.key === cardType)?.title || cardType) && <Pill label={COMMERCIAL_TYPES.find((t) => t.key === cardType)?.title || cardType} styleKey="neutral" />}
          {category && <Pill label={category} styleKey="neutral" />}
          {enterpriseSize && <Pill label={enterpriseSize} styleKey="neutral" />}
          {avgWt && <Pill label={`Avg ${avgWt} kg`} styleKey="neutral" />}
        </Stack>

        {/* Per-service breakdown — a clean divided list, not a wall of chips. */}
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', overflow: 'hidden' }}>
          {services.map((k, i) => {
            const cfg = svcConfig[k] || {}
            const cities = (cfg.cities || []).map((c) => c.value).filter(Boolean).join(', ')
            return (
              <Box key={k} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, px: 1.5, py: 1.1, borderTop: i ? '1px solid' : 'none', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', minWidth: 150 }}>{svcLabel(k)}</Typography>
                <Pill label={modeLabel(cfg.mode || 'margin')} styleKey="neutral" />
                {!!cfg.vol && (
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'primary.dark' }}>
                    {Number(cfg.vol).toLocaleString('en-IN')}<Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: 'text.secondary' }}> shpts/mo to Prozo</Box>
                  </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{cities}</Typography>
              </Box>
            )
          })}
        </Box>

        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.25 }}>
          <b>Pickups:</b> {pickups.filter((p) => p.pin).map((p) => `${pinLabel(p.pin)} · ${p.vol || 0}`).join('  ·  ') || '—'}
        </Typography>
      </SectionCard>

      {/* ─── Rate card preview — the client-facing numbers ─── */}
      <SectionCard title="Rate card preview" subtitle="Your proposed client-facing rates (₹ / shipment), exactly as they go into the proposal.">
        {services.map((k) => {
          const st = serviceMeta(k).apiValue
          const courierWise = version?.rateCardMode === 'COURIER_WISE'
          const grids = courierWise ? svcCourierGrids(st) : [{ courier: null, ...svcGrid(st) }]
          const hasData = grids.some((g) => g.slabs.length)
          return (
            <Box key={k} sx={{ mb: 2, '&:last-of-type': { mb: 0 } }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.primary', mb: 1 }}>{svcLabel(k)}</Typography>
              {hasData ? grids.filter((g) => g.slabs.length).map((g) => (
                <Box key={g.courier || 'blended'} sx={{ mb: 1.25 }}>
                  {g.courier && <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'primary.dark', mb: 0.5 }}>{g.courier}</Typography>}
                  <Box sx={tblWrap}>
                    <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%' }}>
                      <thead><tr><th style={thS4}>Weight slab</th>{g.zones.map((z) => <th key={z} style={{ ...thS4, textAlign: 'right' }}>{z} (₹)</th>)}</tr></thead>
                      <tbody>
                        {g.slabs.map((slab, ri) => (
                          <tr key={slab} style={{ background: ri % 2 ? '#FAFBFC' : '#fff' }}><td style={{ ...tdS4, fontWeight: 700 }}>{slab}</td>{g.zones.map((z) => <td key={z} style={{ ...tdS4, textAlign: 'right' }}>{g.cell[slab]?.[z] ?? '—'}</td>)}</tr>
                        ))}
                      </tbody>
                    </Box>
                  </Box>
                </Box>
              )) : <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No computed lanes for this service yet.</Typography>}
            </Box>
          )
        })}
      </SectionCard>

      {/* ─── Output files ─── */}
      <SectionCard title="Output files" subtitle="Ready to download. You get the client-facing pack; the margin working and internal allocation travel with the deal to Pricing & the Functional Head — masked from you.">
        <Grid container spacing={1.5}>
          {[
            { ttl: 'Client Proposal', sub: 'Outward rate cards + T&Cs — D2C format (.docx)', kind: 'doc' },
            { ttl: 'Approximate allocation', sub: 'Courier parent × service type × zone × allocation (.xlsx)', kind: 'xlsx', rows: allocRows, file: `${safeBrand}_Approximate_Allocation.xlsx`, sheet: 'Approximate Allocation' },
            { ttl: 'Serviceability & TAT', sub: 'Zone × weight-slab serviceability + transit days per service type (.xlsx)', kind: 'xlsx', rows: svcRows, file: `${safeBrand}_Serviceability_TAT.xlsx`, sheet: 'Serviceability' },
          ].map((f) => {
            // Doc (Client Proposal) now routes through Export Preview at this stage, which
            // has the same "no priced lanes" guard as every other lanes-derived file here.
            const empty = f.kind === 'xlsx' ? !f.rows.length : !lanes.length
            return (
              <Grid item xs={12} md={4} key={f.ttl}>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', p: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: '#EAF1FC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{f.ttl}</Typography>
                  </Stack>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5, flex: 1 }}>{f.sub}</Typography>
                  <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, flexWrap: 'wrap' }}>
                    <Button size="small" variant="outlined" sx={fileBtn} disabled={empty}
                      onClick={() => (f.kind === 'doc' ? openClientProposal() : setPreview({ title: f.ttl, rows: f.rows }))}>Preview</Button>
                    <Button size="small" variant="contained" sx={fileBtn} disabled={empty}
                      onClick={() => (f.kind === 'doc' ? openClientProposal() : downloadXlsx(f.file, f.rows, f.sheet))}>Download</Button>
                  </Stack>
                  {empty && <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.5 }}>No computed lanes on this version yet.</Typography>}
                </Box>
              </Grid>
            )
          })}
        </Grid>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.25 }}>
          <b>Also generated for Pricing / Functional Head</b> (not shown to you): margin working, internal allocation, exception &amp; risk report.
        </Typography>
      </SectionCard>

      {/* ─── Before you submit — flags, adjustments, export (merges risks + thin-deal options + tools) ─── */}
      <SectionCard title="Before you submit" subtitle={`Review the flags, adjust if needed, then use the ${route === 3 ? '“Submit for approval”' : '“Submit to L1 Pricing Reviewer”'} button at the bottom of the page.`}>
        {!!risks.length && (
          <Stack spacing={0.75} sx={{ mb: thin ? 2 : 2 }}>
            {risks.map(([t, d]) => (
              <Box key={t + d} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', border: '1px solid #FCD34D', bgcolor: '#FFFBEB', borderRadius: '8px', px: 1.25, py: 0.85 }}>
                <WarningAmberIcon sx={{ fontSize: 16, color: '#B45309', mt: '1px', flexShrink: 0 }} />
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#92400E' }}>{t}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: '#92400E', opacity: 0.85, lineHeight: 1.5 }}>{d}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        )}

        {thin && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={lblTiny}>This deal landed thin — your options</Typography>
            <Stack spacing={0.75} sx={{ mt: 0.75 }}>
              <Box sx={optRow}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Edit RFI &amp; inputs</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Correct the % splits, distribution or targets — the proposal recomputes.</Typography>
                </Box>
                <Button size="small" variant="outlined" sx={btn} disabled={!onEditInputs} onClick={onEditInputs}>Edit inputs</Button>
              </Box>
              <Box sx={optRow}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Regenerate at a system margin</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Lift the weakest lines toward the next band — use the system-margin control below. The result is a new, re-banded version.</Typography>
                </Box>
              </Box>
              <Box sx={optRow}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Request order-level data</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>For “match my current rates” pushback — push to Pricing tagged “awaiting order-level data”, then re-run as a billing simulation when it arrives.</Typography>
                </Box>
              </Box>
            </Stack>
          </Box>
        )}

        <Typography sx={lblTiny}>Adjust</Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 0.75 }}>
          <TextField size="small" label="System margin %" value={smPct} onChange={(e) => setSmPct(e.target.value)} sx={{ width: 150 }} />
          <Button variant="outlined" sx={btn} disabled={busy} onClick={() => onSystemMargin(smPct)}>Generate at system margin</Button>
          <TextField size="small" label="FSC %" value={fscPct} onChange={(e) => setFscPct(e.target.value)} sx={{ width: 110 }} />
          {/* Must match doFsc's own guard (route !== 2 && route !== 3) exactly — it
              previously only checked route===1, so a null/undefined route (engine
              didn't return one — usually missing courier commercials) left the button
              looking clickable while the handler silently toasted and did nothing.
              Feedback: "user is not able to add fuel surcharge." */}
          <Button variant="outlined" sx={btn} disabled={busy || (route !== 2 && route !== 3)} onClick={() => onFsc(fscPct)}
            title={route === 1 ? 'Fuel surcharge applies to Route 2/3 deals only — this deal is already Route 1 (healthy).'
              : (route !== 2 && route !== 3) ? 'No valid route yet — Generate first (or re-generate if courier commercials aren’t loaded).' : undefined}>Add fuel surcharge</Button>
          <Box sx={{ flex: 1 }} />
          {onSaveDraft && <Button variant="text" sx={btn} disabled={busy} onClick={onSaveDraft}>Save draft</Button>}
        </Stack>
      </SectionCard>

      {/* Preview dialog — shows the real rows that will be exported to the .xlsx. */}
      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>{preview?.title} — preview</DialogTitle>
        <DialogContent dividers>
          {preview && (preview.rows.length
            ? <DataTable columns={previewCols(preview.rows)} rows={preview.rows.map((r, i) => ({ ...r, _k: i }))} getRowKey={(r) => r._k} />
            : <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No rows to preview.</Typography>)}
        </DialogContent>
        <DialogActions>
          {preview && !!preview.rows.length && (
            <Button variant="outlined" sx={btn} onClick={() => downloadXlsx(`${safeBrand}_${preview.title.replace(/[^\w-]+/g, '_')}.xlsx`, preview.rows, preview.title.slice(0, 28))}>⬇ Download .xlsx</Button>
          )}
          <Button variant="text" sx={btn} onClick={() => setPreview(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

const btn = { textTransform: 'none', borderRadius: '6px', fontSize: 14 }
const fileBtn = { textTransform: 'none', borderRadius: '6px', fontSize: 12.5, py: 0.35, px: 1.25, minWidth: 0 }
const subHead = { fontSize: 12, fontWeight: 700, mb: 1 }
// Small uppercase section-label, matching the app's KpiRow/stat-tile label style.
const lblTiny = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary' }
// Bordered option row used in the "Before you submit" card.
const optRow = { border: '1px solid', borderColor: 'divider', borderRadius: '8px', p: 1.25, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }
// Rounded, shadowed table wrapper — matches the shared DataTable container.
const tblWrap = { overflowX: 'auto', border: '1px solid #E7EAF0', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }
// Shared cell styles used by the Step 2 matrix + Step 3 rate grid (leave as-is).
const thS = { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e3e8f0', fontSize: 12, fontFamily: 'Poppins, sans-serif', color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }
const tdS = { padding: '4px 8px', borderBottom: '1px solid #f1f5f9', fontFamily: 'Poppins, sans-serif', fontSize: 12, color: '#334155' }
// Step-4 rate-card preview cells — the shared DataTable look (read-only, so more padded/airy).
const thS4 = { textAlign: 'left', padding: '9px 12px', background: '#F9FAFB', borderBottom: '1px solid #E2E8F0', fontSize: 11, fontFamily: 'Poppins, sans-serif', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.02em', fontWeight: 700, whiteSpace: 'nowrap' }
const tdS4 = { padding: '9px 12px', borderBottom: '1px solid #E8ECF1', fontFamily: 'Poppins, sans-serif', fontSize: 13, color: '#334155' }
